// src/ScreenTimeAnalytics.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { Link } from 'react-router-dom';
import NavBar from './NavBar';
import { FaClock } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ScreenTimeAnalyticsPage.css';
import './AnalyticsPage.css';
import './AgentAssist.css';
import { getAllSBUs, getAllTracks, getGroupMapping, GROUP_MAPPING, loadGroupMappingFromCSV } from './groupMapping';
import { getUserMapping, getAllUserSBUs, getAllUserTracks, USERMAIL_MAPPING, loadUserMappingFromCSV } from './userMapping';

const FilterTypeButtons = ({ filterType, onFilterTypeChange }) => (
  <div className="filter-type-group">
    <button
      className={filterType === 'month' ? 'filter-type-btn active' : 'filter-type-btn'}
      onClick={() => onFilterTypeChange('month')}
    >
      Month
    </button>
    <button
      className={filterType === 'date' ? 'filter-type-btn active' : 'filter-type-btn'}
      onClick={() => onFilterTypeChange('date')}
    >
      Date
    </button>
  </div>
);

const MonthSelector = ({ year, month, onYearChange, onMonthChange, label = 'Select Month:' }) => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthIndex = typeof month === 'number' ? month - 1 : monthNames.indexOf(month);
  const selectedDate = new Date(parseInt(year), monthIndex);

  return (
    <div className="month-selector">
      <label className="filter-label">{label}</label>
      <DatePicker
        selected={selectedDate}
        onChange={(date) => {
          if (date) {
            onYearChange(date.getFullYear());
            const newMonth = monthNames[date.getMonth()];
            onMonthChange(newMonth);
          }
        }}
        dateFormat="MM/yyyy"
        showMonthYearPicker
        className="date-input"
        placeholderText="Select Month"
        popperPlacement="bottom-start"
        style={{ minWidth: '200px' }}
      />
    </div>
  );
};

const DateRangeSelector = ({ fromDate, toDate, onFromDateChange, onToDateChange }) => (
  <div className="date-selector">
    <div className="date-range-container">
      <div className="date-group">
        <label className="filter-label-nowrap">From:</label>
        <DatePicker
          selected={fromDate ? new Date(fromDate) : null}
          onChange={(date) => {
            if (date) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              onFromDateChange(`${year}-${month}-${day}`);
            }
          }}
          dateFormat="yyyy-MM-dd"
          className="date-input"
          placeholderText="From Date"
          popperPlacement="bottom-start"
        />
      </div>
      <div className="date-group">
        <label className="filter-label-nowrap">To:</label>
        <DatePicker
          selected={toDate ? new Date(toDate) : null}
          onChange={(date) => {
            if (date) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              onToDateChange(`${year}-${month}-${day}`);
            }
          }}
          dateFormat="yyyy-MM-dd"
          className="date-input"
          placeholderText="To Date"
          popperPlacement="bottom-start"
          minDate={fromDate ? new Date(fromDate) : null}
        />
      </div>
    </div>
  </div>
);

const UnifiedDashboardHeader = ({
  icon,
  title,
  subtitle,
  totalValue,
  totalLabel = 'Total',
  showTotalBadge = true
}) => {
  const displayTotal = Number.isFinite(Number(totalValue)) ? Number(totalValue).toLocaleString() : totalValue;

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
      <div>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{icon}</span>
          <span>{title}</span>
        </h3>
        {subtitle && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>{subtitle}</p>}
      </div>
      {showTotalBadge && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span style={{ fontSize: '20px', fontWeight: 800, color: '#3730a3', lineHeight: 1, letterSpacing: '-0.5px' }}>{displayTotal}</span>
          <span style={{ fontSize: '9px', fontWeight: 600, color: '#4f46e5', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{totalLabel}</span>
        </div>
      )}
    </div>
  );
};

// Emails to exclude from all analytics data
const EXCLUDED_EMAILS = new Set([
  'admin@example.com',
  'user@example.com', 
]);

const isExcludedEmail = (email) => {
  if (!email) return false;
  return EXCLUDED_EMAILS.has(email.toLowerCase().trim());
};

const UserAnalyticsTest = ({ userName, userEmail, onLogout, canViewUserAnalytics = false }) => {
  // Load saved filters from localStorage
  const loadSavedFilters = () => {
    try {
      const saved = localStorage.getItem('screenTimeFilters');
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.error('Error loading saved filters:', err);
      return {};
    }
  };

  const savedFilters = loadSavedFilters();

  // Assignment Group filters
  const [selectedGroupMonth, setSelectedGroupMonth] = useState(savedFilters.selectedGroupMonth || 'January');
  const [selectedGroupWeek, setSelectedGroupWeek] = useState(savedFilters.selectedGroupWeek || '1');
  const [selectedGroupFromDate, setSelectedGroupFromDate] = useState(savedFilters.selectedGroupFromDate || '2026-01-01');
  const [selectedGroupToDate, setSelectedGroupToDate] = useState(savedFilters.selectedGroupToDate || '2026-01-09');
  const [selectedGroupYear, setSelectedGroupYear] = useState(savedFilters.selectedGroupYear || '2026');
  const [selectedGroup, setSelectedGroup] = useState(savedFilters.selectedGroup || null);
  const [groupFilterType, setGroupFilterType] = useState(savedFilters.groupFilterType || 'month');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  
  // Team filters
  const [selectedTeamMonth, setSelectedTeamMonth] = useState(savedFilters.selectedTeamMonth || 'January');
  const [selectedTeamWeek, setSelectedTeamWeek] = useState(savedFilters.selectedTeamWeek || '1');
  const [selectedTeamFromDate, setSelectedTeamFromDate] = useState(savedFilters.selectedTeamFromDate || '2026-01-01');
  const [selectedTeamToDate, setSelectedTeamToDate] = useState(savedFilters.selectedTeamToDate || '2026-01-09');
  const [selectedTeamYear, setSelectedTeamYear] = useState(savedFilters.selectedTeamYear || '2026');
  const [teamFilterType, setTeamFilterType] = useState(savedFilters.teamFilterType || 'month');

  // All Teams Combined filters
  const [selectedAllTeamsMonth, setSelectedAllTeamsMonth] = useState(savedFilters.selectedAllTeamsMonth || 'January');
  const [selectedAllTeamsWeek, setSelectedAllTeamsWeek] = useState(savedFilters.selectedAllTeamsWeek || '1');
  const [selectedAllTeamsFromDate, setSelectedAllTeamsFromDate] = useState(savedFilters.selectedAllTeamsFromDate || '2026-01-01');
  const [selectedAllTeamsToDate, setSelectedAllTeamsToDate] = useState(savedFilters.selectedAllTeamsToDate || '2026-01-09');
  const [selectedAllTeamsYear, setSelectedAllTeamsYear] = useState(savedFilters.selectedAllTeamsYear || '2026');
  const [allTeamsFilterType, setAllTeamsFilterType] = useState(savedFilters.allTeamsFilterType || 'month');

  // Positive Feedback filters
  const [selectedPositiveFeedbackMonth, setSelectedPositiveFeedbackMonth] = useState(savedFilters.selectedPositiveFeedbackMonth || 'January');
  const [selectedPositiveFeedbackWeek, setSelectedPositiveFeedbackWeek] = useState(savedFilters.selectedPositiveFeedbackWeek || '1');
  const [selectedPositiveFeedbackFromDate, setSelectedPositiveFeedbackFromDate] = useState(savedFilters.selectedPositiveFeedbackFromDate || '2026-01-01');
  const [selectedPositiveFeedbackToDate, setSelectedPositiveFeedbackToDate] = useState(savedFilters.selectedPositiveFeedbackToDate || '2026-01-09');
  const [selectedPositiveFeedbackYear, setSelectedPositiveFeedbackYear] = useState(savedFilters.selectedPositiveFeedbackYear || '2026');
  const [positiveFeedbackFilterType, setPositiveFeedbackFilterType] = useState(savedFilters.positiveFeedbackFilterType || 'month');
  const [positiveFeedbackSBU, setPositiveFeedbackSBU] = useState([]);
  const [showPositiveFeedbackSBUDropdown, setShowPositiveFeedbackSBUDropdown] = useState(false);
  const [positiveFeedbackTrack, setPositiveFeedbackTrack] = useState([]);
  const [showPositiveFeedbackTrackDropdown, setShowPositiveFeedbackTrackDropdown] = useState(false);
  const [positiveFeedbackAvailableTracks, setPositiveFeedbackAvailableTracks] = useState([]);

  // Negative Feedback filters
  const [selectedNegativeFeedbackMonth, setSelectedNegativeFeedbackMonth] = useState(savedFilters.selectedNegativeFeedbackMonth || 'January');
  const [selectedNegativeFeedbackWeek, setSelectedNegativeFeedbackWeek] = useState(savedFilters.selectedNegativeFeedbackWeek || '1');
  const [selectedNegativeFeedbackFromDate, setSelectedNegativeFeedbackFromDate] = useState(savedFilters.selectedNegativeFeedbackFromDate || '2026-01-01');
  const [selectedNegativeFeedbackToDate, setSelectedNegativeFeedbackToDate] = useState(savedFilters.selectedNegativeFeedbackToDate || '2026-01-09');
  const [selectedNegativeFeedbackYear, setSelectedNegativeFeedbackYear] = useState(savedFilters.selectedNegativeFeedbackYear || '2026');
  const [negativeFeedbackFilterType, setNegativeFeedbackFilterType] = useState(savedFilters.negativeFeedbackFilterType || 'month');
  const [negativeFeedbackSBU, setNegativeFeedbackSBU] = useState([]);
  const [showNegativeFeedbackSBUDropdown, setShowNegativeFeedbackSBUDropdown] = useState(false);
  const [negativeFeedbackTrack, setNegativeFeedbackTrack] = useState([]);
  const [showNegativeFeedbackTrackDropdown, setShowNegativeFeedbackTrackDropdown] = useState(false);
  const [negativeFeedbackAvailableTracks, setNegativeFeedbackAvailableTracks] = useState([]);

  // Login Count filters
  const [loginCountFilterType, setLoginCountFilterType] = useState(savedFilters.loginCountFilterType || 'month');
  const [loginCountMonth, setLoginCountMonth] = useState(savedFilters.loginCountMonth || 'January');
  const [loginCountYear, setLoginCountYear] = useState(savedFilters.loginCountYear || '2026');
  const [loginCountFromDate, setLoginCountFromDate] = useState(savedFilters.loginCountFromDate || '2026-01-01');
  const [loginCountToDate, setLoginCountToDate] = useState(savedFilters.loginCountToDate || '2026-01-09');
  const [loginCountSBU, setLoginCountSBU] = useState([]);
  const [showLoginCountSBUDropdown, setShowLoginCountSBUDropdown] = useState(false);
  const [loginCountTrack, setLoginCountTrack] = useState([]);
  const [showLoginCountTrackDropdown, setShowLoginCountTrackDropdown] = useState(false);
  const [loginCountAvailableTracks, setLoginCountAvailableTracks] = useState([]);
  const [loginCountData, setLoginCountData] = useState([]);

  // Ticket Process filters
  const [ticketProcessFilterType, setTicketProcessFilterType] = useState(savedFilters.ticketProcessFilterType || 'month');
  const [ticketProcessMonth, setTicketProcessMonth] = useState(savedFilters.ticketProcessMonth || 'January');
  const [ticketProcessYear, setTicketProcessYear] = useState(savedFilters.ticketProcessYear || '2026');
  const [ticketProcessFromDate, setTicketProcessFromDate] = useState(savedFilters.ticketProcessFromDate || '2026-01-01');
  const [ticketProcessToDate, setTicketProcessToDate] = useState(savedFilters.ticketProcessToDate || '2026-01-09');
  const [ticketProcessSBU, setTicketProcessSBU] = useState([]);
  const [showTicketProcessSBUDropdown, setShowTicketProcessSBUDropdown] = useState(false);
  const [ticketProcessTrack, setTicketProcessTrack] = useState([]);
  const [showTicketProcessTrackDropdown, setShowTicketProcessTrackDropdown] = useState(false);
  const [ticketProcessAvailableTracks, setTicketProcessAvailableTracks] = useState([]);
  const [ticketProcessData, setTicketProcessData] = useState([]);

  // Data states
  const [assignmentGroupData, setAssignmentGroupData] = useState([]);
  const [teamScreenTimeData, setTeamScreenTimeData] = useState([]);
  const [allTeamsData, setAllTeamsData] = useState([]);
  const [positiveFeedbackData, setPositiveFeedbackData] = useState([]);
  const [negativeFeedbackData, setNegativeFeedbackData] = useState([]);

  // Login Trends filters
  const [loginTrendsFilterType, setLoginTrendsFilterType] = useState('month');
  const [loginTrendsMonth, setLoginTrendsMonth] = useState(savedFilters.loginTrendsMonth || 'February');
  const [loginTrendsYear, setLoginTrendsYear] = useState(savedFilters.loginTrendsYear || '2026');
  const [loginTrendsWeek, setLoginTrendsWeek] = useState('4');
  const [loginTrendsSBU, setLoginTrendsSBU] = useState([]);
  const [showLoginTrendsSBUDropdown, setShowLoginTrendsSBUDropdown] = useState(false);
  const [loginTrendsTrack, setLoginTrendsTrack] = useState([]);
  const [showLoginTrendsTrackDropdown, setShowLoginTrendsTrackDropdown] = useState(false);
  const [loginTrendsAvailableTracks, setLoginTrendsAvailableTracks] = useState([]);
  const [loginTrendsData, setLoginTrendsData] = useState([]);
  // Login Trends: store period keys for dynamic bar rendering
  const [loginTrendsPeriods, setLoginTrendsPeriods] = useState([]);

  // Ticket Process Trends filters
  const [ticketTrendsFilterType, setTicketTrendsFilterType] = useState('month');
  const [ticketTrendsMonth, setTicketTrendsMonth] = useState(savedFilters.ticketTrendsMonth || 'February');
  const [ticketTrendsYear, setTicketTrendsYear] = useState(savedFilters.ticketTrendsYear || '2026');
  const [ticketTrendsWeek, setTicketTrendsWeek] = useState('4');
  const [ticketTrendsSBU, setTicketTrendsSBU] = useState([]);
  const [showTicketTrendsSBUDropdown, setShowTicketTrendsSBUDropdown] = useState(false);
  const [ticketTrendsTrack, setTicketTrendsTrack] = useState([]);
  const [showTicketTrendsTrackDropdown, setShowTicketTrendsTrackDropdown] = useState(false);
  const [ticketTrendsAvailableTracks, setTicketTrendsAvailableTracks] = useState([]);
  const [ticketTrendsData, setTicketTrendsData] = useState([]);
  const [ticketTrendsPeriods, setTicketTrendsPeriods] = useState([]);

  // Feedback Trends filters
  const [feedbackTrendsFilterType, setFeedbackTrendsFilterType] = useState('month');
  const [feedbackTrendsMonth, setFeedbackTrendsMonth] = useState(savedFilters.feedbackTrendsMonth || 'February');
  const [feedbackTrendsYear, setFeedbackTrendsYear] = useState(savedFilters.feedbackTrendsYear || '2026');
  const [feedbackTrendsWeek, setFeedbackTrendsWeek] = useState('4');
  const [feedbackTrendsSBU, setFeedbackTrendsSBU] = useState([]);
  const [showFeedbackTrendsSBUDropdown, setShowFeedbackTrendsSBUDropdown] = useState(false);
  const [feedbackTrendsTrack, setFeedbackTrendsTrack] = useState([]);
  const [showFeedbackTrendsTrackDropdown, setShowFeedbackTrendsTrackDropdown] = useState(false);
  const [feedbackTrendsAvailableTracks, setFeedbackTrendsAvailableTracks] = useState([]);
  const [feedbackTrendsData, setFeedbackTrendsData] = useState([]);
  const [feedbackTrendsPeriods, setFeedbackTrendsPeriods] = useState([]);
  const [hoveredFeedbackBar, setHoveredFeedbackBar] = useState(null);

  // Areas to Improved Trends filters
  const [areasTrendsFilterType, setAreasTrendsFilterType] = useState('month');
  const [areasTrendsMonth, setAreasTrendsMonth] = useState(savedFilters.areasTrendsMonth || 'February');
  const [areasTrendsYear, setAreasTrendsYear] = useState(savedFilters.areasTrendsYear || '2026');
  const [areasTrendsWeek, setAreasTrendsWeek] = useState('4');
  const [areasTrendsSBU, setAreasTrendsSBU] = useState([]);
  const [showAreasTrendsSBUDropdown, setShowAreasTrendsSBUDropdown] = useState(false);
  const [areasTrendsTrack, setAreasTrendsTrack] = useState([]);
  const [showAreasTrendsTrackDropdown, setShowAreasTrendsTrackDropdown] = useState(false);
  const [areasTrendsAvailableTracks, setAreasTrendsAvailableTracks] = useState([]);
  const [areasTrendsData, setAreasTrendsData] = useState([]);
  const [areasTrendsPeriods, setAreasTrendsPeriods] = useState([]);

  // Screen Time Trends filters
  const [screenTimeTrendsFilterType, setScreenTimeTrendsFilterType] = useState('month');
  const [screenTimeTrendsMonth, setScreenTimeTrendsMonth] = useState(savedFilters.screenTimeTrendsMonth || 'February');
  const [screenTimeTrendsYear, setScreenTimeTrendsYear] = useState(savedFilters.screenTimeTrendsYear || '2026');
  const [screenTimeTrendsWeek, setScreenTimeTrendsWeek] = useState('4');
  const [screenTimeTrendsSBU, setScreenTimeTrendsSBU] = useState([]);
  const [showScreenTimeTrendsSBUDropdown, setShowScreenTimeTrendsSBUDropdown] = useState(false);
  const [screenTimeTrendsTrack, setScreenTimeTrendsTrack] = useState([]);
  const [showScreenTimeTrendsTrackDropdown, setShowScreenTimeTrendsTrackDropdown] = useState(false);
  const [screenTimeTrendsAvailableTracks, setScreenTimeTrendsAvailableTracks] = useState([]);
  const [screenTimeTrendsData, setScreenTimeTrendsData] = useState([]);
  const [screenTimeTrendsPeriods, setScreenTimeTrendsPeriods] = useState([]);

  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableSBUs, setAvailableSBUs] = useState([]);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [availableUserSBUs, setAvailableUserSBUs] = useState([]);
  const [availableUserTracks, setAvailableUserTracks] = useState([]);
  const [selectedSBU, setSelectedSBU] = useState([]);
  const [showSBUDropdown, setShowSBUDropdown] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState([]);
  const [showTrackDropdown, setShowTrackDropdown] = useState(false);

  // Single global filter controls (shown once below header)
  const [globalFilterType, setGlobalFilterType] = useState(savedFilters.globalFilterType || 'month');
  const [globalMonth, setGlobalMonth] = useState(savedFilters.globalMonth || 'January');
  const [globalYear, setGlobalYear] = useState(savedFilters.globalYear || '2026');
  const [globalWeek, setGlobalWeek] = useState(savedFilters.globalWeek || '1');
  const [globalFromDate, setGlobalFromDate] = useState(savedFilters.globalFromDate || '2026-01-01');
  const [globalToDate, setGlobalToDate] = useState(savedFilters.globalToDate || '2026-01-09');
  const [globalSBUValue, setGlobalSBUValue] = useState(savedFilters.globalSBUValue || 'all');
  const [globalTrackValue, setGlobalTrackValue] = useState(savedFilters.globalTrackValue || 'all');
  
  // Backend data state
  const [ticketsData, setTicketsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiFeedbackData, setAiFeedbackData] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [processButtonData, setProcessButtonData] = useState([]);
  const [ticketUpdateTrackingData, setTicketUpdateTrackingData] = useState([]);
  const [ticketUpdateData, setTicketUpdateData] = useState([]);
  
  // SDM Analytics filters and data
  const [sdmAnalyticsData, setSdmAnalyticsData] = useState([]);
  const [sdmFromDate, setSdmFromDate] = useState('');
  const [sdmToDate, setSdmToDate] = useState('');
  const [sdmLoading, setSdmLoading] = useState(false);
  const [sdmDebug, setSdmDebug] = useState(null);
  const [sdmTrendsFilterType, setSdmTrendsFilterType] = useState('month');
  const [sdmTrendsMonth, setSdmTrendsMonth] = useState(savedFilters.sdmTrendsMonth || (savedFilters.globalMonth || 'January'));
  const [sdmTrendsYear, setSdmTrendsYear] = useState(savedFilters.sdmTrendsYear || '2026');
  const [sdmTrendsWeek, setSdmTrendsWeek] = useState(savedFilters.sdmTrendsWeek || '4');
  const [sdmTrendsSelectedMetric, setSdmTrendsSelectedMetric] = useState('login');
  const [sdmTrendsDisplayMetric, setSdmTrendsDisplayMetric] = useState('login');
  const [sdmTrendsChartOpacity, setSdmTrendsChartOpacity] = useState(1);
  const [sdmTrendsPeriods, setSdmTrendsPeriods] = useState([]);
  const [sdmTrendsSeriesData, setSdmTrendsSeriesData] = useState([]);
  const [sdmTrendsLoading, setSdmTrendsLoading] = useState(false);

  // Initialize SDM date range on mount based on global month
  useEffect(() => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const sdmMonthIdx = monthNames.indexOf(globalMonth);
    const sdmYearInt = parseInt(globalYear);
    
    const sdmFrom = new Date(sdmYearInt, sdmMonthIdx, 1);
    const sdmTo = new Date(sdmYearInt, sdmMonthIdx + 1, 0);
    
    const toISODateLocal = (dateObj) => {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, '0');
      const d = String(dateObj.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    setSdmFromDate(toISODateLocal(sdmFrom));
    setSdmToDate(toISODateLocal(sdmTo));
  }, [globalMonth, globalYear]);

  useEffect(() => {
    setSdmTrendsChartOpacity(0);
    const t = setTimeout(() => {
      setSdmTrendsDisplayMetric(sdmTrendsSelectedMetric);
      setSdmTrendsChartOpacity(1);
    }, 180);
    return () => clearTimeout(t);
  }, [sdmTrendsSelectedMetric]);

  const sdmNamesInOrder = useMemo(() => [
    'Baljinder Singh',
    'Harishchandra Karnatak/Rajesh',
    'Mukesh Panwar',
    'Sarath Kandlakunta',
    'Tanmay Bharat',
    'Velu Subramanian'
  ], []);

  const sdmNamesToShow = useMemo(() => new Set(sdmNamesInOrder), [sdmNamesInOrder]);

  const sdmSummaryData = useMemo(() => {
    const summaryMap = (sdmAnalyticsData || [])
      .filter(row => sdmNamesToShow.has(String(row?.sdm || '').trim()))
      .reduce((acc, row) => {
        const sdmName = String(row?.sdm || '').trim();
        if (!sdmName) return acc;

        if (!acc[sdmName]) {
          acc[sdmName] = {
            sdm: sdmName,
            totalMembers: 0,
            uniqueLoginCount: 0,
            uniqueRecommendationCount: 0,
            uniqueFeedbackCount: 0
          };
        }

        acc[sdmName].totalMembers += Number(row?.totalUsers || 0);
        acc[sdmName].uniqueLoginCount += Number(row?.loginUsers || 0);
        acc[sdmName].uniqueRecommendationCount += Number(row?.ticketUsers || 0);
        acc[sdmName].uniqueFeedbackCount += Number(row?.feedbackUsers || 0);
        return acc;
      }, {});

    return Object.values(summaryMap)
      .map(item => {
        const total = item.totalMembers;
        const toolLoginPercent = total > 0 ? ((item.uniqueLoginCount / total) * 100).toFixed(2) : '0.00';
        const recommendationGeneratedPercent = total > 0 ? ((item.uniqueRecommendationCount / total) * 100).toFixed(2) : '0.00';
        const feedbackPercent = total > 0 ? ((item.uniqueFeedbackCount / total) * 100).toFixed(2) : '0.00';

        return {
          ...item,
          totalMembers: Number(total.toFixed(2)),
          toolLoginPercent,
          recommendationGeneratedPercent,
          feedbackPercent
        };
      })
      .sort((a, b) => sdmNamesInOrder.indexOf(a.sdm) - sdmNamesInOrder.indexOf(b.sdm));
  }, [sdmAnalyticsData, sdmNamesInOrder, sdmNamesToShow]);

  const sdmTotalToolUsageRow = useMemo(() => {
    const totals = (sdmSummaryData || []).reduce((acc, row) => {
      acc.totalMembers += Number(row?.totalMembers || 0);
      acc.uniqueLoginCount += Number(row?.uniqueLoginCount || 0);
      acc.uniqueRecommendationCount += Number(row?.uniqueRecommendationCount || 0);
      acc.uniqueFeedbackCount += Number(row?.uniqueFeedbackCount || 0);
      return acc;
    }, {
      totalMembers: 0,
      uniqueLoginCount: 0,
      uniqueRecommendationCount: 0,
      uniqueFeedbackCount: 0
    });

    const total = totals.totalMembers;
    return {
      sdm: 'Total Tool Usage %',
      toolLoginPercent: total > 0 ? ((totals.uniqueLoginCount / total) * 100).toFixed(2) : '0.00',
      recommendationGeneratedPercent: total > 0 ? ((totals.uniqueRecommendationCount / total) * 100).toFixed(2) : '0.00',
      feedbackPercent: total > 0 ? ((totals.uniqueFeedbackCount / total) * 100).toFixed(2) : '0.00'
    };
  }, [sdmSummaryData]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const toISODate = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const buildSDMTrendPeriods = (filterType, month, year, week) => {
    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);

      for (let i = 3; i >= 0; i--) {
        let m = selectedMonthIdx - i;
        let y = selectedYear;
        if (m < 0) {
          m += 12;
          y -= 1;
        }
        const from = new Date(y, m, 1);
        const to = new Date(y, m + 1, 0);
        periods.push({
          key: `${3 - i}`,
          label: `${shortMonthNames[m]} ${y}`,
          fromDate: toISODate(from),
          toDate: toISODate(to)
        });
      }
      return periods;
    }

    const selectedMonthIdx = monthNames.indexOf(month);
    const selectedYear = parseInt(year);
    const selectedWeekNum = parseInt(week);
    const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
    const selectedWeekStart = new Date(firstOfMonth);
    selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);

    for (let i = 3; i >= 0; i--) {
      const wStart = new Date(selectedWeekStart);
      wStart.setDate(wStart.getDate() - (i * 7));
      const wEnd = new Date(wStart);
      wEnd.setDate(wEnd.getDate() + 6);
      periods.push({
        key: `${3 - i}`,
        label: `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}-${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`,
        fromDate: toISODate(wStart),
        toDate: toISODate(wEnd)
      });
    }

    return periods;
  };

  const aggregateSDMRows = (rows) => {
    const summaryMap = (rows || [])
      .filter(row => sdmNamesToShow.has(String(row?.sdm || '').trim()))
      .reduce((acc, row) => {
        const sdmName = String(row?.sdm || '').trim();
        if (!sdmName) return acc;

        if (!acc[sdmName]) {
          acc[sdmName] = {
            sdm: sdmName,
            totalMembers: 0,
            uniqueLoginCount: 0,
            uniqueRecommendationCount: 0,
            uniqueFeedbackCount: 0
          };
        }

        acc[sdmName].totalMembers += Number(row?.totalUsers || 0);
        acc[sdmName].uniqueLoginCount += Number(row?.loginUsers || 0);
        acc[sdmName].uniqueRecommendationCount += Number(row?.ticketUsers || 0);
        acc[sdmName].uniqueFeedbackCount += Number(row?.feedbackUsers || 0);
        return acc;
      }, {});

    return sdmNamesInOrder.map((sdmName) => {
      const item = summaryMap[sdmName] || {
        sdm: sdmName,
        totalMembers: 0,
        uniqueLoginCount: 0,
        uniqueRecommendationCount: 0,
        uniqueFeedbackCount: 0
      };
      const total = item.totalMembers;
      const loginPercent = total > 0 ? Number(((item.uniqueLoginCount / total) * 100).toFixed(2)) : 0;
      const ticketPercent = total > 0 ? Number(((item.uniqueRecommendationCount / total) * 100).toFixed(2)) : 0;
      const feedbackPercent = total > 0 ? Number(((item.uniqueFeedbackCount / total) * 100).toFixed(2)) : 0;
      return {
        name: sdmName,
        loginPercent,
        ticketPercent,
        feedbackPercent
      };
    });
  };

  const sdmTrendsChartData = useMemo(() => {
    const latestFirstPeriods = [...(sdmTrendsPeriods || [])].reverse();
    return sdmNamesInOrder.map((sdmName) => {
      const row = { name: sdmName };

      latestFirstPeriods.forEach((period) => {
        const periodData = sdmTrendsSeriesData.find(series => series.key === period.key);
        const sdmRow = (periodData?.data || []).find(item => item.name === sdmName) || {
          loginPercent: 0,
          ticketPercent: 0,
          feedbackPercent: 0
        };

        row[`${period.label} Login %`] = Number(sdmRow.loginPercent || 0);
        row[`${period.label} Ticket %`] = Number(sdmRow.ticketPercent || 0);
        row[`${period.label} Feedback %`] = Number(sdmRow.feedbackPercent || 0);
      });

      return row;
    });
  }, [sdmNamesInOrder, sdmTrendsPeriods, sdmTrendsSeriesData]);

  const getGrowthFillColor = (value) => {
    const v = Number(value || 0);
    if (v < 30) return '#f87171';
    if (v <= 60) return '#fbbf24';
    return '#34d399';
  };

  const getMetricBorderColor = (metric) => {
    if (metric === 'login') return '#1d4ed8';
    if (metric === 'ticket') return '#7e22ce';
    return '#166534';
  };

  const getGlobalTrackOptions = () => {
    if (globalSBUValue === 'all') {
      return [
        { value: 'all', label: 'All Tracks' },
        ...getAllUserTracks().map(track => ({ value: track, label: track }))
      ];
    }

    const tracksInSBU = new Set();
    Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
      if (mapping.sbu === globalSBUValue) {
        tracksInSBU.add(mapping.track);
      }
    });

    return [
      { value: 'all', label: 'All Tracks' },
      ...getAllUserTracks()
        .filter(track => tracksInSBU.has(track))
        .map(track => ({ value: track, label: track }))
    ];
  };

  const globalTrackOptions = getGlobalTrackOptions();

  // Ref for dropdown to handle click outside
  const groupDropdownRef = useRef(null);
  const positiveFeedbackSBUDropdownRef = useRef(null);
  const positiveFeedbackTrackDropdownRef = useRef(null);
  const negativeFeedbackSBUDropdownRef = useRef(null);
  const negativeFeedbackTrackDropdownRef = useRef(null);
  const trackDropdownRef = useRef(null);
  const sbuDropdownRef = useRef(null);
  const loginCountSBUDropdownRef = useRef(null);
  const loginCountTrackDropdownRef = useRef(null);
  const ticketProcessSBUDropdownRef = useRef(null);
  const ticketProcessTrackDropdownRef = useRef(null);
  const loginTrendsSBUDropdownRef = useRef(null);
  const loginTrendsTrackDropdownRef = useRef(null);
  const ticketTrendsSBUDropdownRef = useRef(null);
  const ticketTrendsTrackDropdownRef = useRef(null);
  const feedbackTrendsSBUDropdownRef = useRef(null);
  const feedbackTrendsTrackDropdownRef = useRef(null);
  const areasTrendsSBUDropdownRef = useRef(null);
  const areasTrendsTrackDropdownRef = useRef(null);
  const screenTimeTrendsSBUDropdownRef = useRef(null);
  const screenTimeTrendsTrackDropdownRef = useRef(null);

  // Group selection handlers
  const handleSelectAllGroups = () => {
    if (isAllGroupsSelected()) {
      setSelectedGroup([]);
    } else {
      setSelectedGroup(availableGroups.filter(g => g.value !== 'all'));
    }
  };

  const handleToggleGroup = (group) => {
    const currentGroups = Array.isArray(selectedGroup) ? selectedGroup : [];
    const isSelected = currentGroups.some(g => g.value === group.value);
    
    if (isSelected) {
      setSelectedGroup(currentGroups.filter(g => g.value !== group.value));
    } else {
      setSelectedGroup([...currentGroups, group]);
    }
  };

  const isAllGroupsSelected = () => {
    const actualGroups = availableGroups.filter(g => g.value !== 'all');
    const currentGroups = Array.isArray(selectedGroup) ? selectedGroup : [];
    return actualGroups.length > 0 && currentGroups.length === actualGroups.length;
  };

  const getSelectedGroupLabel = () => {
    if (!selectedGroup || selectedGroup.length === 0) return 'Select Groups...';
    // Check if "All Groups" option is selected
    if (selectedGroup.length === 1 && selectedGroup[0].value === 'all') return 'All Groups';
    if (isAllGroupsSelected()) return 'All Groups';
    return `${selectedGroup.length} Group${selectedGroup.length > 1 ? 's' : ''} Selected`;
  };

  // Positive Feedback SBU handlers
  const handleSelectAllPositiveFeedbackSBU = () => {
    if (isAllPositiveFeedbackSBUSelected()) { setPositiveFeedbackSBU([]); }
    else { setPositiveFeedbackSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleTogglePositiveFeedbackSBU = (sbu) => {
    const current = Array.isArray(positiveFeedbackSBU) ? positiveFeedbackSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    setPositiveFeedbackSBU(isSelected ? current.filter(s => s.value !== sbu.value) : [...current, sbu]);
  };
  const isAllPositiveFeedbackSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(positiveFeedbackSBU) ? positiveFeedbackSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedPositiveFeedbackSBULabel = () => {
    if (!positiveFeedbackSBU || positiveFeedbackSBU.length === 0) return 'Select SBU...';
    if (isAllPositiveFeedbackSBUSelected()) return 'All SBU';
    return `${positiveFeedbackSBU.length} SBU Selected`;
  };

  // Positive Feedback Track handlers
  const handleSelectAllPositiveFeedbackTrack = () => {
    setPositiveFeedbackTrack([]); setShowPositiveFeedbackTrackDropdown(false);
  };
  const handleTogglePositiveFeedbackTrack = (track) => {
    setPositiveFeedbackTrack([track]); setShowPositiveFeedbackTrackDropdown(false);
  };
  const getSelectedPositiveFeedbackTrackLabel = () => {
    if (!positiveFeedbackTrack || positiveFeedbackTrack.length === 0) return 'Select Track...';
    return positiveFeedbackTrack[0].label;
  };

  // Negative Feedback SBU handlers
  const handleSelectAllNegativeFeedbackSBU = () => {
    if (isAllNegativeFeedbackSBUSelected()) { setNegativeFeedbackSBU([]); }
    else { setNegativeFeedbackSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleToggleNegativeFeedbackSBU = (sbu) => {
    const current = Array.isArray(negativeFeedbackSBU) ? negativeFeedbackSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    setNegativeFeedbackSBU(isSelected ? current.filter(s => s.value !== sbu.value) : [...current, sbu]);
  };
  const isAllNegativeFeedbackSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(negativeFeedbackSBU) ? negativeFeedbackSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedNegativeFeedbackSBULabel = () => {
    if (!negativeFeedbackSBU || negativeFeedbackSBU.length === 0) return 'Select SBU...';
    if (isAllNegativeFeedbackSBUSelected()) return 'All SBU';
    return `${negativeFeedbackSBU.length} SBU Selected`;
  };

  // Negative Feedback Track handlers
  const handleSelectAllNegativeFeedbackTrack = () => {
    setNegativeFeedbackTrack([]); setShowNegativeFeedbackTrackDropdown(false);
  };
  const handleToggleNegativeFeedbackTrack = (track) => {
    setNegativeFeedbackTrack([track]); setShowNegativeFeedbackTrackDropdown(false);
  };
  const getSelectedNegativeFeedbackTrackLabel = () => {
    if (!negativeFeedbackTrack || negativeFeedbackTrack.length === 0) return 'Select Track...';
    return negativeFeedbackTrack[0].label;
  };

  // Track selection handlers
  const handleSelectAllTracks = () => {
    setSelectedTrack([]); setShowTrackDropdown(false);
  };

  const handleToggleTrack = (track) => {
    setSelectedTrack([track]); setShowTrackDropdown(false);
  };

  const getSelectedTrackLabel = () => {
    if (!selectedTrack || selectedTrack.length === 0) return 'Select Track...';
    return selectedTrack[0].label;
  };

  // SBU selection handlers
  const handleSelectAllSBU = () => {
    if (isAllSBUSelected()) {
      setSelectedSBU([]);
    } else {
      setSelectedSBU(availableSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleSBU = (sbu) => {
    const currentSBUs = Array.isArray(selectedSBU) ? selectedSBU : [];
    const isSelected = currentSBUs.some(s => s.value === sbu.value);
    
    if (isSelected) {
      setSelectedSBU(currentSBUs.filter(s => s.value !== sbu.value));
    } else {
      setSelectedSBU([...currentSBUs, sbu]);
    }
  };

  const isAllSBUSelected = () => {
    const actualSBUs = availableSBUs.filter(s => s.value !== 'all');
    const currentSBUs = Array.isArray(selectedSBU) ? selectedSBU : [];
    return actualSBUs.length > 0 && currentSBUs.length === actualSBUs.length;
  };

  const getSelectedSBULabel = () => {
    if (!selectedSBU || selectedSBU.length === 0) return 'Select SBU...';
    if (isAllSBUSelected()) return 'All SBU';
    return `${selectedSBU.length} SBU Selected`;
  };

  // Login Count SBU handlers
  const handleSelectAllLoginCountSBU = () => {
    if (isAllLoginCountSBUSelected()) {
      setLoginCountSBU([]);
    } else {
      setLoginCountSBU(availableUserSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleLoginCountSBU = (sbu) => {
    const current = Array.isArray(loginCountSBU) ? loginCountSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) {
      setLoginCountSBU(current.filter(s => s.value !== sbu.value));
    } else {
      setLoginCountSBU([...current, sbu]);
    }
  };

  const isAllLoginCountSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(loginCountSBU) ? loginCountSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };

  const getSelectedLoginCountSBULabel = () => {
    if (!loginCountSBU || loginCountSBU.length === 0) return 'Select SBU...';
    if (isAllLoginCountSBUSelected()) return 'All SBU';
    return `${loginCountSBU.length} SBU Selected`;
  };

  // Login Count Track handlers
  const handleSelectAllLoginCountTrack = () => {
    setLoginCountTrack([]); setShowLoginCountTrackDropdown(false);
  };

  const handleToggleLoginCountTrack = (track) => {
    setLoginCountTrack([track]); setShowLoginCountTrackDropdown(false);
  };

  const getSelectedLoginCountTrackLabel = () => {
    if (!loginCountTrack || loginCountTrack.length === 0) return 'Select Track...';
    return loginCountTrack[0].label;
  };

  // Login Trends SBU handlers
  const handleSelectAllLoginTrendsSBU = () => {
    if (isAllLoginTrendsSBUSelected()) {
      setLoginTrendsSBU([]);
    } else {
      setLoginTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleLoginTrendsSBU = (sbu) => {
    const current = Array.isArray(loginTrendsSBU) ? loginTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) {
      setLoginTrendsSBU(current.filter(s => s.value !== sbu.value));
    } else {
      setLoginTrendsSBU([...current, sbu]);
    }
  };

  const isAllLoginTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(loginTrendsSBU) ? loginTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };

  const getSelectedLoginTrendsSBULabel = () => {
    if (!loginTrendsSBU || loginTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllLoginTrendsSBUSelected()) return 'All SBU';
    return `${loginTrendsSBU.length} SBU Selected`;
  };

  // Login Trends Track handlers
  const handleSelectAllLoginTrendsTrack = () => {
    setLoginTrendsTrack([]); setShowLoginTrendsTrackDropdown(false);
  };

  const handleToggleLoginTrendsTrack = (track) => {
    setLoginTrendsTrack([track]); setShowLoginTrendsTrackDropdown(false);
  };

  const getSelectedLoginTrendsTrackLabel = () => {
    if (!loginTrendsTrack || loginTrendsTrack.length === 0) return 'Select Track...';
    return loginTrendsTrack[0].label;
  };

  // Ticket Trends SBU handlers
  const handleSelectAllTicketTrendsSBU = () => {
    if (isAllTicketTrendsSBUSelected()) {
      setTicketTrendsSBU([]);
    } else {
      setTicketTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleTicketTrendsSBU = (sbu) => {
    const current = Array.isArray(ticketTrendsSBU) ? ticketTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) {
      setTicketTrendsSBU(current.filter(s => s.value !== sbu.value));
    } else {
      setTicketTrendsSBU([...current, sbu]);
    }
  };

  const isAllTicketTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(ticketTrendsSBU) ? ticketTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };

  const getSelectedTicketTrendsSBULabel = () => {
    if (!ticketTrendsSBU || ticketTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllTicketTrendsSBUSelected()) return 'All SBU';
    return `${ticketTrendsSBU.length} SBU Selected`;
  };

  // Ticket Trends Track handlers
  const handleSelectAllTicketTrendsTrack = () => {
    setTicketTrendsTrack([]); setShowTicketTrendsTrackDropdown(false);
  };

  const handleToggleTicketTrendsTrack = (track) => {
    setTicketTrendsTrack([track]); setShowTicketTrendsTrackDropdown(false);
  };

  const getSelectedTicketTrendsTrackLabel = () => {
    if (!ticketTrendsTrack || ticketTrendsTrack.length === 0) return 'Select Track...';
    return ticketTrendsTrack[0].label;
  };

  // Feedback Trends SBU handlers
  const handleSelectAllFeedbackTrendsSBU = () => {
    if (isAllFeedbackTrendsSBUSelected()) { setFeedbackTrendsSBU([]); } else { setFeedbackTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleToggleFeedbackTrendsSBU = (sbu) => {
    const current = Array.isArray(feedbackTrendsSBU) ? feedbackTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) { setFeedbackTrendsSBU(current.filter(s => s.value !== sbu.value)); } else { setFeedbackTrendsSBU([...current, sbu]); }
  };
  const isAllFeedbackTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(feedbackTrendsSBU) ? feedbackTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedFeedbackTrendsSBULabel = () => {
    if (!feedbackTrendsSBU || feedbackTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllFeedbackTrendsSBUSelected()) return 'All SBU';
    return `${feedbackTrendsSBU.length} SBU Selected`;
  };
  // Feedback Trends Track handlers
  const handleSelectAllFeedbackTrendsTrack = () => {
    setFeedbackTrendsTrack([]); setShowFeedbackTrendsTrackDropdown(false);
  };
  const handleToggleFeedbackTrendsTrack = (track) => {
    setFeedbackTrendsTrack([track]); setShowFeedbackTrendsTrackDropdown(false);
  };
  const getSelectedFeedbackTrendsTrackLabel = () => {
    if (!feedbackTrendsTrack || feedbackTrendsTrack.length === 0) return 'Select Track...';
    return feedbackTrendsTrack[0].label;
  };

  // Areas Trends SBU handlers
  const handleSelectAllAreasTrendsSBU = () => {
    if (isAllAreasTrendsSBUSelected()) { setAreasTrendsSBU([]); } else { setAreasTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleToggleAreasTrendsSBU = (sbu) => {
    const current = Array.isArray(areasTrendsSBU) ? areasTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) { setAreasTrendsSBU(current.filter(s => s.value !== sbu.value)); } else { setAreasTrendsSBU([...current, sbu]); }
  };
  const isAllAreasTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(areasTrendsSBU) ? areasTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedAreasTrendsSBULabel = () => {
    if (!areasTrendsSBU || areasTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllAreasTrendsSBUSelected()) return 'All SBU';
    return `${areasTrendsSBU.length} SBU Selected`;
  };
  // Areas Trends Track handlers
  const handleSelectAllAreasTrendsTrack = () => {
    setAreasTrendsTrack([]); setShowAreasTrendsTrackDropdown(false);
  };
  const handleToggleAreasTrendsTrack = (track) => {
    setAreasTrendsTrack([track]); setShowAreasTrendsTrackDropdown(false);
  };
  const getSelectedAreasTrendsTrackLabel = () => {
    if (!areasTrendsTrack || areasTrendsTrack.length === 0) return 'Select Track...';
    return areasTrendsTrack[0].label;
  };

  // Screen Time Trends SBU handlers
  const handleSelectAllScreenTimeTrendsSBU = () => {
    if (isAllScreenTimeTrendsSBUSelected()) { setScreenTimeTrendsSBU([]); } else { setScreenTimeTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleToggleScreenTimeTrendsSBU = (sbu) => {
    const current = Array.isArray(screenTimeTrendsSBU) ? screenTimeTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) { setScreenTimeTrendsSBU(current.filter(s => s.value !== sbu.value)); } else { setScreenTimeTrendsSBU([...current, sbu]); }
  };
  const isAllScreenTimeTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(screenTimeTrendsSBU) ? screenTimeTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedScreenTimeTrendsSBULabel = () => {
    if (!screenTimeTrendsSBU || screenTimeTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllScreenTimeTrendsSBUSelected()) return 'All SBU';
    return `${screenTimeTrendsSBU.length} SBU Selected`;
  };
  // Screen Time Trends Track handlers
  const handleToggleScreenTimeTrendsTrack = (track) => {
    setScreenTimeTrendsTrack([track]); setShowScreenTimeTrendsTrackDropdown(false);
  };
  const getSelectedScreenTimeTrendsTrackLabel = () => {
    if (!screenTimeTrendsTrack || screenTimeTrendsTrack.length === 0) return 'Select Track...';
    return screenTimeTrendsTrack[0].label;
  };

  // Ticket Process SBU handlers
  const handleSelectAllTicketProcessSBU = () => {
    if (isAllTicketProcessSBUSelected()) {
      setTicketProcessSBU([]);
    } else {
      setTicketProcessSBU(availableUserSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleTicketProcessSBU = (sbu) => {
    const current = Array.isArray(ticketProcessSBU) ? ticketProcessSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) {
      setTicketProcessSBU(current.filter(s => s.value !== sbu.value));
    } else {
      setTicketProcessSBU([...current, sbu]);
    }
  };

  const isAllTicketProcessSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(ticketProcessSBU) ? ticketProcessSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };

  const getSelectedTicketProcessSBULabel = () => {
    if (!ticketProcessSBU || ticketProcessSBU.length === 0) return 'Select SBU...';
    if (isAllTicketProcessSBUSelected()) return 'All SBU';
    return `${ticketProcessSBU.length} SBU Selected`;
  };

  // Ticket Process Track handlers
  const handleSelectAllTicketProcessTrack = () => {
    setTicketProcessTrack([]); setShowTicketProcessTrackDropdown(false);
  };

  const handleToggleTicketProcessTrack = (track) => {
    setTicketProcessTrack([track]); setShowTicketProcessTrackDropdown(false);
  };

  const getSelectedTicketProcessTrackLabel = () => {
    if (!ticketProcessTrack || ticketProcessTrack.length === 0) return 'Select Track...';
    return ticketProcessTrack[0].label;
  };

  const sortTrendPeriods = (periods) => {
    return [...(periods || [])].sort((a, b) => {
      const aNum = parseInt(String(a).split('|')[0], 10);
      const bNum = parseInt(String(b).split('|')[0], 10);
      const aHasNum = !Number.isNaN(aNum);
      const bHasNum = !Number.isNaN(bNum);
      if (aHasNum && bHasNum) return aNum - bNum;
      if (aHasNum) return -1;
      if (bHasNum) return 1;
      return String(a).localeCompare(String(b));
    });
  };

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filtersToSave = {
      selectedGroupMonth,
      selectedGroupWeek,
      selectedGroupFromDate,
      selectedGroupToDate,
      selectedGroup,
      groupFilterType,
      selectedTeamMonth,
      selectedTeamWeek,
      selectedTeamFromDate,
      selectedTeamToDate,
      teamFilterType,
      selectedAllTeamsMonth,
      selectedAllTeamsWeek,
      selectedAllTeamsFromDate,
      selectedAllTeamsToDate,
      allTeamsFilterType,
      selectedPositiveFeedbackMonth,
      selectedPositiveFeedbackWeek,
      selectedPositiveFeedbackFromDate,
      selectedPositiveFeedbackToDate,
      positiveFeedbackSBU,
      positiveFeedbackTrack,
      positiveFeedbackFilterType,
      selectedNegativeFeedbackMonth,
      selectedNegativeFeedbackWeek,
      selectedNegativeFeedbackFromDate,
      selectedNegativeFeedbackToDate,
      negativeFeedbackSBU,
      negativeFeedbackTrack,
      negativeFeedbackFilterType,
      selectedSBU,
      loginCountFilterType,
      loginCountMonth,
      loginCountYear,
      loginCountFromDate,
      loginCountToDate,
      ticketProcessFilterType,
      ticketProcessMonth,
      ticketProcessYear,
      ticketProcessFromDate,
      ticketProcessToDate,
      globalFilterType,
      globalMonth,
      globalYear,
      globalWeek,
      globalFromDate,
      globalToDate,
      globalSBUValue,
      globalTrackValue,
      sdmFromDate,
      sdmToDate
    };
    
    try {
      localStorage.setItem('screenTimeFilters', JSON.stringify(filtersToSave));
    } catch (err) {
      console.error('Error saving filters:', err);
    }
  }, [
    selectedGroupMonth, selectedGroupWeek, selectedGroupFromDate, selectedGroupToDate, selectedGroup, groupFilterType,
    selectedTeamMonth, selectedTeamWeek, selectedTeamFromDate, selectedTeamToDate, teamFilterType,
    selectedAllTeamsMonth, selectedAllTeamsWeek, selectedAllTeamsFromDate, selectedAllTeamsToDate, allTeamsFilterType,
    selectedPositiveFeedbackMonth, selectedPositiveFeedbackWeek, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, positiveFeedbackSBU, positiveFeedbackTrack, positiveFeedbackFilterType,
    selectedNegativeFeedbackMonth, selectedNegativeFeedbackWeek, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, negativeFeedbackSBU, negativeFeedbackTrack, negativeFeedbackFilterType,
    selectedSBU,
    loginCountFilterType, loginCountMonth, loginCountYear, loginCountFromDate, loginCountToDate,
    ticketProcessFilterType, ticketProcessMonth, ticketProcessYear, ticketProcessFromDate, ticketProcessToDate,
    globalFilterType, globalMonth, globalYear, globalWeek, globalFromDate, globalToDate, globalSBUValue, globalTrackValue,
    sdmFromDate, sdmToDate
  ]);

  // Sync the single global filter to every dashboard filter state
  useEffect(() => {
    const selectedSBUOption = globalSBUValue === 'all'
      ? []
      : availableUserSBUs.filter(s => s.value === globalSBUValue);
    const selectedTrackOption = globalTrackValue === 'all'
      ? []
      : availableUserTracks.filter(t => t.value === globalTrackValue);

    setSelectedSBU(selectedSBUOption);
    setSelectedTrack(selectedTrackOption);

    setLoginCountSBU(selectedSBUOption);
    setTicketProcessSBU(selectedSBUOption);
    setPositiveFeedbackSBU(selectedSBUOption);
    setNegativeFeedbackSBU(selectedSBUOption);
    setLoginTrendsSBU(selectedSBUOption);
    setTicketTrendsSBU(selectedSBUOption);
    setFeedbackTrendsSBU(selectedSBUOption);
    setAreasTrendsSBU(selectedSBUOption);
    setScreenTimeTrendsSBU(selectedSBUOption);

    setLoginCountTrack(selectedTrackOption);
    setTicketProcessTrack(selectedTrackOption);
    setPositiveFeedbackTrack(selectedTrackOption);
    setNegativeFeedbackTrack(selectedTrackOption);
    setLoginTrendsTrack(selectedTrackOption);
    setTicketTrendsTrack(selectedTrackOption);
    setFeedbackTrendsTrack(selectedTrackOption);
    setAreasTrendsTrack(selectedTrackOption);
    setScreenTimeTrendsTrack(selectedTrackOption);

    const nonTrendType = globalFilterType === 'week' ? 'month' : globalFilterType;
    const trendsType = globalFilterType === 'date' ? 'month' : globalFilterType;

    setGroupFilterType(nonTrendType);
    setTeamFilterType(nonTrendType);
    setAllTeamsFilterType(nonTrendType);
    setPositiveFeedbackFilterType(nonTrendType);
    setNegativeFeedbackFilterType(nonTrendType);
    setLoginCountFilterType(nonTrendType);
    setTicketProcessFilterType(nonTrendType);

    setSelectedGroupMonth(globalMonth);
    setSelectedTeamMonth(globalMonth);
    setSelectedAllTeamsMonth(globalMonth);
    setSelectedPositiveFeedbackMonth(globalMonth);
    setSelectedNegativeFeedbackMonth(globalMonth);
    setLoginCountMonth(globalMonth);
    setTicketProcessMonth(globalMonth);

    setSelectedGroupYear(globalYear);
    setSelectedTeamYear(globalYear);
    setSelectedAllTeamsYear(globalYear);
    setSelectedPositiveFeedbackYear(globalYear);
    setSelectedNegativeFeedbackYear(globalYear);
    setLoginCountYear(globalYear);
    setTicketProcessYear(globalYear);

    setSelectedGroupWeek(globalWeek);
    setSelectedTeamWeek(globalWeek);
    setSelectedAllTeamsWeek(globalWeek);
    setSelectedPositiveFeedbackWeek(globalWeek);
    setSelectedNegativeFeedbackWeek(globalWeek);

    setSelectedGroupFromDate(globalFromDate);
    setSelectedGroupToDate(globalToDate);
    setSelectedTeamFromDate(globalFromDate);
    setSelectedTeamToDate(globalToDate);
    setSelectedAllTeamsFromDate(globalFromDate);
    setSelectedAllTeamsToDate(globalToDate);
    setSelectedPositiveFeedbackFromDate(globalFromDate);
    setSelectedPositiveFeedbackToDate(globalToDate);
    setSelectedNegativeFeedbackFromDate(globalFromDate);
    setSelectedNegativeFeedbackToDate(globalToDate);
    setLoginCountFromDate(globalFromDate);
    setLoginCountToDate(globalToDate);
    setTicketProcessFromDate(globalFromDate);
    setTicketProcessToDate(globalToDate);

    setLoginTrendsFilterType(trendsType);
    setTicketTrendsFilterType(trendsType);
    setFeedbackTrendsFilterType(trendsType);
    setAreasTrendsFilterType(trendsType);
    setScreenTimeTrendsFilterType(trendsType);

    setLoginTrendsMonth(globalMonth);
    setTicketTrendsMonth(globalMonth);
    setFeedbackTrendsMonth(globalMonth);
    setAreasTrendsMonth(globalMonth);
    setScreenTimeTrendsMonth(globalMonth);

    setLoginTrendsYear(globalYear);
    setTicketTrendsYear(globalYear);
    setFeedbackTrendsYear(globalYear);
    setAreasTrendsYear(globalYear);
    setScreenTimeTrendsYear(globalYear);

    setLoginTrendsWeek(globalWeek);
    setTicketTrendsWeek(globalWeek);
    setFeedbackTrendsWeek(globalWeek);
    setAreasTrendsWeek(globalWeek);
    setScreenTimeTrendsWeek(globalWeek);

    // Sync SDM Trends dashboard
    setSdmTrendsFilterType(trendsType);
    setSdmTrendsMonth(globalMonth);
    setSdmTrendsYear(globalYear);
    setSdmTrendsWeek(globalWeek);

    // Sync SDM Adoption Percentages date range from global filter
    const sdmMonthIdx = monthNames.indexOf(globalMonth);
    const sdmYearInt = parseInt(globalYear);
    let sdmFrom = '', sdmTo = '';
    if (globalFilterType === 'date') {
      sdmFrom = globalFromDate;
      sdmTo = globalToDate;
    } else if (globalFilterType === 'week') {
      const wStart = new Date(sdmYearInt, sdmMonthIdx, 1 + (parseInt(globalWeek) - 1) * 7);
      const wEnd = new Date(wStart);
      wEnd.setDate(wEnd.getDate() + 6);
      sdmFrom = toISODate(wStart);
      sdmTo = toISODate(wEnd);
    } else {
      sdmFrom = toISODate(new Date(sdmYearInt, sdmMonthIdx, 1));
      sdmTo = toISODate(new Date(sdmYearInt, sdmMonthIdx + 1, 0));
    }
    setSdmFromDate(sdmFrom);
    setSdmToDate(sdmTo);
  }, [globalFilterType, globalMonth, globalYear, globalWeek, globalFromDate, globalToDate, globalSBUValue, globalTrackValue, availableUserSBUs, availableUserTracks]);

  useEffect(() => {
    const validTrackValues = globalTrackOptions.map(option => option.value);
    if (!validTrackValues.includes(globalTrackValue)) {
      setGlobalTrackValue('all');
    }
  }, [globalSBUValue, globalTrackValue]);

  // Fetch AI feedback and session data on mount
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        // Fetch AI feedback data
        const feedbackResponse = await fetch('/api/feedback-all');
        if (feedbackResponse.ok) {
          const feedbackData = await feedbackResponse.json();
          if (feedbackData.success) {
            console.log('AI Feedback Data Loaded:', feedbackData.feedback.length, 'entries');
            const filteredFeedback = feedbackData.feedback.filter(f => !isExcludedEmail(f.userEmail));
            console.log('AI Feedback Data Loaded:', filteredFeedback.length, 'entries (excluded:', feedbackData.feedback.length - filteredFeedback.length, ')');
            console.log('Thumbs Up:', filteredFeedback.filter(f => f.feedbackType === 'thumbs_up').length);
            console.log('Thumbs Down:', filteredFeedback.filter(f => f.feedbackType === 'thumbs_down').length);
            setAiFeedbackData(filteredFeedback);
          }
        }
        
        // Fetch session data with timestamps for filtering
        const sessionResponse = await fetch('/api/sessions/all');
        if (sessionResponse.ok) {
          const sessionDataResult = await sessionResponse.json();
          if (sessionDataResult.success) {
            const filteredSessions = sessionDataResult.data.filter(s => !isExcludedEmail(s.userEmail));
            console.log('Session Data Loaded:', filteredSessions.length, 'sessions (excluded:', sessionDataResult.data.length - filteredSessions.length, ')');
            setSessionData(filteredSessions);
          }
        }

        // Fetch process button click data
        const processResponse = await fetch('/api/analytics/all-process-clicks');
        if (processResponse.ok) {
          const processResult = await processResponse.json();
          if (processResult.success) {
            const filteredClicks = processResult.clicks.filter(c => !isExcludedEmail(c.userEmail));
            console.log('Process Button Data Loaded:', filteredClicks.length, 'clicks (excluded:', processResult.clicks.length - filteredClicks.length, ')');
            setProcessButtonData(filteredClicks);
          }
        }

        // Fetch ticket update tracking data
        const ticketUpdateResponse = await fetch('/api/analytics/ticket-updates-all');
        if (ticketUpdateResponse.ok) {
          const ticketUpdateResult = await ticketUpdateResponse.json();
          if (ticketUpdateResult.success) {
            const rawUpdates = ticketUpdateResult.data || [];
            const filteredUpdates = rawUpdates.filter(u => !isExcludedEmail(u.userEmail));
            console.log('Ticket Update Data Loaded:', filteredUpdates.length, 'updates (excluded:', rawUpdates.length - filteredUpdates.length, ')');
            setTicketUpdateTrackingData(filteredUpdates);
          }
        }
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      }
    };
    
    fetchAnalyticsData();
  }, []);

  // Fetch SDM analytics data when dates change
  useEffect(() => {
    const fetchSDMAnalytics = async () => {
      try {
        setSdmLoading(true);
        const params = new URLSearchParams();
        if (sdmFromDate) {
          params.set('fromDate', sdmFromDate);
        }
        if (sdmToDate) {
          params.set('toDate', sdmToDate);
        }
        const query = params.toString();
        const response = await fetch(query ? `/api/analytics/sdm-track-percentages?${query}` : '/api/analytics/sdm-track-percentages');
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setSdmAnalyticsData(result.data || []);
            setSdmDebug(result.debug || null);
            console.log('SDM Analytics Data Loaded:', result.data?.length || 0, 'groups', result.debug || {});
          }
        }
      } catch (error) {
        console.error('Error fetching SDM analytics:', error);
      } finally {
        setSdmLoading(false);
      }
    };
    
    fetchSDMAnalytics();
  }, [sdmFromDate, sdmToDate]);

  // Fetch SDM trends data for 4 periods based on month/week selector
  useEffect(() => {
    const fetchSDMTrendsData = async () => {
      try {
        setSdmTrendsLoading(true);
        const periods = buildSDMTrendPeriods(sdmTrendsFilterType, sdmTrendsMonth, sdmTrendsYear, sdmTrendsWeek);
        setSdmTrendsPeriods(periods);

        const periodRows = await Promise.all(periods.map(async (period) => {
          const params = new URLSearchParams();
          params.set('fromDate', period.fromDate);
          params.set('toDate', period.toDate);
          const response = await fetch(`/api/analytics/sdm-track-percentages?${params.toString()}`);
          if (!response.ok) {
            return { key: period.key, label: period.label, data: [] };
          }
          const result = await response.json();
          return { key: period.key, label: period.label, data: aggregateSDMRows(result?.data || []) };
        }));

        setSdmTrendsSeriesData(periodRows);
      } catch (error) {
        console.error('Error fetching SDM trends analytics:', error);
        setSdmTrendsSeriesData([]);
      } finally {
        setSdmTrendsLoading(false);
      }
    };

    fetchSDMTrendsData();
  }, [sdmTrendsFilterType, sdmTrendsMonth, sdmTrendsYear, sdmTrendsWeek, sdmNamesInOrder.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
        setShowGroupDropdown(false);
      }
      if (positiveFeedbackSBUDropdownRef.current && !positiveFeedbackSBUDropdownRef.current.contains(event.target)) {
        setShowPositiveFeedbackSBUDropdown(false);
      }
      if (positiveFeedbackTrackDropdownRef.current && !positiveFeedbackTrackDropdownRef.current.contains(event.target)) {
        setShowPositiveFeedbackTrackDropdown(false);
      }
      if (negativeFeedbackSBUDropdownRef.current && !negativeFeedbackSBUDropdownRef.current.contains(event.target)) {
        setShowNegativeFeedbackSBUDropdown(false);
      }
      if (negativeFeedbackTrackDropdownRef.current && !negativeFeedbackTrackDropdownRef.current.contains(event.target)) {
        setShowNegativeFeedbackTrackDropdown(false);
      }
      if (trackDropdownRef.current && !trackDropdownRef.current.contains(event.target)) {
        setShowTrackDropdown(false);
      }
      if (sbuDropdownRef.current && !sbuDropdownRef.current.contains(event.target)) {
        setShowSBUDropdown(false);
      }
      if (loginCountSBUDropdownRef.current && !loginCountSBUDropdownRef.current.contains(event.target)) {
        setShowLoginCountSBUDropdown(false);
      }
      if (loginCountTrackDropdownRef.current && !loginCountTrackDropdownRef.current.contains(event.target)) {
        setShowLoginCountTrackDropdown(false);
      }
      if (ticketProcessSBUDropdownRef.current && !ticketProcessSBUDropdownRef.current.contains(event.target)) {
        setShowTicketProcessSBUDropdown(false);
      }
      if (ticketProcessTrackDropdownRef.current && !ticketProcessTrackDropdownRef.current.contains(event.target)) {
        setShowTicketProcessTrackDropdown(false);
      }
      if (loginTrendsSBUDropdownRef.current && !loginTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowLoginTrendsSBUDropdown(false);
      }
      if (loginTrendsTrackDropdownRef.current && !loginTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowLoginTrendsTrackDropdown(false);
      }
      if (ticketTrendsSBUDropdownRef.current && !ticketTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowTicketTrendsSBUDropdown(false);
      }
      if (ticketTrendsTrackDropdownRef.current && !ticketTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowTicketTrendsTrackDropdown(false);
      }
      if (feedbackTrendsSBUDropdownRef.current && !feedbackTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowFeedbackTrendsSBUDropdown(false);
      }
      if (feedbackTrendsTrackDropdownRef.current && !feedbackTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowFeedbackTrendsTrackDropdown(false);
      }
      if (areasTrendsSBUDropdownRef.current && !areasTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowAreasTrendsSBUDropdown(false);
      }
      if (areasTrendsTrackDropdownRef.current && !areasTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowAreasTrendsTrackDropdown(false);
      }
      if (screenTimeTrendsSBUDropdownRef.current && !screenTimeTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowScreenTimeTrendsSBUDropdown(false);
      }
      if (screenTimeTrendsTrackDropdownRef.current && !screenTimeTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowScreenTimeTrendsTrackDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch data from backend
  const fetchData = async () => {
    const API_URL = "/api/tickets";
    const response = await fetch(API_URL, { method: "GET" });
    return response;
  };

  // Generate positive feedback data (Satisfied/Not Satisfied)
  const generatePositiveFeedbackData = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    // Filter AI feedback data by timestamp
    let filteredFeedback = aiFeedbackData;
    
    if (filterType === 'month' && month && year) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return feedbackDate.getFullYear() === parseInt(year) && feedbackDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return feedbackDate >= from && feedbackDate <= to;
      });
    }
    
    // Filter by SBU & Track (using userMapping by email)
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      const trackValues = (tracks && Array.isArray(tracks) && tracks.length > 0) ? tracks.map(t => t.value) : null;
      filteredFeedback = filteredFeedback.filter(f => {
        const mapping = getUserMapping(f.userEmail);
        if (!mapping) return false;
        if (!sbuValues.includes(mapping.sbu)) return false;
        if (trackValues && !trackValues.includes(mapping.track)) return false;
        return true;
      });
    }
    
    // Use filtered data from AI feedback table
    const satisfied = filteredFeedback.filter(f => f.feedbackType === 'thumbs_up').length;
    const notSatisfied = filteredFeedback.filter(f => f.feedbackType === 'thumbs_down').length;
    
    const feedbackData = [
      { name: 'Satisfied', count: satisfied, fill: 'url(#satisfiedGradient)' },
      { name: 'Not Satisfied', count: notSatisfied, fill: 'url(#notSatisfiedGradient)' }
    ];
    
    return feedbackData;
  };

  // Generate negative feedback data (Areas to be improved)
  const generateNegativeFeedbackData = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    // Filter AI feedback data by timestamp
    let filteredFeedback = aiFeedbackData;
    
    if (filterType === 'month' && month && year) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return feedbackDate.getFullYear() === parseInt(year) && feedbackDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return feedbackDate >= from && feedbackDate <= to;
      });
    }
    
    // Filter by SBU & Track (using userMapping by email)
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      const trackValues = (tracks && Array.isArray(tracks) && tracks.length > 0) ? tracks.map(t => t.value) : null;
      filteredFeedback = filteredFeedback.filter(f => {
        const mapping = getUserMapping(f.userEmail);
        if (!mapping) return false;
        if (!sbuValues.includes(mapping.sbu)) return false;
        if (trackValues && !trackValues.includes(mapping.track)) return false;
        return true;
      });
    }
    
    // Use filtered data from AI feedback table - group by reason
    const reasonCounts = {};
    
    filteredFeedback
      .filter(f => f.feedbackType === 'thumbs_down' && f.reason)
      .forEach(f => {
        const reason = f.reason;
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      });
    
    // Convert to array format for chart
    const improvementAreas = Object.keys(reasonCounts).map(reason => ({
      name: reason,
      count: reasonCounts[reason]
    }));
    
    // Sort by count descending
    improvementAreas.sort((a, b) => b.count - a.count);
    
    return improvementAreas;
  };

  // Generate Feedback Trends data - Satisfied/Not Satisfied grouped by track over 4 time periods
  const generateFeedbackTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!aiFeedbackData || aiFeedbackData.length === 0) return { data: [], periods: [] };
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 3; i >= 0; i--) {
        let m = selectedMonthIdx - i; let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({ key: `${3 - i}|${shortMonthNames[m]}`, filter: (f) => { if (!f.timestamp) return false; const d = new Date(f.timestamp); return d.getFullYear() === y && d.getMonth() === m; } });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      for (let i = 3; i >= 0; i--) {
        const wStart = new Date(selectedWeekStart); wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart); wEnd.setDate(wEnd.getDate() + 6); wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart); const wEndCopy = new Date(wEnd);
        periods.push({ key: `${3 - i}|${startLabel}-${endLabel}`, filter: (f) => { if (!f.timestamp) return false; const d = new Date(f.timestamp); return d >= wStartCopy && d <= wEndCopy; } });
      }
    }
    const periodKeys = periods.map(p => p.key);

    // Filter by SBU/Track (using userMapping by email)
    let filtered = aiFeedbackData;
    if (selectedSBUValues.length > 0) {
      filtered = filtered.filter(f => { const m = getUserMapping(f.userEmail); return selectedSBUValues.includes(m.sbu); });
    }
    if (selectedTrackValues.length > 0) {
      filtered = filtered.filter(f => { const m = getUserMapping(f.userEmail); return selectedTrackValues.includes(m.track); });
    }

    const showUsers = selectedTrackValues.length > 0;
    if (!showUsers) {
      // Group by track: each track gets Satisfied/Not Satisfied per period
      // Build: [{name: 'Track', 'Jan 2026 👍': x, 'Jan 2026 👎': y, ...}]
      const trackTotals = {};
      filtered.forEach(f => {
        const track = getUserMapping(f.userEmail).track || 'Unknown';
        const inAny = periods.some(p => p.filter(f));
        if (inAny) trackTotals[track] = (trackTotals[track] || 0) + 1;
      });
      const top5 = Object.entries(trackTotals).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([t]) => t);
      // For feedback we show Satisfied & Not Satisfied per period
      const satisfiedKeys = periodKeys.map(k => `${k} 👍`);
      const notSatisfiedKeys = periodKeys.map(k => `${k} 👎`);
      const allKeys = [];
      periodKeys.forEach(k => { allKeys.push(`${k} 👍`); allKeys.push(`${k} 👎`); });
      const data = top5.map(track => {
        const row = { name: track };
        periods.forEach(period => {
          const trackFeedback = filtered.filter(f => { return getUserMapping(f.userEmail).track === track && period.filter(f); });
          const satisfiedCount = trackFeedback.filter(f => f.feedbackType === 'thumbs_up').length;
          const notSatisfiedCount = trackFeedback.filter(f => f.feedbackType === 'thumbs_down').length;
          row[`${period.key} 👍`] = satisfiedCount;
          row[`${period.key} 👎`] = notSatisfiedCount;
          row[`${period.key} Total`] = satisfiedCount + notSatisfiedCount;
        });
        return row;
      });
      return { data, periods: allKeys };
    } else {
      const userTotals = {};
      filtered.forEach(f => {
        const userName = f.userName || f.userEmail?.split('@')[0] || 'Unknown';
        const inAny = periods.some(p => p.filter(f));
        if (inAny) userTotals[userName] = (userTotals[userName] || 0) + 1;
      });
      const top5 = Object.entries(userTotals).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([u]) => u);
      const allKeys = [];
      periodKeys.forEach(k => { allKeys.push(`${k} 👍`); allKeys.push(`${k} 👎`); });
      const data = top5.map(user => {
        const row = { name: user };
        periods.forEach(period => {
          const userFeedback = filtered.filter(f => { const uName = f.userName || f.userEmail?.split('@')[0] || 'Unknown'; return uName === user && period.filter(f); });
          const satisfiedCount = userFeedback.filter(f => f.feedbackType === 'thumbs_up').length;
          const notSatisfiedCount = userFeedback.filter(f => f.feedbackType === 'thumbs_down').length;
          row[`${period.key} 👍`] = satisfiedCount;
          row[`${period.key} 👎`] = notSatisfiedCount;
          row[`${period.key} Total`] = satisfiedCount + notSatisfiedCount;
        });
        return row;
      });
      return { data, periods: allKeys };
    }
  };

  // Generate Areas to Improved Trends data - top reasons grouped by track over 4 time periods
  const generateAreasTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!aiFeedbackData || aiFeedbackData.length === 0) return { data: [], periods: [] };
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 3; i >= 0; i--) {
        let m = selectedMonthIdx - i; let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({ key: `${3 - i}|${shortMonthNames[m]}`, filter: (f) => { if (!f.timestamp) return false; const d = new Date(f.timestamp); return d.getFullYear() === y && d.getMonth() === m; } });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      for (let i = 3; i >= 0; i--) {
        const wStart = new Date(selectedWeekStart); wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart); wEnd.setDate(wEnd.getDate() + 6); wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart); const wEndCopy = new Date(wEnd);
        periods.push({ key: `${3 - i}|${startLabel}-${endLabel}`, filter: (f) => { if (!f.timestamp) return false; const d = new Date(f.timestamp); return d >= wStartCopy && d <= wEndCopy; } });
      }
    }
    const periodKeys = periods.map(p => p.key);

    // Filter: only thumbs_down with reason, then by SBU/Track (using userMapping by email)
    let filtered = aiFeedbackData.filter(f => f.feedbackType === 'thumbs_down' && f.reason);
    if (selectedSBUValues.length > 0) {
      filtered = filtered.filter(f => { const m = getUserMapping(f.userEmail); return selectedSBUValues.includes(m.sbu); });
    }
    if (selectedTrackValues.length > 0) {
      filtered = filtered.filter(f => { const m = getUserMapping(f.userEmail); return selectedTrackValues.includes(m.track); });
    }

    // Group by reason, count per period
    const reasonTotals = {};
    filtered.forEach(f => {
      const reason = f.reason;
      const inAny = periods.some(p => p.filter(f));
      if (inAny) reasonTotals[reason] = (reasonTotals[reason] || 0) + 1;
    });
    const top5Reasons = Object.entries(reasonTotals).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([r]) => r);

    const data = top5Reasons.map(reason => {
      const row = { name: reason };
      periods.forEach(period => {
        row[period.key] = filtered.filter(f => f.reason === reason && period.filter(f)).length;
      });
      return row;
    });
    return { data, periods: periodKeys };
  };

  const getSessionScreenSeconds = (session) => (
    session.activeDuration ?? session.totalDuration ?? 0
  );

  const generateAssignmentGroupData = (activeFilterType, group, month, week, fromDate, toDate, year) => {
    // Use session data instead of ticket data
    if (!sessionData || sessionData.length === 0) return [];
    
    // Check if "All Groups" is selected or if it's an array of groups
    const currentGroups = Array.isArray(group) ? group : [];
    const isAllGroups = currentGroups.length === 0 || currentGroups.some(g => g.value === 'all');
    const selectedGroups = currentGroups.filter(g => g.value !== 'all');
    
    // Filter sessions by selected assignment groups
    let filteredSessions = sessionData;
    
    if (!isAllGroups && selectedGroups.length > 0) {
      filteredSessions = sessionData.filter(session => {
        const sessionGroup = session.assignmentGroup || '';
        return selectedGroups.some(g => sessionGroup === g.value);
      });
    }
    
    // Filter by date/month/year
    if (activeFilterType === 'month' && month && year) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (activeFilterType === 'date' && fromDate && toDate) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }
    
    console.log('Filtered sessions for Assignment Group:', filteredSessions.length);
    
    // Group by user and sum their screen time
    const memberScreenTime = {};
    const userGroups = {};
    
    filteredSessions.forEach(session => {
      const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
      const assignmentGroup = session.assignmentGroup || 'Unassigned';
      const duration = getSessionScreenSeconds(session);
      
      if (!memberScreenTime[userName]) {
        memberScreenTime[userName] = 0;
        userGroups[userName] = assignmentGroup;
      }
      
      memberScreenTime[userName] += duration;
    });
    
    // Convert to chart format
    const chartData = Object.keys(memberScreenTime).map(userName => ({
      name: userName,
      screenTime: parseFloat((memberScreenTime[userName] / 3600).toFixed(1)), // Convert seconds to hours
      team: userGroups[userName]
    }));
    
    // Sort by screen time descending and take top 10
    chartData.sort((a, b) => b.screenTime - a.screenTime);
    console.log('Assignment Group Chart Data:', chartData);
    return chartData.slice(0, 20); // Top 20 users
  };

  const generateTeamScreenTimeData = (activeFilterType, month, week, fromDate, toDate, year) => {
    // Use session data for real-time screen time instead of ticket data
    if (!sessionData || sessionData.length === 0) {
      return [];
    }
    
    // Filter sessions by date/month/year
    let filteredSessions = sessionData;
    
    if (activeFilterType === 'month' && month && year) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = new Date(session.startTime || session.timestamp);
        if (!session.startTime && !session.timestamp) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (activeFilterType === 'date' && fromDate && toDate) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = new Date(session.startTime || session.timestamp);
        if (!session.startTime && !session.timestamp) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }
    
    // Group sessions by assignment group and calculate total screen time
    const teamScreenTime = {};
    
    filteredSessions.forEach(session => {
      const team = session.assignmentGroup || 'Unassigned';
      if (!team || team === 'Unassigned') return;
      
      // totalDuration is in seconds, convert to hours
      const screenTimeHours = getSessionScreenSeconds(session) / 3600;
      
      if (!teamScreenTime[team]) {
        teamScreenTime[team] = 0;
      }
      teamScreenTime[team] += screenTimeHours;
    });
    
    // Convert to array format for chart
    const result = Object.entries(teamScreenTime)
      .map(([name, screenTime]) => ({
        name: name,
        screenTime: screenTime
      }))
        .filter(item => item.screenTime > 0)
      .sort((a, b) => b.screenTime - a.screenTime)
      .slice(0, 20); // Top 20 teams
    
    return result;
  };

  // Generate all teams data showing assignment groups filtered by selected track
  const generateAllTeamsData = (activeFilterType, month, week, fromDate, toDate, tracks, sbus, year) => {
    // Use session data instead of ticket data for real-time user screen time
    if (!sessionData || sessionData.length === 0) {
      return [];
    }
    
    // Filter sessions by date/month/year
    let filteredSessions = sessionData;
    
    if (activeFilterType === 'month' && month && year) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (activeFilterType === 'date' && fromDate && toDate) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }

    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];
    
    // Filter by SBU first (using userMapping by email)
    let sbuFilteredSessions = filteredSessions;
    if (selectedSBUValues.length > 0) {
      sbuFilteredSessions = filteredSessions.filter(session => {
        const mapping = getUserMapping(session.userEmail);
        return selectedSBUValues.includes(mapping.sbu);
      });
    }

    // If Track is NOT selected → aggregate by track (screen time)
    if (selectedTrackValues.length === 0) {
      const trackScreenTime = {};
      
      sbuFilteredSessions.forEach(session => {
        const mapping = getUserMapping(session.userEmail);
        const trackName = mapping.track || 'Unknown';
        const durationInSeconds = getSessionScreenSeconds(session);
        const screenTimeHours = durationInSeconds / 3600;
        
        if (!trackScreenTime[trackName]) {
          trackScreenTime[trackName] = { screenTime: 0, sbu: mapping.sbu, isTrackLevel: true };
        }
        trackScreenTime[trackName].screenTime += screenTimeHours;
      });
      
      const result = Object.entries(trackScreenTime)
        .map(([name, data]) => ({
          name,
          screenTime: data.screenTime,
          sbu: data.sbu,
          track: name,
          isTrackLevel: true
        }))
          .filter(item => item.screenTime > 0)
        .sort((a, b) => b.screenTime - a.screenTime)
        .slice(0, 20);
      
      return result;
    }
    
    // If Track IS selected → show individual users within that track
    let finalFilteredSessions = sbuFilteredSessions;
    if (selectedTrackValues.length > 0) {
      finalFilteredSessions = sbuFilteredSessions.filter(session => {
        const mapping = getUserMapping(session.userEmail);
        return selectedTrackValues.includes(mapping.track);
      });
    }
    
    const userScreenTime = {};
    
    finalFilteredSessions.forEach(session => {
      const mapping = getUserMapping(session.userEmail);
      const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
      const durationInSeconds = getSessionScreenSeconds(session);
      const screenTimeHours = durationInSeconds / 3600;
      
      if (!userScreenTime[userName]) {
        userScreenTime[userName] = { screenTime: 0, sbu: mapping.sbu, track: mapping.track, isTrackLevel: false };
      }
      userScreenTime[userName].screenTime += screenTimeHours;
    });
    
    const result = Object.entries(userScreenTime)
      .map(([name, data]) => ({
        name,
        screenTime: data.screenTime,
        sbu: data.sbu,
        track: data.track,
        isTrackLevel: false
      }))
        .filter(item => item.screenTime > 0)
      .sort((a, b) => b.screenTime - a.screenTime)
      .slice(0, 20);
    
    console.log('Screen Time by Track Data:', result);
    return result;
  };

  // Generate Screen Time Trends data - shows screen time (hours) for selected month + previous 3 months, or selected week + previous 3 weeks
  const generateScreenTimeTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!sessionData || sessionData.length === 0) return { data: [], periods: [] };
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    // Build time period definitions (4 periods: selected + 3 previous)
    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 3; i >= 0; i--) {
        let m = selectedMonthIdx - i;
        let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({
          key: `${3 - i}|${shortMonthNames[m]}`,
          filter: (session) => {
            const d = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
            if (!d) return false;
            return d.getFullYear() === y && d.getMonth() === m;
          }
        });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      
      for (let i = 3; i >= 0; i--) {
        const wStart = new Date(selectedWeekStart);
        wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart);
        wEnd.setDate(wEnd.getDate() + 6);
        wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart);
        const wEndCopy = new Date(wEnd);
        periods.push({
          key: `${3 - i}|${startLabel}-${endLabel}`,
          filter: (session) => {
            const d = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
            if (!d) return false;
            return d >= wStartCopy && d <= wEndCopy;
          }
        });
      }
    }

    const periodKeys = periods.map(p => p.key);

    // Determine if showing tracks or users
    const showUsers = selectedTrackValues.length > 0;

    // Filter sessions by SBU
    let sbuFiltered = sessionData;
    if (selectedSBUValues.length > 0) {
      sbuFiltered = sbuFiltered.filter(session => {
        return selectedSBUValues.includes(getUserMapping(session.userEmail).sbu);
      });
    }

    // Further filter by Track if selected
    let trackFiltered = sbuFiltered;
    if (selectedTrackValues.length > 0) {
      trackFiltered = sbuFiltered.filter(session => {
        return selectedTrackValues.includes(getUserMapping(session.userEmail).track);
      });
    }

    if (!showUsers) {
      // Group by track, sum screen time per period
      const trackTotals = {};
      sbuFiltered.forEach(session => {
        const track = getUserMapping(session.userEmail).track || 'Unknown';
        const inAnyPeriod = periods.some(p => p.filter(session));
        if (inAnyPeriod) {
          const durationHours = getSessionScreenSeconds(session) / 3600;
          trackTotals[track] = (trackTotals[track] || 0) + durationHours;
        }
      });

      // Get top 5 tracks
      const top5Tracks = Object.entries(trackTotals)
          .filter(([, total]) => total > 0)
          .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([track]) => track);

      // Build data for each top track with period screen times
      const data = top5Tracks.map(track => {
        const row = { name: track };
        periods.forEach(period => {
          const total = sbuFiltered
            .filter(session => getUserMapping(session.userEmail).track === track && period.filter(session))
            .reduce((sum, session) => sum + (getSessionScreenSeconds(session) / 3600), 0);
          row[period.key] = parseFloat(total.toFixed(1));
        });
        return row;
      });
      return { data, periods: periodKeys };
    } else {
      // Group by user, sum screen time per period
      const userTotals = {};
      trackFiltered.forEach(session => {
        const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
        const inAnyPeriod = periods.some(p => p.filter(session));
        if (inAnyPeriod) {
          const durationHours = getSessionScreenSeconds(session) / 3600;
          userTotals[userName] = (userTotals[userName] || 0) + durationHours;
        }
      });

      const top5Users = Object.entries(userTotals)
        .filter(([, total]) => total > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([user]) => user);

      const data = top5Users.map(user => {
        const row = { name: user };
        periods.forEach(period => {
          const total = trackFiltered
            .filter(session => {
              const uName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
              return uName === user && period.filter(session);
            })
            .reduce((sum, session) => sum + (getSessionScreenSeconds(session) / 3600), 0);
          row[period.key] = parseFloat(total.toFixed(1));
        });
        return row;
      });
      return { data, periods: periodKeys };
    }
  };

  // Generate Login Count data - counts how many times each user logged in
  // Generate Login Trends data - shows login counts for selected month + previous 3 months, or selected week + previous 3 weeks
  const generateLoginTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!sessionData || sessionData.length === 0) return { data: [], periods: [] };
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    // Build time period definitions (4 periods: selected + 3 previous)
    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 3; i >= 0; i--) {
        let m = selectedMonthIdx - i;
        let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({
          key: `${3 - i}|${shortMonthNames[m]}`,
          filter: (session) => {
            const d = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
            if (!d) return false;
            return d.getFullYear() === y && d.getMonth() === m;
          }
        });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      
      for (let i = 3; i >= 0; i--) {
        const wStart = new Date(selectedWeekStart);
        wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart);
        wEnd.setDate(wEnd.getDate() + 6);
        wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart);
        const wEndCopy = new Date(wEnd);
        periods.push({
          key: `${3 - i}|${startLabel}-${endLabel}`,
          filter: (session) => {
            const d = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
            if (!d) return false;
            return d >= wStartCopy && d <= wEndCopy;
          }
        });
      }
    }

    const periodKeys = periods.map(p => p.key);

    // Determine if showing tracks or users
    const showUsers = selectedTrackValues.length > 0;

    // Filter sessions by SBU (using userMapping by email)
    let sbuFiltered = sessionData;
    if (selectedSBUValues.length > 0) {
      sbuFiltered = sbuFiltered.filter(session => {
        return selectedSBUValues.includes(getUserMapping(session.userEmail).sbu);
      });
    }

    // Further filter by Track if selected
    let trackFiltered = sbuFiltered;
    if (selectedTrackValues.length > 0) {
      trackFiltered = sbuFiltered.filter(session => {
        return selectedTrackValues.includes(getUserMapping(session.userEmail).track);
      });
    }

    if (!showUsers) {
      // Group by track, count per period
      const trackTotals = {};
      // Count total logins across all periods per track (for ranking)
      sbuFiltered.forEach(session => {
        const track = getUserMapping(session.userEmail).track || 'Unknown';
        // Check if session falls in any of our periods
        const inAnyPeriod = periods.some(p => p.filter(session));
        if (inAnyPeriod) {
          trackTotals[track] = (trackTotals[track] || 0) + 1;
        }
      });

      // Get top 5 tracks
      const top5Tracks = Object.entries(trackTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([track]) => track);

      // Build data for each top track with period counts
      const data = top5Tracks.map(track => {
        const row = { name: track };
        periods.forEach(period => {
          const count = sbuFiltered.filter(session => {
            return getUserMapping(session.userEmail).track === track && period.filter(session);
          }).length;
          row[period.key] = count;
        });
        return row;
      });
      return { data, periods: periodKeys };
    } else {
      // Group by user, count per period
      const userTotals = {};
      trackFiltered.forEach(session => {
        const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
        const inAnyPeriod = periods.some(p => p.filter(session));
        if (inAnyPeriod) {
          userTotals[userName] = (userTotals[userName] || 0) + 1;
        }
      });

      const top5Users = Object.entries(userTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([user]) => user);

      const data = top5Users.map(user => {
        const row = { name: user };
        periods.forEach(period => {
          const count = trackFiltered.filter(session => {
            const uName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
            return uName === user && period.filter(session);
          }).length;
          row[period.key] = count;
        });
        return row;
      });
      return { data, periods: periodKeys };
    }
  };

  const generateLoginCountData = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    if (!sessionData || sessionData.length === 0) return [];
    
    let filteredSessions = sessionData;
    
    // Filter by date/month
    if (filterType === 'month' && month && year) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }

    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];
    
    // Filter by SBU first (using userMapping by email)
    let sbuFilteredSessions = filteredSessions;
    if (selectedSBUValues.length > 0) {
      sbuFilteredSessions = filteredSessions.filter(session => {
        const mapping = getUserMapping(session.userEmail);
        return selectedSBUValues.includes(mapping.sbu);
      });
    }

    // If Track is NOT selected → aggregate by track
    if (selectedTrackValues.length === 0) {
      const trackLoginCounts = {};
      
      sbuFilteredSessions.forEach(session => {
        const mapping = getUserMapping(session.userEmail);
        const trackName = mapping.track || 'Unknown';
        
        if (!trackLoginCounts[trackName]) {
          trackLoginCounts[trackName] = { count: 0, sbu: mapping.sbu, isTrackLevel: true };
        }
        trackLoginCounts[trackName].count += 1;
      });
      
      const result = Object.entries(trackLoginCounts)
        .map(([name, data]) => ({
          name,
          loginCount: data.count,
          sbu: data.sbu,
          track: name,
          isTrackLevel: true
        }))
        .sort((a, b) => b.loginCount - a.loginCount)
        .slice(0, 20);
      
      return result;
    }
    
    // If Track IS selected → show individual users within that track
    let finalFilteredSessions = sbuFilteredSessions;
    if (selectedTrackValues.length > 0) {
      finalFilteredSessions = sbuFilteredSessions.filter(session => {
        const mapping = getUserMapping(session.userEmail);
        return selectedTrackValues.includes(mapping.track);
      });
    }
    
    const userLoginCounts = {};
    
    finalFilteredSessions.forEach(session => {
      const mapping = getUserMapping(session.userEmail);
      const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
      
      if (!userLoginCounts[userName]) {
        userLoginCounts[userName] = { count: 0, group: session.assignmentGroup || '', sbu: mapping.sbu, track: mapping.track };
      }
      userLoginCounts[userName].count += 1;
    });
    
    const result = Object.entries(userLoginCounts)
      .map(([name, data]) => ({
        name,
        loginCount: data.count,
        group: data.group,
        sbu: data.sbu,
        track: data.track,
        isTrackLevel: false
      }))
      .sort((a, b) => b.loginCount - a.loginCount)
      .slice(0, 20);
    
    return result;
  };

  // Generate Ticket Process data - shows who clicked Process button (AI recommendation)
  // Uses the user's HOME track (from sessionData/login) not the ticket's assignment group
  // SBU selected + no Track → aggregate by track; Track selected → show individual users
  const generateTicketProcessData = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    if (!processButtonData || processButtonData.length === 0) return [];
    
    // Build user→track mapping from sessionData using userEmail (lowercase) as key
    // This ensures consistent matching between sessions and process button clicks
    const userTrackMap = {};
    if (sessionData && sessionData.length > 0) {
      sessionData.forEach(session => {
        const userEmail = (session.userEmail || '').toLowerCase().trim();
        const group = session.assignmentGroup || '';
        if (!userEmail) return;
        
        if (!userTrackMap[userEmail]) {
          userTrackMap[userEmail] = { groups: {}, userName: session.userName || userEmail.split('@')[0] };
        }
        // Count occurrences of each assignment group per user to find their primary group
        if (group) {
          userTrackMap[userEmail].groups[group] = (userTrackMap[userEmail].groups[group] || 0) + 1;
        }
      });
    }
    
    // Resolve each user's SBU/Track directly from userMapping by email
    const getUserHomeMapping = (userEmail) => {
      const mapping = getUserMapping(userEmail);
      const emailKey = (userEmail || '').toLowerCase().trim();
      const userData = userTrackMap[emailKey];
      const userName = userData ? userData.userName : (userEmail?.split('@')[0] || 'Unknown');
      const primaryGroup = userData && Object.keys(userData.groups).length > 0 
        ? Object.entries(userData.groups).sort((a, b) => b[1] - a[1])[0][0] 
        : '';
      return { sbu: mapping.sbu, track: mapping.track, group: primaryGroup, userName };
    };
    
    let filteredClicks = processButtonData;
    
    // Filter by date/month
    if (filterType === 'month' && month && year) {
      filteredClicks = filteredClicks.filter(click => {
        const clickDate = click.clickedAt ? new Date(click.clickedAt) : (click.timestamp ? new Date(click.timestamp) : null);
        if (!clickDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return clickDate.getFullYear() === parseInt(year) && clickDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredClicks = filteredClicks.filter(click => {
        const clickDate = click.clickedAt ? new Date(click.clickedAt) : (click.timestamp ? new Date(click.timestamp) : null);
        if (!clickDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return clickDate >= from && clickDate <= to;
      });
    }

    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];
    
    // Filter by SBU using user's HOME track (not ticket's assignment group)
    let sbuFilteredClicks = filteredClicks;
    if (selectedSBUValues.length > 0) {
      sbuFilteredClicks = filteredClicks.filter(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        return selectedSBUValues.includes(homeMapping.sbu);
      });
    }

    // If Track is NOT selected → aggregate by user's home track
    if (selectedTrackValues.length === 0) {
      const trackCounts = {};
      
      sbuFilteredClicks.forEach(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        const trackName = homeMapping.track || 'Unknown';
        
        if (!trackCounts[trackName]) {
          trackCounts[trackName] = { count: 0, sbu: homeMapping.sbu, isTrackLevel: true };
        }
        trackCounts[trackName].count += 1;
      });
      
      const result = Object.entries(trackCounts)
        .map(([name, data]) => ({
          name,
          processCount: data.count,
          sbu: data.sbu,
          track: name,
          isTrackLevel: true
        }))
        .sort((a, b) => b.processCount - a.processCount)
        .slice(0, 20);
      
      return result;
    }
    
    // If Track IS selected → show individual users within that track (user's home track)
    let finalFilteredClicks = sbuFilteredClicks;
    if (selectedTrackValues.length > 0) {
      finalFilteredClicks = sbuFilteredClicks.filter(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        return selectedTrackValues.includes(homeMapping.track);
      });
    }
    
    const userCounts = {};
    
    finalFilteredClicks.forEach(click => {
      const homeMapping = getUserHomeMapping(click.userEmail);
      const displayName = homeMapping.userName || click.userName || click.userEmail?.split('@')[0] || 'Unknown';
      
      if (!userCounts[displayName]) {
        userCounts[displayName] = { count: 0, group: homeMapping.group, sbu: homeMapping.sbu, track: homeMapping.track };
      }
      userCounts[displayName].count += 1;
    });
    
    const result = Object.entries(userCounts)
      .map(([name, data]) => ({
        name,
        processCount: data.count,
        group: data.group,
        sbu: data.sbu,
        track: data.track,
        isTrackLevel: false
      }))
      .sort((a, b) => b.processCount - a.processCount)
      .slice(0, 20);
    
    return result;
  };

  // Generate Ticket Update data - shows who updated ticket in ServiceNow
  // SBU selected + no Track -> aggregate by track; Track selected -> show individual users
  const generateTicketUpdateData = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    if (!ticketUpdateTrackingData || ticketUpdateTrackingData.length === 0) return [];

    let filteredUpdates = ticketUpdateTrackingData;

    // Filter by date/month
    if (filterType === 'month' && month && year) {
      filteredUpdates = filteredUpdates.filter(update => {
        const updateDate = update.updatedAt ? new Date(update.updatedAt) : (update.timestamp ? new Date(update.timestamp) : null);
        if (!updateDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return updateDate.getFullYear() === parseInt(year) && updateDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredUpdates = filteredUpdates.filter(update => {
        const updateDate = update.updatedAt ? new Date(update.updatedAt) : (update.timestamp ? new Date(update.timestamp) : null);
        if (!updateDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return updateDate >= from && updateDate <= to;
      });
    }

    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    // Filter by SBU
    let sbuFilteredUpdates = filteredUpdates;
    if (selectedSBUValues.length > 0) {
      sbuFilteredUpdates = filteredUpdates.filter(update => {
        const mapping = getUserMapping(update.userEmail);
        return selectedSBUValues.includes(mapping.sbu);
      });
    }

    // If Track is NOT selected -> aggregate by track
    if (selectedTrackValues.length === 0) {
      const trackCounts = {};

      sbuFilteredUpdates.forEach(update => {
        const mapping = getUserMapping(update.userEmail);
        const trackName = mapping.track || 'Unknown';

        if (!trackCounts[trackName]) {
          trackCounts[trackName] = { count: 0, sbu: mapping.sbu, isTrackLevel: true };
        }
        trackCounts[trackName].count += 1;
      });

      return Object.entries(trackCounts)
        .map(([name, data]) => ({
          name,
          updateCount: data.count,
          sbu: data.sbu,
          track: name,
          isTrackLevel: true
        }))
        .sort((a, b) => b.updateCount - a.updateCount)
        .slice(0, 20);
    }

    // If Track IS selected -> show individual users in selected track(s)
    let finalFilteredUpdates = sbuFilteredUpdates;
    if (selectedTrackValues.length > 0) {
      finalFilteredUpdates = sbuFilteredUpdates.filter(update => {
        const mapping = getUserMapping(update.userEmail);
        return selectedTrackValues.includes(mapping.track);
      });
    }

    const userCounts = {};

    finalFilteredUpdates.forEach(update => {
      const mapping = getUserMapping(update.userEmail);
      const displayName = update.userName || (update.userEmail ? update.userEmail.split('@')[0] : 'Unknown');

      if (!userCounts[displayName]) {
        userCounts[displayName] = { count: 0, sbu: mapping.sbu, track: mapping.track };
      }
      userCounts[displayName].count += 1;
    });

    return Object.entries(userCounts)
      .map(([name, data]) => ({
        name,
        updateCount: data.count,
        sbu: data.sbu,
        track: data.track,
        isTrackLevel: false
      }))
      .sort((a, b) => b.updateCount - a.updateCount)
      .slice(0, 20);
  };

  const generateTicketTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!processButtonData || processButtonData.length === 0) return { data: [], periods: [] };

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    const userTrackMap = {};
    if (sessionData && sessionData.length > 0) {
      sessionData.forEach(session => {
        const userEmail = (session.userEmail || '').toLowerCase().trim();
        const group = session.assignmentGroup || '';
        if (!userEmail) return;
        if (!userTrackMap[userEmail]) {
          userTrackMap[userEmail] = { groups: {}, userName: session.userName || userEmail.split('@')[0] };
        }
        if (group) {
          userTrackMap[userEmail].groups[group] = (userTrackMap[userEmail].groups[group] || 0) + 1;
        }
      });
    }

    const getUserHomeMapping = (userEmail) => {
      const mapping = getUserMapping(userEmail);
      const emailKey = (userEmail || '').toLowerCase().trim();
      const userData = userTrackMap[emailKey];
      const userName = userData ? userData.userName : (userEmail?.split('@')[0] || 'Unknown');
      const primaryGroup = userData && Object.keys(userData.groups).length > 0
        ? Object.entries(userData.groups).sort((a, b) => b[1] - a[1])[0][0]
        : '';
      return { sbu: mapping.sbu, track: mapping.track, group: primaryGroup, userName };
    };

    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 0; i <= 3; i++) {
        let m = selectedMonthIdx - i;
        let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({
          key: `${i}|${shortMonthNames[m]} ${y}`,
          filter: (click) => {
            const d = click.clickedAt ? new Date(click.clickedAt) : (click.timestamp ? new Date(click.timestamp) : null);
            if (!d) return false;
            return d.getFullYear() === y && d.getMonth() === m;
          }
        });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      for (let i = 0; i <= 3; i++) {
        const wStart = new Date(selectedWeekStart);
        wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart);
        wEnd.setDate(wEnd.getDate() + 6);
        wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart);
        const wEndCopy = new Date(wEnd);
        periods.push({
          key: `${i}|${startLabel}-${endLabel}`,
          filter: (click) => {
            const d = click.clickedAt ? new Date(click.clickedAt) : (click.timestamp ? new Date(click.timestamp) : null);
            if (!d) return false;
            return d >= wStartCopy && d <= wEndCopy;
          }
        });
      }
    }

    const periodKeys = periods.map(p => p.key);
    const showUsers = selectedTrackValues.length > 0;

    let sbuFiltered = processButtonData;
    if (selectedSBUValues.length > 0) {
      sbuFiltered = sbuFiltered.filter(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        return selectedSBUValues.includes(homeMapping.sbu);
      });
    }

    let trackFiltered = sbuFiltered;
    if (selectedTrackValues.length > 0) {
      trackFiltered = sbuFiltered.filter(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        return selectedTrackValues.includes(homeMapping.track);
      });
    }

    if (!showUsers) {
      const trackTotals = {};
      sbuFiltered.forEach(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        const track = homeMapping.track || 'Unknown';
        const inAnyPeriod = periods.some(p => p.filter(click));
        if (inAnyPeriod) {
          trackTotals[track] = (trackTotals[track] || 0) + 1;
        }
      });

      const top5Tracks = Object.entries(trackTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([track]) => track);

      const data = top5Tracks.map(track => {
        const row = { name: track };
        periods.forEach(period => {
          const count = sbuFiltered.filter(click => {
            const homeMapping = getUserHomeMapping(click.userEmail);
            return homeMapping.track === track && period.filter(click);
          }).length;
          row[period.key] = count;
        });
        return row;
      });
      return { data, periods: periodKeys };
    }

    const userTotals = {};
    trackFiltered.forEach(click => {
      const homeMapping = getUserHomeMapping(click.userEmail);
      const userName = homeMapping.userName || click.userName || click.userEmail?.split('@')[0] || 'Unknown';
      const inAnyPeriod = periods.some(p => p.filter(click));
      if (inAnyPeriod) {
        userTotals[userName] = (userTotals[userName] || 0) + 1;
      }
    });

    const top5Users = Object.entries(userTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([user]) => user);

    const data = top5Users.map(user => {
      const row = { name: user };
      periods.forEach(period => {
        const count = trackFiltered.filter(click => {
          const homeMapping = getUserHomeMapping(click.userEmail);
          const userName = homeMapping.userName || click.userName || click.userEmail?.split('@')[0] || 'Unknown';
          return userName === user && period.filter(click);
        }).length;
        row[period.key] = count;
      });
      return row;
    });
      return { data, periods: periodKeys };
  };

  // Load Track Mapping - from CSV API first, then fallback to hardcoded
  useEffect(() => {
    const loadTrackMapping = async () => {
      // Try to load group mapping from CSV file via API
      await loadGroupMappingFromCSV();
      // Try to load user mapping from CSV file via API
      await loadUserMappingFromCSV();

      // Load SBUs from groupMapping (for Screen Time dashboards)
      const sbus = getAllSBUs();
      const sbuOptions = [
        { value: 'all', label: 'All SBUs' },
        ...sbus.map(sbu => ({ value: sbu, label: sbu }))
      ];
      setAvailableSBUs(sbuOptions);
      // Start with all SBUs selected (excluding the 'all' option itself)
      setSelectedSBU(sbus.map(sbu => ({ value: sbu, label: sbu })));
      
      // Load Tracks from groupMapping (for Screen Time dashboards)
      const tracks = getAllTracks();
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableTracks(trackOptions);
      // Start with no tracks selected (user must pick SBU first, then Track)
      setSelectedTrack([]);

      // Load SBUs & Tracks from userMapping (for Login Count, Ticket Process, Feedback, and Trends dashboards)
      const userSbus = getAllUserSBUs();
      const userSbuOptions = [
        { value: 'all', label: 'All SBUs' },
        ...userSbus.map(sbu => ({ value: sbu, label: sbu }))
      ];
      setAvailableUserSBUs(userSbuOptions);

      const userTracks = getAllUserTracks();
      const userTrackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableUserTracks(userTrackOptions);

      // Initialize Login Count SBU/Track selections (from userMapping)
      setLoginCountSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setLoginCountTrack([]);
      setLoginCountAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ]);
      // Initialize Ticket Process SBU/Track selections (from userMapping)
      setTicketProcessSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setTicketProcessTrack([]);
      setTicketProcessAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ]);
      // Initialize Positive Feedback SBU/Track selections (from userMapping)
      setPositiveFeedbackSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setPositiveFeedbackTrack([]);
      setPositiveFeedbackAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ]);
      // Initialize Negative Feedback SBU/Track selections (from userMapping)
      setNegativeFeedbackSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setNegativeFeedbackTrack([]);
      setNegativeFeedbackAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ]);
      // Initialize Trends dashboards SBU to all selected (from userMapping)
      setLoginTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setTicketTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setFeedbackTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setAreasTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setScreenTimeTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      // Initialize Trends dashboards available tracks (from userMapping)
      const trendTrackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ];
      setLoginTrendsAvailableTracks(trendTrackOptions);
      setTicketTrendsAvailableTracks(trendTrackOptions);
      setFeedbackTrendsAvailableTracks(trendTrackOptions);
      setAreasTrendsAvailableTracks(trendTrackOptions);
      setScreenTimeTrendsAvailableTracks(trendTrackOptions);
    };
    
    loadTrackMapping();
  }, []);

  // Load tickets from backend on mount
  useEffect(() => {
    const loadTicketsData = async () => {
      // Try to load from cache first (faster)
      const cachedData = localStorage.getItem('agentAssistTicketsCache');
      if (cachedData) {
        try {
          const cache = JSON.parse(cachedData);
          const tickets = cache.tickets || cache;
          // Ensure tickets is an array and has data
          if (Array.isArray(tickets) && tickets.length > 0) {
            setTicketsData(tickets);
            console.log('Loaded', tickets.length, 'tickets from cache');
            return; // Use cache, don't fetch
          }
        } catch (cacheErr) {
          console.error('Error loading cached data:', cacheErr);
        }
      }
      
      // No cache or cache is empty, fetch from API
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetchData();
        const result = await response.json();
        const tickets = result.result || [];
        
        setTicketsData(tickets);
        
        // Cache the data
        const cacheData = {
          tickets: tickets,
          timestamp: Date.now()
        };
        localStorage.setItem('agentAssistTicketsCache', JSON.stringify(cacheData));
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load ticket data');
      } finally {
        setLoading(false);
      }
    };
    
    loadTicketsData();
  }, []);
  
  // Function to manually refresh data
  const handleSyncData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchData();
      const result = await response.json();
      const tickets = result.result || [];
      
      setTicketsData(tickets);
      
      // Cache the data
      const cacheData = {
        tickets: tickets,
        timestamp: Date.now()
      };
      localStorage.setItem('agentAssistTicketsCache', JSON.stringify(cacheData));
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to sync data');
    } finally {
      setLoading(false);
    }
  };

  // Initialize available groups from tickets data
  useEffect(() => {
    if (!ticketsData || ticketsData.length === 0) return;
    
    // Extract unique assignment groups from tickets
    const uniqueGroups = [...new Set(ticketsData
      .map(ticket => ticket.assignment_group || ticket.assignmentGroup)
      .filter(group => group !== null && group !== undefined && group !== '')
    )];
    
    // Create group options with "All Groups" as first option
    const groupOptions = [
      { value: 'all', label: 'All Groups' },
      ...uniqueGroups.map(group => ({
        value: group,
        label: group
      })).sort((a, b) => a.label.localeCompare(b.label))
    ];
    
    setAvailableGroups(groupOptions);
    
    // Set default for Assignment Group filter to "All Groups" (only once when groups are first loaded)
    if (groupOptions.length > 0 && availableGroups.length === 0) {
      // First time loading groups, set default to all individual groups (not the "All Groups" option)
      setSelectedGroup(groupOptions.filter(g => g.value !== 'all')); // All individual groups
      
      // Feedback SBU/Track is initialized in loadTrackMapping, no group init needed here
    }
  }, [ticketsData, availableGroups.length]);

  // Filter available tracks based on selected SBU
  useEffect(() => {
    if (availableSBUs.length === 0) return;
    
    // If SBU is selected, filter tracks to only show those in selected SBUs
    if (selectedSBU && selectedSBU.length > 0) {
      const selectedSBUValues = selectedSBU.map(s => s.value);
      // Get all tracks from the mapping that belong to selected SBUs
      const tracksInSelectedSBUs = new Set();
      Object.values(GROUP_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSelectedSBUs.add(mapping.track);
        }
      });
      // Include 'Others' track when 'Others' SBU is selected
      if (selectedSBUValues.includes('Others')) {
        tracksInSelectedSBUs.add('Others');
      }
      
      // Filter available tracks
      const allTracks = getAllTracks();
      const filteredTracks = allTracks.filter(track => tracksInSelectedSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableTracks(trackOptions);
      
      // Clear selected tracks if they're not in the filtered list
      if (selectedTrack && selectedTrack.length > 0) {
        const validTracks = selectedTrack.filter(t => 
          filteredTracks.includes(t.value) || t.value === 'all'
        );
        if (validTracks.length !== selectedTrack.length) {
          setSelectedTrack(validTracks);
        }
      }
    } else {
      // No SBU selected, show all tracks
      const tracks = getAllTracks();
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableTracks(trackOptions);
    }
  }, [selectedSBU, availableSBUs.length]);

  // Filter Ticket Process available tracks based on Ticket Process SBU selection (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    
    if (ticketProcessSBU && ticketProcessSBU.length > 0) {
      const selectedSBUValues = ticketProcessSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSBUs.add(mapping.track);
        }
      });
      if (selectedSBUValues.includes('Others')) {
        tracksInSBUs.add('Others');
      }
      
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setTicketProcessAvailableTracks(trackOptions);
      
      if (ticketProcessTrack && ticketProcessTrack.length > 0) {
        const validTracks = ticketProcessTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== ticketProcessTrack.length) {
          setTicketProcessTrack(validTracks);
        }
      }
    } else {
      const tracks = getAllUserTracks();
      setTicketProcessAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ]);
    }
  }, [ticketProcessSBU, availableUserSBUs.length]);

  // Filter Login Count available tracks based on Login Count SBU selection (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    
    if (loginCountSBU && loginCountSBU.length > 0) {
      const selectedSBUValues = loginCountSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSBUs.add(mapping.track);
        }
      });
      if (selectedSBUValues.includes('Others')) {
        tracksInSBUs.add('Others');
      }
      
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setLoginCountAvailableTracks(trackOptions);
      
      if (loginCountTrack && loginCountTrack.length > 0) {
        const validTracks = loginCountTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== loginCountTrack.length) {
          setLoginCountTrack(validTracks);
        }
      }
    } else {
      const tracks = getAllUserTracks();
      setLoginCountAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ]);
    }
  }, [loginCountSBU, availableUserSBUs.length]);

  // Filter Login Trends available tracks based on Login Trends SBU selection (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    
    if (loginTrendsSBU && loginTrendsSBU.length > 0) {
      const selectedSBUValues = loginTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSBUs.add(mapping.track);
        }
      });
      if (selectedSBUValues.includes('Others')) {
        tracksInSBUs.add('Others');
      }
      
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setLoginTrendsAvailableTracks(trackOptions);
      
      if (loginTrendsTrack && loginTrendsTrack.length > 0) {
        const validTracks = loginTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== loginTrendsTrack.length) {
          setLoginTrendsTrack(validTracks);
        }
      }
    } else {
      const tracks = getAllUserTracks();
      setLoginTrendsAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ]);
    }
  }, [loginTrendsSBU, availableUserSBUs.length]);

  // Filter Ticket Trends available tracks based on Ticket Trends SBU selection (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    
    if (ticketTrendsSBU && ticketTrendsSBU.length > 0) {
      const selectedSBUValues = ticketTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSBUs.add(mapping.track);
        }
      });
      if (selectedSBUValues.includes('Others')) {
        tracksInSBUs.add('Others');
      }
      
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setTicketTrendsAvailableTracks(trackOptions);
      
      if (ticketTrendsTrack && ticketTrendsTrack.length > 0) {
        const validTracks = ticketTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== ticketTrendsTrack.length) {
          setTicketTrendsTrack(validTracks);
        }
      }
    } else {
      const tracks = getAllUserTracks();
      setTicketTrendsAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ]);
    }
  }, [ticketTrendsSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Feedback Trends (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    if (feedbackTrendsSBU && feedbackTrendsSBU.length > 0) {
      const selectedSBUValues = feedbackTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => { if (selectedSBUValues.includes(mapping.sbu)) tracksInSBUs.add(mapping.track); });
      if (selectedSBUValues.includes('Others')) tracksInSBUs.add('Others');
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      setFeedbackTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...filteredTracks.map(track => ({ value: track, label: track }))]);
      if (feedbackTrendsTrack && feedbackTrendsTrack.length > 0) {
        const validTracks = feedbackTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== feedbackTrendsTrack.length) setFeedbackTrendsTrack(validTracks);
      }
    } else {
      const tracks = getAllUserTracks();
      setFeedbackTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...tracks.map(track => ({ value: track, label: track }))]);
    }
  }, [feedbackTrendsSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Areas Trends (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    if (areasTrendsSBU && areasTrendsSBU.length > 0) {
      const selectedSBUValues = areasTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => { if (selectedSBUValues.includes(mapping.sbu)) tracksInSBUs.add(mapping.track); });
      if (selectedSBUValues.includes('Others')) tracksInSBUs.add('Others');
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      setAreasTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...filteredTracks.map(track => ({ value: track, label: track }))]);
      if (areasTrendsTrack && areasTrendsTrack.length > 0) {
        const validTracks = areasTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== areasTrendsTrack.length) setAreasTrendsTrack(validTracks);
      }
    } else {
      const tracks = getAllUserTracks();
      setAreasTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...tracks.map(track => ({ value: track, label: track }))]);
    }
  }, [areasTrendsSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Screen Time Trends (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    if (screenTimeTrendsSBU && screenTimeTrendsSBU.length > 0) {
      const selectedSBUValues = screenTimeTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => { if (selectedSBUValues.includes(mapping.sbu)) tracksInSBUs.add(mapping.track); });
      if (selectedSBUValues.includes('Others')) tracksInSBUs.add('Others');
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      setScreenTimeTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...filteredTracks.map(track => ({ value: track, label: track }))]);
      if (screenTimeTrendsTrack && screenTimeTrendsTrack.length > 0) {
        const validTracks = screenTimeTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== screenTimeTrendsTrack.length) setScreenTimeTrendsTrack(validTracks);
      }
    } else {
      const tracks = getAllUserTracks();
      setScreenTimeTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...tracks.map(track => ({ value: track, label: track }))]);
    }
  }, [screenTimeTrendsSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Positive Feedback (userMapping)
  useEffect(() => {
    if (positiveFeedbackSBU && positiveFeedbackSBU.length > 0 && availableUserSBUs.length > 0) {
      const selectedSbuValues = positiveFeedbackSBU.map(s => s.value);
      const allTracks = getAllUserTracks();
      const filtered = allTracks.filter(track => {
        if (track === 'Others' && selectedSbuValues.includes('Others')) return true;
        const entries = Object.entries(USERMAIL_MAPPING);
        return entries.some(([, mapping]) => mapping.track === track && selectedSbuValues.includes(mapping.sbu));
      });
      setPositiveFeedbackAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...filtered.map(t => ({ value: t, label: t }))
      ]);
      // Remove any currently selected tracks that are no longer available
      if (positiveFeedbackTrack && positiveFeedbackTrack.length > 0) {
        const valid = positiveFeedbackTrack.filter(t => filtered.includes(t.value));
        if (valid.length !== positiveFeedbackTrack.length) {
          setPositiveFeedbackTrack(valid);
        }
      }
    }
  }, [positiveFeedbackSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Negative Feedback (userMapping)
  useEffect(() => {
    if (negativeFeedbackSBU && negativeFeedbackSBU.length > 0 && availableUserSBUs.length > 0) {
      const selectedSbuValues = negativeFeedbackSBU.map(s => s.value);
      const allTracks = getAllUserTracks();
      const filtered = allTracks.filter(track => {
        if (track === 'Others' && selectedSbuValues.includes('Others')) return true;
        const entries = Object.entries(USERMAIL_MAPPING);
        return entries.some(([, mapping]) => mapping.track === track && selectedSbuValues.includes(mapping.sbu));
      });
      setNegativeFeedbackAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...filtered.map(t => ({ value: t, label: t }))
      ]);
      // Remove any currently selected tracks that are no longer available
      if (negativeFeedbackTrack && negativeFeedbackTrack.length > 0) {
        const valid = negativeFeedbackTrack.filter(t => filtered.includes(t.value));
        if (valid.length !== negativeFeedbackTrack.length) {
          setNegativeFeedbackTrack(valid);
        }
      }
    }
  }, [negativeFeedbackSBU, availableUserSBUs.length]);

  // Update data when filters or AI feedback data changes
  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      setPositiveFeedbackData(generatePositiveFeedbackData(positiveFeedbackFilterType, selectedPositiveFeedbackMonth, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, selectedPositiveFeedbackYear, positiveFeedbackSBU, positiveFeedbackTrack));
    }
  }, [aiFeedbackData, selectedPositiveFeedbackMonth, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, selectedPositiveFeedbackYear, positiveFeedbackSBU, positiveFeedbackTrack, positiveFeedbackFilterType]);

  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      setNegativeFeedbackData(generateNegativeFeedbackData(negativeFeedbackFilterType, selectedNegativeFeedbackMonth, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, selectedNegativeFeedbackYear, negativeFeedbackSBU, negativeFeedbackTrack));
    }
  }, [aiFeedbackData, selectedNegativeFeedbackMonth, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, selectedNegativeFeedbackYear, negativeFeedbackSBU, negativeFeedbackTrack, negativeFeedbackFilterType]);

  useEffect(() => {
    if (sessionData.length > 0) {
      setAssignmentGroupData(generateAssignmentGroupData(groupFilterType, selectedGroup, selectedGroupMonth, selectedGroupWeek, selectedGroupFromDate, selectedGroupToDate, selectedGroupYear));
    }
  }, [sessionData, selectedGroup, selectedGroupMonth, selectedGroupWeek, selectedGroupFromDate, selectedGroupToDate, selectedGroupYear, groupFilterType]);

  useEffect(() => {
    if (sessionData.length > 0) {
      setTeamScreenTimeData(generateTeamScreenTimeData(teamFilterType, selectedTeamMonth, selectedTeamWeek, selectedTeamFromDate, selectedTeamToDate, selectedTeamYear));
    }
  }, [selectedTeamMonth, selectedTeamWeek, selectedTeamFromDate, selectedTeamToDate, selectedTeamYear, teamFilterType, sessionData]);

  useEffect(() => {
    if (sessionData.length > 0) {
      setAllTeamsData(generateAllTeamsData(allTeamsFilterType, selectedAllTeamsMonth, selectedAllTeamsWeek, selectedAllTeamsFromDate, selectedAllTeamsToDate, selectedTrack, selectedSBU, selectedAllTeamsYear));
    }
  }, [selectedAllTeamsMonth, selectedAllTeamsWeek, selectedAllTeamsFromDate, selectedAllTeamsToDate, selectedAllTeamsYear, allTeamsFilterType, sessionData, selectedTrack, selectedSBU, availableTracks, availableSBUs]);

  // Login Count data update
  useEffect(() => {
    if (sessionData.length > 0) {
      setLoginCountData(generateLoginCountData(loginCountFilterType, loginCountMonth, loginCountFromDate, loginCountToDate, loginCountYear, loginCountSBU, loginCountTrack));
    }
  }, [sessionData, loginCountFilterType, loginCountMonth, loginCountYear, loginCountFromDate, loginCountToDate, loginCountSBU, loginCountTrack]);

  // Login Trends data update
  useEffect(() => {
    if (sessionData.length > 0) {
      const result = generateLoginTrendsData(loginTrendsFilterType, loginTrendsMonth, loginTrendsYear, loginTrendsWeek, loginTrendsSBU, loginTrendsTrack);
      setLoginTrendsData(result.data || []);
      setLoginTrendsPeriods(sortTrendPeriods(result.periods || []));
    }
  }, [sessionData, loginTrendsFilterType, loginTrendsMonth, loginTrendsYear, loginTrendsWeek, loginTrendsSBU, loginTrendsTrack]);

  // Ticket Process data update (depends on sessionData for user home track lookup)
  useEffect(() => {
    if (processButtonData.length > 0) {
      setTicketProcessData(generateTicketProcessData(ticketProcessFilterType, ticketProcessMonth, ticketProcessFromDate, ticketProcessToDate, ticketProcessYear, ticketProcessSBU, ticketProcessTrack));
    }
  }, [processButtonData, sessionData, ticketProcessFilterType, ticketProcessMonth, ticketProcessYear, ticketProcessFromDate, ticketProcessToDate, ticketProcessSBU, ticketProcessTrack]);

  // Ticket Update data update
  useEffect(() => {
    if (ticketUpdateTrackingData.length > 0) {
      setTicketUpdateData(generateTicketUpdateData(ticketProcessFilterType, ticketProcessMonth, ticketProcessFromDate, ticketProcessToDate, ticketProcessYear, ticketProcessSBU, ticketProcessTrack));
    } else {
      setTicketUpdateData([]);
    }
  }, [ticketUpdateTrackingData, ticketProcessFilterType, ticketProcessMonth, ticketProcessYear, ticketProcessFromDate, ticketProcessToDate, ticketProcessSBU, ticketProcessTrack]);

  // Ticket Trends data update
  useEffect(() => {
    if (processButtonData.length > 0) {
      const result = generateTicketTrendsData(ticketTrendsFilterType, ticketTrendsMonth, ticketTrendsYear, ticketTrendsWeek, ticketTrendsSBU, ticketTrendsTrack);
      setTicketTrendsData(result.data || []);
      setTicketTrendsPeriods(sortTrendPeriods(result.periods || []));
    }
  }, [processButtonData, sessionData, ticketTrendsFilterType, ticketTrendsMonth, ticketTrendsYear, ticketTrendsWeek, ticketTrendsSBU, ticketTrendsTrack]);

  // Feedback Trends data update
  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      const result = generateFeedbackTrendsData(feedbackTrendsFilterType, feedbackTrendsMonth, feedbackTrendsYear, feedbackTrendsWeek, feedbackTrendsSBU, feedbackTrendsTrack);
      setFeedbackTrendsData(result.data || []);
      setFeedbackTrendsPeriods(sortTrendPeriods(result.periods || []));
    }
  }, [aiFeedbackData, feedbackTrendsFilterType, feedbackTrendsMonth, feedbackTrendsYear, feedbackTrendsWeek, feedbackTrendsSBU, feedbackTrendsTrack]);

  // Areas Trends data update
  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      const result = generateAreasTrendsData(areasTrendsFilterType, areasTrendsMonth, areasTrendsYear, areasTrendsWeek, areasTrendsSBU, areasTrendsTrack);
      setAreasTrendsData(result.data || []);
      setAreasTrendsPeriods(sortTrendPeriods(result.periods || []));
    }
  }, [aiFeedbackData, areasTrendsFilterType, areasTrendsMonth, areasTrendsYear, areasTrendsWeek, areasTrendsSBU, areasTrendsTrack]);

  // Screen Time Trends data update
  useEffect(() => {
    if (sessionData.length > 0) {
      const result = generateScreenTimeTrendsData(screenTimeTrendsFilterType, screenTimeTrendsMonth, screenTimeTrendsYear, screenTimeTrendsWeek, screenTimeTrendsSBU, screenTimeTrendsTrack);
      setScreenTimeTrendsData(result.data || []);
      setScreenTimeTrendsPeriods(sortTrendPeriods(result.periods || []));
    }
  }, [sessionData, screenTimeTrendsFilterType, screenTimeTrendsMonth, screenTimeTrendsYear, screenTimeTrendsWeek, screenTimeTrendsSBU, screenTimeTrendsTrack]);

  const formatScreenTime = (hours) => {
    if (hours >= 100) {
      return `${hours.toFixed(0)} hrs`;
    }
    if (hours >= 1) {
      return `${hours.toFixed(1)} hrs`;
    }
    const minutes = Math.round(hours * 60);
    if (minutes < 1) return `< 1 min`;
    return `${minutes} min`;
  };

  // Function to download data as Excel (CSV format)
  const downloadExcel = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to download');
      return;
    }

    // Escape a CSV value: wrap in quotes, double any internal quotes
    // For date/time values use ="value" formula so Excel treats them as text
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      // Detect date (MM/DD/YYYY) or time (HH:MM:SS AM/PM) patterns
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(str) || /^\d{2}:\d{2}:\d{2}\s[AP]M$/.test(str)) {
        return `"=""${str}"""`;
      }
      return `"${str.replace(/"/g, '""')}"`;
    };

    const headers = Object.keys(data[0]);
    const headerRow = headers.map(h => escapeCsv(h)).join(',');
    const rows = data.map(row =>
      headers.map(h => escapeCsv(row[h])).join(',')
    );
    const csv = [headerRow, ...rows].join('\r\n');

    // UTF-8 BOM so Excel opens with correct encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper to split an ISO timestamp into separate Date and Time strings
  // Prefixes with space so Excel treats them as plain text, not date/time
  const formatDateTimeParts = (isoString) => {
    if (!isoString) return { date: '', time: '' };
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return { date: '', time: '' };
    const pad = (n) => String(n).padStart(2, '0');
    const date = `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
    let hours = d.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const time = `${pad(hours)}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${ampm}`;
    return { date, time };
  };

  // Helper: filter feedback data by date/month and SBU/Track (reused across feedback downloads)
  const getFilteredFeedback = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    let filtered = aiFeedbackData;
    if (filterType === 'month' && month && year) {
      filtered = filtered.filter(f => {
        if (!f.timestamp) return false;
        const fd = new Date(f.timestamp);
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return fd.getFullYear() === parseInt(year) && fd.getMonth() === monthNames.indexOf(month);
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filtered = filtered.filter(f => {
        if (!f.timestamp) return false;
        const fd = new Date(f.timestamp);
        const from = new Date(fromDate); from.setHours(0,0,0,0);
        const to = new Date(toDate); to.setHours(23,59,59,999);
        return fd >= from && fd <= to;
      });
    }
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      const trackValues = (tracks && Array.isArray(tracks) && tracks.length > 0) ? tracks.map(t => t.value) : null;
      filtered = filtered.filter(f => {
        const mapping = getUserMapping(f.userEmail);
        if (!mapping) return false;
        if (!sbuValues.includes(mapping.sbu)) return false;
        if (trackValues && !trackValues.includes(mapping.track)) return false;
        return true;
      });
    }
    return filtered;
  };

  // Helper: filter session data by date/month and SBU/Track (reused across session downloads)
  const getFilteredSessions = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    let filtered = sessionData || [];
    if (filterType === 'month' && month && year) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return sd.getFullYear() === parseInt(year) && sd.getMonth() === monthNames.indexOf(month);
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const from = new Date(fromDate); from.setHours(0,0,0,0);
        const to = new Date(toDate); to.setHours(23,59,59,999);
        return sd >= from && sd <= to;
      });
    }
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      filtered = filtered.filter(s => sbuValues.includes(getUserMapping(s.userEmail).sbu));
    }
    if (tracks && Array.isArray(tracks) && tracks.length > 0) {
      const trackValues = tracks.map(t => t.value);
      filtered = filtered.filter(s => trackValues.includes(getUserMapping(s.userEmail).track));
    }
    return filtered;
  };

  // Helper: filter process button clicks by date/month and SBU/Track
  const getFilteredProcessClicks = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    let filtered = processButtonData || [];
    if (filterType === 'month' && month && year) {
      filtered = filtered.filter(c => {
        const cd = c.clickedAt ? new Date(c.clickedAt) : (c.timestamp ? new Date(c.timestamp) : null);
        if (!cd) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return cd.getFullYear() === parseInt(year) && cd.getMonth() === monthNames.indexOf(month);
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filtered = filtered.filter(c => {
        const cd = c.clickedAt ? new Date(c.clickedAt) : (c.timestamp ? new Date(c.timestamp) : null);
        if (!cd) return false;
        const from = new Date(fromDate); from.setHours(0,0,0,0);
        const to = new Date(toDate); to.setHours(23,59,59,999);
        return cd >= from && cd <= to;
      });
    }
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      filtered = filtered.filter(c => sbuValues.includes(getUserMapping(c.userEmail).sbu));
    }
    if (tracks && Array.isArray(tracks) && tracks.length > 0) {
      const trackValues = tracks.map(t => t.value);
      filtered = filtered.filter(c => trackValues.includes(getUserMapping(c.userEmail).track));
    }
    return filtered;
  };

  // Helper: filter ticket updates by date/month and SBU/Track
  const getFilteredTicketUpdates = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    let filtered = ticketUpdateTrackingData || [];
    if (filterType === 'month' && month && year) {
      filtered = filtered.filter(u => {
        const ud = u.updatedAt ? new Date(u.updatedAt) : (u.timestamp ? new Date(u.timestamp) : null);
        if (!ud) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return ud.getFullYear() === parseInt(year) && ud.getMonth() === monthNames.indexOf(month);
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filtered = filtered.filter(u => {
        const ud = u.updatedAt ? new Date(u.updatedAt) : (u.timestamp ? new Date(u.timestamp) : null);
        if (!ud) return false;
        const from = new Date(fromDate); from.setHours(0,0,0,0);
        const to = new Date(toDate); to.setHours(23,59,59,999);
        return ud >= from && ud <= to;
      });
    }
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      filtered = filtered.filter(u => sbuValues.includes(getUserMapping(u.userEmail).sbu));
    }
    if (tracks && Array.isArray(tracks) && tracks.length > 0) {
      const trackValues = tracks.map(t => t.value);
      filtered = filtered.filter(u => trackValues.includes(getUserMapping(u.userEmail).track));
    }
    return filtered;
  };

  // Helper: build date matcher for trends window (selected period + previous 3 periods)
  const buildTrendWindowMatcher = (filterType, month, year, week) => {
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const periodSet = new Set();

      for (let i = 0; i <= 3; i++) {
        let m = selectedMonthIdx - i;
        let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periodSet.add(`${y}-${m}`);
      }

      return (dateObj) => {
        if (!dateObj || isNaN(dateObj.getTime())) return false;
        return periodSet.has(`${dateObj.getFullYear()}-${dateObj.getMonth()}`);
      };
    }

    if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);

      const ranges = [];
      for (let i = 0; i <= 3; i++) {
        const wStart = new Date(selectedWeekStart);
        wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart);
        wEnd.setDate(wEnd.getDate() + 6);
        wEnd.setHours(23, 59, 59, 999);
        ranges.push([wStart, wEnd]);
      }

      return (dateObj) => {
        if (!dateObj || isNaN(dateObj.getTime())) return false;
        return ranges.some(([start, end]) => dateObj >= start && dateObj <= end);
      };
    }

    // Fallback: no trend window filtering
    return () => true;
  };

  // Helper: return index of trend window period for chronological sorting (0 = oldest)
  const buildTrendWindowOrderGetter = (filterType, month, year, week) => {
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const periodKeys = [];

      for (let i = 3; i >= 0; i--) {
        let m = selectedMonthIdx - i;
        let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periodKeys.push(`${y}-${m}`);
      }

      return (dateObj) => {
        if (!dateObj || isNaN(dateObj.getTime())) return Number.MAX_SAFE_INTEGER;
        const idx = periodKeys.indexOf(`${dateObj.getFullYear()}-${dateObj.getMonth()}`);
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
      };
    }

    if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);

      const ranges = [];
      for (let i = 3; i >= 0; i--) {
        const wStart = new Date(selectedWeekStart);
        wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart);
        wEnd.setDate(wEnd.getDate() + 6);
        wEnd.setHours(23, 59, 59, 999);
        ranges.push([wStart, wEnd]);
      }

      return (dateObj) => {
        if (!dateObj || isNaN(dateObj.getTime())) return Number.MAX_SAFE_INTEGER;
        const idx = ranges.findIndex(([start, end]) => dateObj >= start && dateObj <= end);
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
      };
    }

    return () => 0;
  };

  const handleDownloadPositiveFeedback = () => {
    const filtered = getFilteredFeedback(positiveFeedbackFilterType, selectedPositiveFeedbackMonth, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, selectedPositiveFeedbackYear, positiveFeedbackSBU, positiveFeedbackTrack);
    const downloadData = filtered.map(f => {
      const mapping = getUserMapping(f.userEmail);
      const dt = formatDateTimeParts(f.timestamp);
      return {
        'User Name': f.userName || (f.userEmail ? f.userEmail.split('@')[0] : ''),
        'User Email': f.userEmail || '',
        'Ticket Number': f.ticketNumber || '',
        'Feedback Type': f.feedbackType === 'thumbs_up' ? 'Satisfied' : f.feedbackType === 'thumbs_down' ? 'Not Satisfied' : (f.feedbackType || ''),
        'Reason': f.reason || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Assignment Group': f.assignmentGroup || '',
        'Date': dt.date,
        'Time': dt.time,
        'Comments': f.comments || ''
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const sbuName = Array.isArray(positiveFeedbackSBU) && positiveFeedbackSBU.length > 0 ? positiveFeedbackSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Feedback_${sbuName}_${selectedPositiveFeedbackMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadNegativeFeedback = () => {
    const filtered = getFilteredFeedback(negativeFeedbackFilterType, selectedNegativeFeedbackMonth, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, selectedNegativeFeedbackYear, negativeFeedbackSBU, negativeFeedbackTrack);
    const downloadData = filtered.filter(f => f.feedbackType === 'thumbs_down').map(f => {
      const mapping = getUserMapping(f.userEmail);
      const dt = formatDateTimeParts(f.timestamp);
      return {
        'User Name': f.userName || (f.userEmail ? f.userEmail.split('@')[0] : ''),
        'User Email': f.userEmail || '',
        'Ticket Number': f.ticketNumber || '',
        'Feedback Type': f.feedbackType || '',
        'Reason': f.reason || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Assignment Group': f.assignmentGroup || '',
        'Date': dt.date,
        'Time': dt.time,
        'Comments': f.comments || ''
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const sbuName = Array.isArray(negativeFeedbackSBU) && negativeFeedbackSBU.length > 0 ? negativeFeedbackSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Neg_Feedback_${sbuName}_${selectedNegativeFeedbackMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadLoginTrends = () => {
    const isInTrendWindow = buildTrendWindowMatcher(loginTrendsFilterType, loginTrendsMonth, loginTrendsYear, loginTrendsWeek);
    const getTrendOrder = buildTrendWindowOrderGetter(loginTrendsFilterType, loginTrendsMonth, loginTrendsYear, loginTrendsWeek);
    const filtered = getFilteredSessions('trend', null, null, null, loginTrendsYear, loginTrendsSBU, loginTrendsTrack).filter(s => {
      const d = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
      return isInTrendWindow(d);
    });
    const sortedFiltered = [...filtered].sort((a, b) => {
      const aDate = a.startTime ? new Date(a.startTime) : (a.timestamp ? new Date(a.timestamp) : null);
      const bDate = b.startTime ? new Date(b.startTime) : (b.timestamp ? new Date(b.timestamp) : null);
      const orderDiff = getTrendOrder(aDate) - getTrendOrder(bDate);
      if (orderDiff !== 0) return orderDiff;
      return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
    });

    const downloadData = sortedFiltered.map(s => {
      const mapping = getUserMapping(s.userEmail);
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });

    const sbuName = Array.isArray(loginTrendsSBU) && loginTrendsSBU.length > 0 ? loginTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Login_Trends_${sbuName}_4PeriodWindow_${loginTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadTicketTrends = () => {
    const isInTrendWindow = buildTrendWindowMatcher(ticketTrendsFilterType, ticketTrendsMonth, ticketTrendsYear, ticketTrendsWeek);
    const getTrendOrder = buildTrendWindowOrderGetter(ticketTrendsFilterType, ticketTrendsMonth, ticketTrendsYear, ticketTrendsWeek);
    const filtered = getFilteredProcessClicks('trend', null, null, null, ticketTrendsYear, ticketTrendsSBU, ticketTrendsTrack).filter(c => {
      const d = c.clickedAt ? new Date(c.clickedAt) : (c.timestamp ? new Date(c.timestamp) : null);
      return isInTrendWindow(d);
    });
    const sortedFiltered = [...filtered].sort((a, b) => {
      const aDate = a.clickedAt ? new Date(a.clickedAt) : (a.timestamp ? new Date(a.timestamp) : null);
      const bDate = b.clickedAt ? new Date(b.clickedAt) : (b.timestamp ? new Date(b.timestamp) : null);
      const orderDiff = getTrendOrder(aDate) - getTrendOrder(bDate);
      if (orderDiff !== 0) return orderDiff;
      return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
    });

    const downloadData = sortedFiltered.map(c => {
      const mapping = getUserMapping(c.userEmail);
      const dt = formatDateTimeParts(c.clickedAt || c.timestamp);
      return {
        'User Name': c.userName || (c.userEmail ? c.userEmail.split('@')[0] : ''),
        'User Email': c.userEmail || '',
        'Ticket Number': c.ticketNumber || '',
        'Short Description': c.shortDescription || '',
        'Assignment Group': c.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Clicked Date': dt.date,
        'Clicked Time': dt.time,
        'Priority': c.priority || ''
      };
    });

    const sbuName = Array.isArray(ticketTrendsSBU) && ticketTrendsSBU.length > 0 ? ticketTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Ticket_Trends_${sbuName}_4PeriodWindow_${ticketTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadFeedbackTrends = () => {
    const isInTrendWindow = buildTrendWindowMatcher(feedbackTrendsFilterType, feedbackTrendsMonth, feedbackTrendsYear, feedbackTrendsWeek);
    const getTrendOrder = buildTrendWindowOrderGetter(feedbackTrendsFilterType, feedbackTrendsMonth, feedbackTrendsYear, feedbackTrendsWeek);
    const filtered = getFilteredFeedback('trend', null, null, null, feedbackTrendsYear, feedbackTrendsSBU, feedbackTrendsTrack).filter(f => {
      const d = f.timestamp ? new Date(f.timestamp) : null;
      return isInTrendWindow(d);
    });
    const sortedFiltered = [...filtered].sort((a, b) => {
      const aDate = a.timestamp ? new Date(a.timestamp) : null;
      const bDate = b.timestamp ? new Date(b.timestamp) : null;
      const orderDiff = getTrendOrder(aDate) - getTrendOrder(bDate);
      if (orderDiff !== 0) return orderDiff;
      return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
    });

    const downloadData = sortedFiltered.map(f => {
      const mapping = getUserMapping(f.userEmail);
      const dt = formatDateTimeParts(f.timestamp);
      return {
        'User Name': f.userName || (f.userEmail ? f.userEmail.split('@')[0] : ''),
        'User Email': f.userEmail || '',
        'Ticket Number': f.ticketNumber || '',
        'Feedback Type': f.feedbackType || '',
        'Reason': f.reason || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Assignment Group': f.assignmentGroup || '',
        'Date': dt.date,
        'Time': dt.time,
        'Comments': f.comments || ''
      };
    });

    const sbuName = Array.isArray(feedbackTrendsSBU) && feedbackTrendsSBU.length > 0 ? feedbackTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `FB_Trends_${sbuName}_4PeriodWindow_${feedbackTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadAreasTrends = () => {
    const isInTrendWindow = buildTrendWindowMatcher(areasTrendsFilterType, areasTrendsMonth, areasTrendsYear, areasTrendsWeek);
    const getTrendOrder = buildTrendWindowOrderGetter(areasTrendsFilterType, areasTrendsMonth, areasTrendsYear, areasTrendsWeek);
    const filtered = getFilteredFeedback('trend', null, null, null, areasTrendsYear, areasTrendsSBU, areasTrendsTrack).filter(f => {
      const d = f.timestamp ? new Date(f.timestamp) : null;
      return isInTrendWindow(d);
    });
    const sortedFiltered = [...filtered].sort((a, b) => {
      const aDate = a.timestamp ? new Date(a.timestamp) : null;
      const bDate = b.timestamp ? new Date(b.timestamp) : null;
      const orderDiff = getTrendOrder(aDate) - getTrendOrder(bDate);
      if (orderDiff !== 0) return orderDiff;
      return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
    });

    const downloadData = sortedFiltered.filter(f => f.feedbackType === 'thumbs_down' && f.reason).map(f => {
      const mapping = getUserMapping(f.userEmail);
      const dt = formatDateTimeParts(f.timestamp);
      return {
        'User Name': f.userName || (f.userEmail ? f.userEmail.split('@')[0] : ''),
        'User Email': f.userEmail || '',
        'Ticket Number': f.ticketNumber || '',
        'Reason': f.reason || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Assignment Group': f.assignmentGroup || '',
        'Date': dt.date,
        'Time': dt.time,
        'Comments': f.comments || ''
      };
    });

    const sbuName = Array.isArray(areasTrendsSBU) && areasTrendsSBU.length > 0 ? areasTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Areas_Trends_${sbuName}_4PeriodWindow_${areasTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadScreenTimeTrends = () => {
    const isInTrendWindow = buildTrendWindowMatcher(screenTimeTrendsFilterType, screenTimeTrendsMonth, screenTimeTrendsYear, screenTimeTrendsWeek);
    const getTrendOrder = buildTrendWindowOrderGetter(screenTimeTrendsFilterType, screenTimeTrendsMonth, screenTimeTrendsYear, screenTimeTrendsWeek);
    const filtered = getFilteredSessions('trend', null, null, null, screenTimeTrendsYear, screenTimeTrendsSBU, screenTimeTrendsTrack).filter(s => {
      const d = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
      return isInTrendWindow(d);
    });
    const sortedFiltered = [...filtered].sort((a, b) => {
      const aDate = a.startTime ? new Date(a.startTime) : (a.timestamp ? new Date(a.timestamp) : null);
      const bDate = b.startTime ? new Date(b.startTime) : (b.timestamp ? new Date(b.timestamp) : null);
      const orderDiff = getTrendOrder(aDate) - getTrendOrder(bDate);
      if (orderDiff !== 0) return orderDiff;
      return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
    });

    const downloadData = sortedFiltered.map(s => {
      const mapping = getUserMapping(s.userEmail);
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });

    const sbuName = Array.isArray(screenTimeTrendsSBU) && screenTimeTrendsSBU.length > 0 ? screenTimeTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `ScreenTime_Trends_${sbuName}_4PeriodWindow_${screenTimeTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadGroupData = () => {
    const isAllGroups = Array.isArray(selectedGroup) && selectedGroup.some(g => g.value === 'all');
    const selectedGroups = (Array.isArray(selectedGroup) ? selectedGroup : []).filter(g => g.value !== 'all');
    let filtered = sessionData || [];
    if (!isAllGroups && selectedGroups.length > 0) {
      filtered = filtered.filter(s => selectedGroups.some(g => (s.assignmentGroup || '') === g.value));
    }
    if (groupFilterType === 'month' && selectedGroupMonth && selectedGroupYear) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return sd.getFullYear() === parseInt(selectedGroupYear) && sd.getMonth() === monthNames.indexOf(selectedGroupMonth);
      });
    } else if (groupFilterType === 'date' && selectedGroupFromDate && selectedGroupToDate) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const from = new Date(selectedGroupFromDate); from.setHours(0,0,0,0);
        const to = new Date(selectedGroupToDate); to.setHours(23,59,59,999);
        return sd >= from && sd <= to;
      });
    }
    const downloadData = filtered.map(s => {
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    // Sort descending: group by Assignment Group, largest group first
    const grpCounts = {};
    downloadData.forEach(r => { grpCounts[r['Assignment Group']] = (grpCounts[r['Assignment Group']] || 0) + 1; });
    downloadData.sort((a, b) => (grpCounts[b['Assignment Group']] || 0) - (grpCounts[a['Assignment Group']] || 0));
    const groupName = isAllGroups ? 'All_Groups' : (Array.isArray(selectedGroup) ? selectedGroup.map(g => g.label).join('_') : selectedGroup?.label || 'Group');
    const filename = `Group_${groupName}_${selectedGroupMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadTeamData = () => {
    let filtered = sessionData || [];
    if (teamFilterType === 'month' && selectedTeamMonth && selectedTeamYear) {
      filtered = filtered.filter(s => {
        const sd = new Date(s.startTime || s.timestamp);
        if (!s.startTime && !s.timestamp) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return sd.getFullYear() === parseInt(selectedTeamYear) && sd.getMonth() === monthNames.indexOf(selectedTeamMonth);
      });
    } else if (teamFilterType === 'date' && selectedTeamFromDate && selectedTeamToDate) {
      filtered = filtered.filter(s => {
        const sd = new Date(s.startTime || s.timestamp);
        if (!s.startTime && !s.timestamp) return false;
        const from = new Date(selectedTeamFromDate); from.setHours(0,0,0,0);
        const to = new Date(selectedTeamToDate); to.setHours(23,59,59,999);
        return sd >= from && sd <= to;
      });
    }
    const downloadData = filtered.map(s => {
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    // Sort descending: group by Assignment Group, largest group first
    const grpCounts = {};
    downloadData.forEach(r => { grpCounts[r['Assignment Group']] = (grpCounts[r['Assignment Group']] || 0) + 1; });
    downloadData.sort((a, b) => (grpCounts[b['Assignment Group']] || 0) - (grpCounts[a['Assignment Group']] || 0));
    const filename = `Team_Time_${selectedTeamMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadAllTeamsData = () => {
    let filtered = sessionData || [];
    if (allTeamsFilterType === 'month' && selectedAllTeamsMonth && selectedAllTeamsYear) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return sd.getFullYear() === parseInt(selectedAllTeamsYear) && sd.getMonth() === monthNames.indexOf(selectedAllTeamsMonth);
      });
    } else if (allTeamsFilterType === 'date' && selectedAllTeamsFromDate && selectedAllTeamsToDate) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const from = new Date(selectedAllTeamsFromDate); from.setHours(0,0,0,0);
        const to = new Date(selectedAllTeamsToDate); to.setHours(23,59,59,999);
        return sd >= from && sd <= to;
      });
    }
    if (Array.isArray(selectedSBU) && selectedSBU.length > 0) {
      const sbuVals = selectedSBU.map(s => s.value);
      filtered = filtered.filter(s => sbuVals.includes(getGroupMapping(s.assignmentGroup).sbu));
    }
    if (Array.isArray(selectedTrack) && selectedTrack.length > 0) {
      const trackVals = selectedTrack.map(t => t.value);
      filtered = filtered.filter(s => trackVals.includes(getGroupMapping(s.assignmentGroup).track));
    }
    const downloadData = filtered.map(s => {
      const mapping = getGroupMapping(s.assignmentGroup);
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const trackName = Array.isArray(selectedTrack) && selectedTrack.length > 0 ? selectedTrack.map(t => t.label).join('_') : 'All_Tracks';
    const sbuName = Array.isArray(selectedSBU) && selectedSBU.length > 0 ? selectedSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Track_Time_${sbuName}_${trackName}_${selectedAllTeamsMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadLoginCount = () => {
    const filtered = getFilteredSessions(loginCountFilterType, loginCountMonth, loginCountFromDate, loginCountToDate, loginCountYear, loginCountSBU, loginCountTrack);
    const downloadData = filtered.map(s => {
      const mapping = getUserMapping(s.userEmail);
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const filename = `Login_Count_${loginCountMonth}_${loginCountYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadTicketProcess = () => {
    const filtered = getFilteredProcessClicks(ticketProcessFilterType, ticketProcessMonth, ticketProcessFromDate, ticketProcessToDate, ticketProcessYear, ticketProcessSBU, ticketProcessTrack);
    if (!filtered || filtered.length === 0) return;
    const downloadData = filtered.map(c => {
      const mapping = getUserMapping(c.userEmail);
      const dt = formatDateTimeParts(c.clickedAt || c.timestamp);
      return {
        'User Name': c.userName || (c.userEmail ? c.userEmail.split('@')[0] : ''),
        'User Email': c.userEmail || '',
        'Ticket Number': c.ticketNumber || '',
        'Short Description': c.shortDescription || '',
        'Assignment Group': c.assignmentGroup || '',
        'Assigned To': c.assignedTo || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Clicked Date': dt.date,
        'Clicked Time': dt.time,
        'Priority': c.priority || '',
        'Category': c.category || ''
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const filename = `Ticket_Process_${ticketProcessMonth}_${ticketProcessYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadTicketUpdates = () => {
    const filtered = getFilteredTicketUpdates(ticketProcessFilterType, ticketProcessMonth, ticketProcessFromDate, ticketProcessToDate, ticketProcessYear, ticketProcessSBU, ticketProcessTrack);
    if (!filtered || filtered.length === 0) return;
    const downloadData = filtered.map(u => {
      const mapping = getUserMapping(u.userEmail);
      const dt = formatDateTimeParts(u.updatedAt || u.timestamp);
      return {
        'User Name': u.userName || (u.userEmail ? u.userEmail.split('@')[0] : ''),
        'User Email': u.userEmail || '',
        'Ticket Number': u.ticketNumber || '',
        'Assignment Group': u.assignmentGroup || '',
        'Requested State': u.requestedStateLabel || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Updated Date': dt.date,
        'Updated Time': dt.time
      };
    });
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const filename = `Ticket_Updates_${ticketProcessMonth}_${ticketProcessYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadSdmAdoption = () => {
    if (!sdmSummaryData || sdmSummaryData.length === 0) return;
    const downloadData = sdmSummaryData.map((row) => ({
      'SDM': row.sdm || '',
      'Tool Login %': Number(row.toolLoginPercent || 0),
      'Recommendation Generated %': Number(row.recommendationGeneratedPercent || 0),
      'Feedback %': Number(row.feedbackPercent || 0)
    }));
    downloadData.push({
      'SDM': sdmTotalToolUsageRow.sdm,
      'Tool Login %': Number(sdmTotalToolUsageRow.toolLoginPercent || 0),
      'Recommendation Generated %': Number(sdmTotalToolUsageRow.recommendationGeneratedPercent || 0),
      'Feedback %': Number(sdmTotalToolUsageRow.feedbackPercent || 0)
    });
    const filename = `SDM_Adoption_Percentages_${globalMonth}_${globalYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadSdmTrends = () => {
    if (!sdmTrendsChartData || sdmTrendsChartData.length === 0 || !sdmTrendsPeriods || sdmTrendsPeriods.length === 0) return;

    const orderedPeriods = [...sdmTrendsPeriods].reverse();
    const downloadData = sdmTrendsChartData.map((row) => {
      const out = { 'SDM': row.name || '' };
      orderedPeriods.forEach((period) => {
        out[`${period.label} Login %`] = Number(row[`${period.label} Login %`] || 0).toFixed(2);
        out[`${period.label} Ticket %`] = Number(row[`${period.label} Ticket %`] || 0).toFixed(2);
        out[`${period.label} Feedback %`] = Number(row[`${period.label} Feedback %`] || 0).toFixed(2);
      });
      return out;
    });

    const filename = `SDM_Trends_${globalMonth}_${globalYear}`;
    downloadExcel(downloadData, filename);
  };

  const formatTrendPeriodLabel = (value) => {
    const cleanLabel = String(value || '')
      .replace(/^\d+\|/, '')
      .replace(' 👍', '')
      .replace(' 👎', '');
    const monthOnlyMatch = cleanLabel.match(/^([A-Za-z]{3})\s+\d{4}$/);
    return monthOnlyMatch ? monthOnlyMatch[1] : cleanLabel;
  };

  const overallFeedbackTrendBasePeriods = (() => {
    const orderedBasePeriods = [];
    const periodSource = (feedbackTrendsPeriods && feedbackTrendsPeriods.length > 0)
      ? feedbackTrendsPeriods
      : (feedbackTrendsData[0]
        ? Object.keys(feedbackTrendsData[0]).filter((key) => key.endsWith(' 👍') || key.endsWith(' 👎'))
        : []);

    periodSource.forEach((periodKey) => {
      const baseKey = periodKey.replace(' 👍', '').replace(' 👎', '');
      if (!orderedBasePeriods.includes(baseKey)) {
        orderedBasePeriods.push(baseKey);
      }
    });

    return orderedBasePeriods;
  })();

  return (
    <div className="analytics-wrapper">
      <NavBar userName={userName} userEmail={userEmail} onLogout={onLogout} canViewUserAnalytics={canViewUserAnalytics} />
      
      <div className="screen-time-page">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">
              <span className="card-title-icon">
                <FaClock className="header-icon-purple" />
                <span>User Analytics</span>
              </span>
            </h1>
            <p className="dashboard-subtitle">Monitor user engagement and ticket resolution time (Real-time Data)</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button onClick={handleSyncData} className="sync-data-btn" disabled={loading}>
              {loading ? '⏳ Syncing...' : '🔄 Sync Data'}
            </button>
            <Link to="/AgentAssist" className="analytics-link">
              Home
            </Link>
          </div>
        </div>

        <div className="global-filters-panel">
          <div className="global-filters-row">
            <div className="global-filter-field">
              <label>SBU</label>
              <select value={globalSBUValue} onChange={(e) => { setGlobalSBUValue(e.target.value); setGlobalTrackValue('all'); }}>
                <option value="all">All SBU</option>
                {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => (
                  <option key={sbu.value} value={sbu.value}>{sbu.label}</option>
                ))}
              </select>
            </div>

            <div className="global-filter-field">
              <label>Track</label>
              <select value={globalTrackValue} onChange={(e) => setGlobalTrackValue(e.target.value)}>
                <option value="all">All Tracks</option>
                {globalTrackOptions.filter(t => t.value !== 'all').map((track) => (
                  <option key={track.value} value={track.value}>{track.label}</option>
                ))}
              </select>
            </div>

            <div className="global-filter-field">
              <label>Period</label>
              <div className="global-filter-type-group">
                <button
                  className={globalFilterType === 'month' ? 'active' : ''}
                  onClick={() => setGlobalFilterType('month')}
                >
                  Month
                </button>
                <button
                  className={globalFilterType === 'date' ? 'active' : ''}
                  onClick={() => setGlobalFilterType('date')}
                >
                  Date
                </button>
                <button
                  className={globalFilterType === 'week' ? 'active' : ''}
                  onClick={() => setGlobalFilterType('week')}
                >
                  Week
                </button>
              </div>
            </div>

            {(globalFilterType === 'month' || globalFilterType === 'week') && (
              <MonthSelector
                year={globalYear}
                month={globalMonth}
                onYearChange={(y) => setGlobalYear(String(y))}
                onMonthChange={setGlobalMonth}
                label={globalFilterType === 'week' ? 'Week Base Month:' : 'Month:'}
              />
            )}

            {globalFilterType === 'date' && (
              <DateRangeSelector
                fromDate={globalFromDate}
                toDate={globalToDate}
                onFromDateChange={setGlobalFromDate}
                onToDateChange={setGlobalToDate}
              />
            )}

            {globalFilterType === 'week' && (
              <div className="global-filter-field">
                <label>Week</label>
                <select value={globalWeek} onChange={(e) => setGlobalWeek(e.target.value)}>
                  <option value="1">Week 1</option>
                  <option value="2">Week 2</option>
                  <option value="3">Week 3</option>
                  <option value="4">Week 4</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-title">Loading screen time data...</div>
            <div className="loading-subtitle">Please wait while we fetch the latest information</div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-container">
            <strong>Error:</strong> {error?.message || error}
          </div>
        )}

        {/* Data Display */}
        {!loading && (
          <>
        {/* Trends of SDM Analytics - Full Width */}
        <div className="chart-card" style={{ marginTop: '6px', gridColumn: '1 / -1', width: '100%' }}>
          <div className="chart-header">
            <UnifiedDashboardHeader
              icon="📉"
              title="Trends of SDM Analytics"
              subtitle="SDM-wise Login %, Ticket %, and Feedback % across 4 periods"
              showTotalBadge={false}
            />
          </div>

          <div style={{ margin: '0 8px 8px', padding: '8px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '11px', color: '#374151', whiteSpace: 'nowrap' }}>
              <input
                type="radio"
                name="sdmTrendsMetric"
                value="login"
                checked={sdmTrendsSelectedMetric === 'login'}
                onChange={() => setSdmTrendsSelectedMetric('login')}
                style={{ accentColor: getMetricBorderColor('login') }}
              />
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Login %</span>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '11px', color: '#374151', whiteSpace: 'nowrap' }}>
              <input
                type="radio"
                name="sdmTrendsMetric"
                value="ticket"
                checked={sdmTrendsSelectedMetric === 'ticket'}
                onChange={() => setSdmTrendsSelectedMetric('ticket')}
                style={{ accentColor: getMetricBorderColor('ticket') }}
              />
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Ticket %</span>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '11px', color: '#374151', whiteSpace: 'nowrap' }}>
              <input
                type="radio"
                name="sdmTrendsMetric"
                value="feedback"
                checked={sdmTrendsSelectedMetric === 'feedback'}
                onChange={() => setSdmTrendsSelectedMetric('feedback')}
                style={{ accentColor: getMetricBorderColor('feedback') }}
              />
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Feedback %</span>
            </label>
          </div>

          </div>

          <div style={{ margin: '0 8px 8px', padding: '8px 8px 4px', borderRadius: '8px', background: '#ffffff', border: '1px solid #e2e8f0', opacity: sdmTrendsChartOpacity, transition: 'opacity 0.18s ease' }}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={sdmTrendsChartData}
              margin={{ top: 16, right: 8, bottom: 30, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="name"
                stroke="#4a5568"
                interval={0}
                height={32}
                tick={({ x, y, payload }) => {
                  const label = payload.value || '';
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text x={0} y={0} dy={10} textAnchor="middle" fill="#334155" fontSize={9} fontWeight={600}>
                        {label}
                      </text>
                    </g>
                  );
                }}
              />
              <YAxis
                stroke="#4a5568"
                style={{ fontSize: '10px' }}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                width={36}
              />
              <Tooltip
                shared={false}
                contentStyle={{ background: 'rgba(255,255,255,0.98)', border: '1px solid #dbe4ff', borderRadius: '12px', boxShadow: '0 14px 30px rgba(15,23,42,0.14)' }}
                formatter={(value, name) => [`${Number(value || 0).toFixed(2)}%`, name]}
                labelFormatter={(label) => `SDM: ${label}`}
              />
                {sdmTrendsDisplayMetric === 'login' && [...(sdmTrendsPeriods || [])].reverse().map((period, idx) => (
                  <Bar key={`${period.key}-login`} dataKey={`${period.label} Login %`} name={`Login % - ${period.label}`} fill="#34d399" barSize={12} radius={[6, 6, 0, 0]} isAnimationActive={false}>
                    {sdmTrendsChartData.map((row, rowIndex) => (
                      <Cell key={`${period.key}-login-cell-${rowIndex}`} fill={getGrowthFillColor(row[`${period.label} Login %`])} />
                    ))}
                    <LabelList dataKey={`${period.label} Login %`} position="top" style={{ fill: '#1d4ed8', fontWeight: 700, fontSize: '9px' }} formatter={(value) => Number(value || 0) > 0 ? `${Number(value).toFixed(1)}%` : ''} />
                  </Bar>
              ))}
                {sdmTrendsDisplayMetric === 'ticket' && [...(sdmTrendsPeriods || [])].reverse().map((period, idx) => (
                  <Bar key={`${period.key}-ticket`} dataKey={`${period.label} Ticket %`} name={`Ticket % - ${period.label}`} fill="#34d399" barSize={12} radius={[6, 6, 0, 0]} isAnimationActive={false}>
                    {sdmTrendsChartData.map((row, rowIndex) => (
                      <Cell key={`${period.key}-ticket-cell-${rowIndex}`} fill={getGrowthFillColor(row[`${period.label} Ticket %`])} />
                    ))}
                    <LabelList dataKey={`${period.label} Ticket %`} position="top" style={{ fill: '#7e22ce', fontWeight: 700, fontSize: '9px' }} formatter={(value) => Number(value || 0) > 0 ? `${Number(value).toFixed(1)}%` : ''} />
                  </Bar>
              ))}
                {sdmTrendsDisplayMetric === 'feedback' && [...(sdmTrendsPeriods || [])].reverse().map((period, idx) => (
                  <Bar key={`${period.key}-feedback`} dataKey={`${period.label} Feedback %`} name={`Feedback % - ${period.label}`} fill="#34d399" barSize={12} radius={[6, 6, 0, 0]} isAnimationActive={false}>
                    {sdmTrendsChartData.map((row, rowIndex) => (
                      <Cell key={`${period.key}-feedback-cell-${rowIndex}`} fill={getGrowthFillColor(row[`${period.label} Feedback %`])} />
                    ))}
                    <LabelList dataKey={`${period.label} Feedback %`} position="top" style={{ fill: '#166534', fontWeight: 700, fontSize: '9px' }} formatter={(value) => Number(value || 0) > 0 ? `${Number(value).toFixed(1)}%` : ''} />
                  </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          </div>

          <div className="download-button-container" style={{ position: 'absolute', right: '8px', bottom: '8px', marginTop: 0 }}>
            <button onClick={handleDownloadSdmTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        <div className="charts-grid" style={{ marginTop: '10px' }}>
          {/* SDM/Track Analytics Percentage Table */}
          <div className="chart-card" style={{ position: 'relative', paddingBottom: '46px' }}>
            <div className="chart-header">
              <UnifiedDashboardHeader
                icon="📊"
                title="SDM Analytics - Adoption Percentages"
                subtitle="SDM-wise adoption summary"
                showTotalBadge={false}
              />
            </div>

            {/* SDM Analytics Table */}
            <div style={{ overflowX: 'hidden', borderRadius: '6px', border: '1px solid #e5e7eb'  }}>
            <table style={{
              width: '100%',
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              fontSize: '10px',
              backgroundColor: 'white'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #d1d5db', width: '40%', whiteSpace: 'normal', lineHeight: 1.2 }}>SDM</th>
                  <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: '600', color: '#4f46e5', borderBottom: '1px solid #d1d5db', width: '20%', whiteSpace: 'normal', lineHeight: 1.2 }}>Tool Login %</th>
                  <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: '600', color: '#7c3aed', borderBottom: '1px solid #d1d5db', width: '20%', whiteSpace: 'normal', lineHeight: 1.2 }}>Recommendation Generated %</th>
                  <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: '600', color: '#059669', borderBottom: '1px solid #d1d5db', width: '20%', whiteSpace: 'normal', lineHeight: 1.2 }}>Feedback %</th>
                </tr>
              </thead>
              <tbody>
                {sdmSummaryData && sdmSummaryData.length > 0 ? (
                  (() => {
                    const getPercentColor = (percent) => {
                      const p = parseFloat(percent);
                      if (p >= 80) return '#10b981';
                      if (p >= 50) return '#f59e0b';
                      return '#ef4444';
                    };

                    return (
                      <>
                        {sdmSummaryData.map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                            <td style={{ padding: '4px 6px', textAlign: 'left', color: '#111827', fontWeight: '500', lineHeight: 1.2, wordBreak: 'break-word' }}>{row.sdm || '-'}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                backgroundColor: getPercentColor(row.toolLoginPercent) + '20',
                                color: getPercentColor(row.toolLoginPercent),
                                fontWeight: '600',
                                fontSize: '10px'
                              }}>
                                {row.toolLoginPercent}%
                              </span>
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                backgroundColor: getPercentColor(row.recommendationGeneratedPercent) + '20',
                                color: getPercentColor(row.recommendationGeneratedPercent),
                                fontWeight: '600',
                                fontSize: '10px'
                              }}>
                                {row.recommendationGeneratedPercent}%
                              </span>
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                backgroundColor: getPercentColor(row.feedbackPercent) + '20',
                                color: getPercentColor(row.feedbackPercent),
                                fontWeight: '600',
                                fontSize: '10px'
                              }}>
                                {row.feedbackPercent}%
                              </span>
                            </td>
                          </tr>
                        ))}

                        <tr style={{ borderTop: '2px solid #d1d5db', borderBottom: '1px solid #d1d5db', backgroundColor: '#eef2ff' }}>
                          <td style={{ padding: '4px 6px', textAlign: 'left', color: '#1f2937', fontWeight: '700', lineHeight: 1.2, wordBreak: 'break-word' }}>{sdmTotalToolUsageRow.sdm}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              backgroundColor: getPercentColor(sdmTotalToolUsageRow.toolLoginPercent) + '20',
                              color: getPercentColor(sdmTotalToolUsageRow.toolLoginPercent),
                              fontWeight: '700',
                              fontSize: '10px'
                            }}>
                              {sdmTotalToolUsageRow.toolLoginPercent}%
                            </span>
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              backgroundColor: getPercentColor(sdmTotalToolUsageRow.recommendationGeneratedPercent) + '20',
                              color: getPercentColor(sdmTotalToolUsageRow.recommendationGeneratedPercent),
                              fontWeight: '700',
                              fontSize: '10px'
                            }}>
                              {sdmTotalToolUsageRow.recommendationGeneratedPercent}%
                            </span>
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              backgroundColor: getPercentColor(sdmTotalToolUsageRow.feedbackPercent) + '20',
                              color: getPercentColor(sdmTotalToolUsageRow.feedbackPercent),
                              fontWeight: '700',
                              fontSize: '10px'
                            }}>
                              {sdmTotalToolUsageRow.feedbackPercent}%
                            </span>
                          </td>
                        </tr>
                      </>
                    );
                  })()
                ) : (
                  <tr>
                    <td colSpan="4" style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>
                      {sdmLoading ? '🔄 Loading SDM analytics...' : 'No data available for the selected date range or SDM mapping source'}
                      {!sdmLoading && sdmDebug ? (
                        <div style={{ marginTop: '10px', color: '#6b7280', fontSize: '11px', lineHeight: 1.6 }}>
                          <div>SDM mappings: {sdmDebug.sdmMappingCount ?? 0}</div>
                          <div>Sessions: {sdmDebug.filteredSessions ?? 0}</div>
                          <div>Process clicks: {sdmDebug.filteredProcessClicks ?? 0}</div>
                          <div>Feedback: {sdmDebug.filteredFeedback ?? 0}</div>
                          <div>Matched active users: {sdmDebug.matchedActiveUsers ?? 0}</div>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            <div className="download-button-container" style={{ position: 'absolute', right: '12px', bottom: '10px', marginTop: 0 }}>
              <button onClick={handleDownloadSdmAdoption} className="download-btn">📥 Download</button>
            </div>
          </div>

          {/* ─── Agent Assist Login Count Dashboard ─── */}
          <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '0',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(99,102,241,0.08)',
          border: '1px solid #e5e7eb',
          borderTop: '2px solid rgba(99,102,241,0.5)',
          overflow: 'hidden',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(99,102,241,0.20)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(99,102,241,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >

          <div style={{ padding: '10px 12px 0 12px' }}>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                {/* Icon pill */}
                <div style={{
                  width: '34px', height: '34px', borderRadius: '8px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 3px 10px rgba(99,102,241,0.35)', fontSize: '16px'
                }}>🔐</div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>Agent Assist Login Count</h3>
                  <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#6b7280', fontWeight: 400, lineHeight: 1.4 }}>
                    {loginCountTrack?.length > 0 ? 'Top 20 users by login count for selected track(s)' : loginCountSBU?.length > 0 ? 'Top 20 tracks by login count — select a Track to see users' : 'Select SBU → Track to see how many times each user logged in'}
                  </p>
                </div>
              </div>

              {/* Total badge */}
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
                border: '1.5px solid #a5b4fc', borderRadius: '8px',
                padding: '4px 8px', textAlign: 'center', flexShrink: 0,
                boxShadow: '0 2px 8px rgba(99,102,241,0.12)'
              }}>
                <span style={{ fontSize: '16px', fontWeight: 800, color: '#3730a3', lineHeight: 1, letterSpacing: '-0.4px' }}>
                  {loginCountData.reduce((sum, d) => sum + (d.loginCount || 0), 0)}
                </span>
                <span style={{ fontSize: '8px', fontWeight: 600, color: '#4f46e5', marginTop: '1px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Logins</span>
              </div>
            </div>

          </div>

          {/* Chart */}
          <div style={{ padding: '2px 6px 0 6px' }}>
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={loginCountData} margin={{ top: 22, right: 12, bottom: 2, left: 4 }}>
                <defs>
                  <linearGradient id="loginCountGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#818cf8" />
                    <stop offset="100%" stopColor="#4f46e5" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="name" stroke="#9ca3af" axisLine={{ stroke: '#9ca3af', strokeWidth: 1 }} tickLine={{ stroke: '#9ca3af' }} interval={0} height={36}
                  tick={({ x, y, payload, index }) => {
                    if (index % 2 !== 0) return <g />;
                    const raw = payload.value || '';
                    const label = raw.length > 12 ? raw.substring(0, 11) + '…' : raw;
                    return (<g transform={`translate(${x},${y})`}><text x={0} y={0} dy={12} textAnchor="middle" fill="#9ca3af" fontSize={9} fontWeight={500}>{label}</text></g>);
                  }}
                />
                <YAxis stroke="#9ca3af" axisLine={{ stroke: '#9ca3af', strokeWidth: 1 }} tickLine={{ stroke: '#9ca3af' }} style={{ fontSize: '11px' }} tick={{ fill: '#9ca3af' }} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1.5px solid #f3f4f6', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '10px 14px' }}
                  cursor={{ fill: 'rgba(99,102,241,0.06)' }}
                  formatter={(value) => [<span style={{ fontWeight: 700, color: '#3730a3' }}>{value}</span>, 'Login Count']}
                  labelFormatter={(label) => {
                    const item = loginCountData.find(d => d.name === label);
                    return <span style={{ fontWeight: 700, color: '#111827', fontSize: '11px' }}>{item?.isTrackLevel ? `Track: ${label}` : item ? `${label} (${item.track})` : label}</span>;
                  }}
                />
                <Bar dataKey="loginCount" fill="url(#loginCountGradient)" barSize={12} radius={[5, 5, 0, 0]}>
                  <LabelList dataKey="loginCount" position="top" style={{ fill: '#3730a3', fontWeight: 700, fontSize: '10px' }} formatter={v => v > 0 ? v : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Footer */}
          <div style={{ padding: '6px 12px 10px', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleDownloadLoginCount} className="download-btn">📥 Download</button>
          </div>
        </div>
        </div>

        <div className="charts-grid" style={{ marginTop: '10px' }}>
        {/* ─── Ticket Process Dashboard ─── */}
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '0',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(99,102,241,0.08)',
          border: '1px solid #e5e7eb',
          borderTop: '2px solid rgba(99,102,241,0.5)',
          overflow: 'hidden',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          position: 'relative',
          paddingBottom: '40px',
        }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(99,102,241,0.20)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(99,102,241,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <div style={{ padding: '10px 12px 0 12px' }}>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                {/* Icon pill */}
                <div style={{
                  width: '34px', height: '34px', borderRadius: '8px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 3px 10px rgba(99,102,241,0.35)', fontSize: '16px'
                }}>🎫</div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>Ticket Process</h3>
                  <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#6b7280', fontWeight: 400, lineHeight: 1.4 }}>
                    {ticketProcessTrack?.length > 0 ? 'Top 20 users by process button clicks for selected track(s)' : ticketProcessSBU?.length > 0 ? 'Top 20 tracks by process button clicks — select a Track to see users' : 'Select SBU → Track to see who asked for AI recommendation'}
                  </p>
                </div>
              </div>

              {/* Total badge */}
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
                border: '1.5px solid #a5b4fc', borderRadius: '8px',
                padding: '4px 8px', textAlign: 'center', flexShrink: 0,
                boxShadow: '0 2px 8px rgba(99,102,241,0.12)'
              }}>
                <span style={{ fontSize: '16px', fontWeight: 800, color: '#3730a3', lineHeight: 1, letterSpacing: '-0.4px' }}>
                  {ticketProcessData.reduce((sum, d) => sum + (d.processCount || 0), 0)}
                </span>
                <span style={{ fontSize: '8px', fontWeight: 600, color: '#4f46e5', marginTop: '1px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Processes</span>
              </div>
            </div>

          </div>

          {/* Chart */}
          <div style={{ padding: '2px 6px 0 6px' }}>
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={ticketProcessData} margin={{ top: 22, right: 12, bottom: 2, left: 4 }}>
                <defs>
                  <linearGradient id="ticketProcessGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#818cf8" />
                    <stop offset="100%" stopColor="#4f46e5" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="name" stroke="#9ca3af" axisLine={{ stroke: '#9ca3af', strokeWidth: 1 }} tickLine={{ stroke: '#9ca3af' }} interval={0} height={36}
                  tick={({ x, y, payload, index }) => {
                    if (index % 2 !== 0) return <g />;
                    const raw = payload.value || '';
                    const label = raw.length > 12 ? raw.substring(0, 11) + '…' : raw;
                    return (<g transform={`translate(${x},${y})`}><text x={0} y={0} dy={12} textAnchor="middle" fill="#9ca3af" fontSize={9} fontWeight={500}>{label}</text></g>);
                  }}
                />
                <YAxis stroke="#9ca3af" axisLine={{ stroke: '#9ca3af', strokeWidth: 1 }} tickLine={{ stroke: '#9ca3af' }} style={{ fontSize: '11px' }} tick={{ fill: '#9ca3af' }} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1.5px solid #f3f4f6', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '10px 14px' }}
                  cursor={{ fill: 'rgba(99,102,241,0.06)' }}
                  formatter={(value) => [<span style={{ fontWeight: 700, color: '#3730a3' }}>{value}</span>, 'Process Count']}
                  labelFormatter={(label) => {
                    const item = ticketProcessData.find(d => d.name === label);
                    return <span style={{ fontWeight: 700, color: '#111827', fontSize: '12px' }}>{item?.isTrackLevel ? `Track: ${label}` : item ? `${label} (${item.track})` : label}</span>;
                  }}
                />
                <Bar dataKey="processCount" fill="url(#ticketProcessGradient)" barSize={14} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey="processCount" position="top" style={{ fill: '#3730a3', fontWeight: 700, fontSize: '11px' }} formatter={v => v > 0 ? v : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="download-button-container" style={{ position: 'absolute', right: '12px', bottom: '10px', marginTop: 0 }}>
            <button onClick={handleDownloadTicketProcess} className="download-btn">📥 Download</button>
          </div>
        </div>

          {/* Ticket Update Dashboard */}
          <div className="chart-card" style={{ position: 'relative', paddingBottom: '40px' }}>
            <div style={{ padding: '10px 12px 0 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{
                    width: '34px', height: '34px', borderRadius: '8px', flexShrink: 0,
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 3px 10px rgba(99,102,241,0.35)', fontSize: '16px'
                  }}>🛠️</div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>Ticket Updates</h3>
                    <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#6b7280', fontWeight: 400, lineHeight: 1.4 }}>
                      {ticketProcessTrack?.length > 0 ? 'Top 20 users by ticket updates for selected track(s)' : ticketProcessSBU?.length > 0 ? 'Top 20 tracks by ticket updates — select a Track to see users' : 'Select SBU → Track to see who updated ServiceNow tickets'}
                    </p>
                  </div>
                </div>

                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
                  border: '1.5px solid #a5b4fc', borderRadius: '8px',
                  padding: '4px 8px', textAlign: 'center', flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(99,102,241,0.12)'
                }}>
                  <span style={{ fontSize: '16px', fontWeight: 800, color: '#3730a3', lineHeight: 1, letterSpacing: '-0.4px' }}>
                    {ticketUpdateData.reduce((sum, d) => sum + (d.updateCount || 0), 0)}
                  </span>
                  <span style={{ fontSize: '8px', fontWeight: 600, color: '#4f46e5', marginTop: '1px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Updates</span>
                </div>
              </div>
            </div>

            <div style={{ padding: '2px 6px 0 6px' }}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={ticketUpdateData} margin={{ top: 22, right: 12, bottom: 2, left: 4 }}>
                  <defs>
                    <linearGradient id="ticketUpdateGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6ee7b7" />
                      <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="name" stroke="#9ca3af" axisLine={{ stroke: '#9ca3af', strokeWidth: 1 }} tickLine={{ stroke: '#9ca3af' }} interval={0} height={36}
                    tick={({ x, y, payload, index }) => {
                      if (index % 2 !== 0) return <g />;
                      const raw = payload.value || '';
                      const label = raw.length > 12 ? raw.substring(0, 11) + '…' : raw;
                      return (<g transform={`translate(${x},${y})`}><text x={0} y={0} dy={12} textAnchor="middle" fill="#9ca3af" fontSize={9} fontWeight={500}>{label}</text></g>);
                    }}
                  />
                  <YAxis stroke="#9ca3af" axisLine={{ stroke: '#9ca3af', strokeWidth: 1 }} tickLine={{ stroke: '#9ca3af' }} style={{ fontSize: '11px' }} tick={{ fill: '#9ca3af' }} />
                  <Tooltip
                    contentStyle={{ background: 'white', border: '1.5px solid #f3f4f6', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '10px 14px' }}
                    cursor={{ fill: 'rgba(16,185,129,0.08)' }}
                    formatter={(value) => [<span style={{ fontWeight: 700, color: '#047857' }}>{value}</span>, 'Update Count']}
                    labelFormatter={(label) => {
                      const item = ticketUpdateData.find(d => d.name === label);
                      return <span style={{ fontWeight: 700, color: '#111827', fontSize: '12px' }}>{item?.isTrackLevel ? `Track: ${label}` : item ? `${label} (${item.track})` : label}</span>;
                    }}
                  />
                  <Bar dataKey="updateCount" fill="url(#ticketUpdateGradient)" barSize={14} radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="updateCount" position="top" style={{ fill: '#047857', fontWeight: 700, fontSize: '11px' }} formatter={v => v > 0 ? v : ''} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="download-button-container" style={{ position: 'absolute', right: '12px', bottom: '10px', marginTop: 0 }}>
              <button onClick={handleDownloadTicketUpdates} className="download-btn">📥 Download</button>
            </div>
          </div>
        </div>

        <div className="charts-grid">
          {/* Dashboard 3A: Overall Feedback */}
          <div className="chart-card">
            <div className="chart-header">
              <UnifiedDashboardHeader
                icon="😊"
                title="Overall Feedback"
                subtitle="User satisfaction metrics"
                totalValue={positiveFeedbackData.reduce((sum, d) => sum + (d.count || 0), 0)}
                totalLabel="Total Feedbacks"
                showTotalBadge={false}
              />
            </div>
            {/* Filters: SBU, Track, Month/Date on one row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {/* SBU Filter */}
                <div className="feedback-filter-item feedback-filter-group">
                  <label className="feedback-filter-label">SBU:</label>
                  <div ref={positiveFeedbackSBUDropdownRef} className="feedback-dropdown-wrapper">
                    <div 
                      onClick={() => setShowPositiveFeedbackSBUDropdown(!showPositiveFeedbackSBUDropdown)}
                      className="feedback-group-dropdown-trigger"
                    >
                      <span className="feedback-group-label">{getSelectedPositiveFeedbackSBULabel()}</span>
                      <span className="feedback-dropdown-arrow">▼</span>
                    </div>
                    {showPositiveFeedbackSBUDropdown && (
                      <div className="feedback-group-dropdown-menu">
                        <div onClick={handleSelectAllPositiveFeedbackSBU} className="feedback-group-select-all">
                          <input type="checkbox" checked={isAllPositiveFeedbackSBUSelected()} onChange={handleSelectAllPositiveFeedbackSBU} className="feedback-group-checkbox positive" onClick={(e) => e.stopPropagation()} />
                          <span className="feedback-group-option-label">Select All SBU</span>
                        </div>
                        {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => (
                          <div key={sbu.value} onClick={() => handleTogglePositiveFeedbackSBU(sbu)} className="feedback-group-option">
                            <input type="checkbox" checked={Array.isArray(positiveFeedbackSBU) && positiveFeedbackSBU.some(s => s.value === sbu.value)} onChange={() => handleTogglePositiveFeedbackSBU(sbu)} className="feedback-group-checkbox positive" onClick={(e) => e.stopPropagation()} />
                            <span className="feedback-group-option-label">{sbu.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Track Filter */}
                <div className="feedback-filter-item feedback-filter-group">
                  <label className="feedback-filter-label">Track:</label>
                  <div ref={positiveFeedbackTrackDropdownRef} className="feedback-dropdown-wrapper">
                    <div 
                      onClick={() => setShowPositiveFeedbackTrackDropdown(!showPositiveFeedbackTrackDropdown)}
                      className="feedback-group-dropdown-trigger"
                    >
                      <span className="feedback-group-label" style={{ flex: 1 }}>{getSelectedPositiveFeedbackTrackLabel()}</span>
                      {positiveFeedbackTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setPositiveFeedbackTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                      <span className="feedback-dropdown-arrow">▼</span>
                    </div>
                    {showPositiveFeedbackTrackDropdown && (
                      <div className="feedback-group-dropdown-menu">
                        {positiveFeedbackAvailableTracks.filter(t => t.value !== 'all').map((track) => (
                          <div key={track.value} onClick={() => handleTogglePositiveFeedbackTrack(track)} className="feedback-group-option">
                            <input type="radio" name="positiveFeedbackTrackRadio" checked={Array.isArray(positiveFeedbackTrack) && positiveFeedbackTrack.some(t => t.value === track.value)} onChange={() => handleTogglePositiveFeedbackTrack(track)} className="feedback-group-checkbox positive" onClick={(e) => e.stopPropagation()} />
                            <span className="feedback-group-option-label">{track.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

            </div>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={positiveFeedbackData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }} barSize={24}>
                  <defs>
                    <linearGradient id="satisfiedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#059669" />
                    </linearGradient>
                    <linearGradient id="notSatisfiedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" />
                      <stop offset="100%" stopColor="#dc2626" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#4a5568"
                    style={{ fontSize: '11px', fontWeight: '500' }}
                    angle={0}
                    textAnchor="middle"
                    height={30}
                    interval={0}
                  />
                  <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [value, 'Count']}
                  />
                  <Bar 
                    dataKey="count" 
                    barSize={12}
                    radius={[8, 8, 0, 0]}
                  >
                    {positiveFeedbackData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index === 0 ? 'url(#satisfiedGradient)' : 'url(#notSatisfiedGradient)'} />
                    ))}
                    <LabelList dataKey="count" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '12px' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            <div className="download-button-container">
              <button onClick={handleDownloadPositiveFeedback} className="download-btn">
                📥 Download
              </button>
            </div>
          </div>

          {/* Dashboard 3B: Areas to be improved */}
          <div className="chart-card">
            <div className="chart-header">
              <UnifiedDashboardHeader
                icon="⚠️"
                title="Areas to be Improved"
                subtitle="Feedback areas needing attention"
                totalValue={negativeFeedbackData.reduce((sum, d) => sum + (d.count || 0), 0)}
                totalLabel="Total Issues"
                showTotalBadge={false}
              />
            </div>
            {/* Filters: SBU, Track, Month/Date on one row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {/* SBU Filter */}
                <div className="feedback-filter-item feedback-filter-group">
                  <label className="feedback-filter-label">SBU:</label>
                  <div ref={negativeFeedbackSBUDropdownRef} className="feedback-dropdown-wrapper">
                    <div 
                      onClick={() => setShowNegativeFeedbackSBUDropdown(!showNegativeFeedbackSBUDropdown)}
                      className="feedback-group-dropdown-trigger"
                    >
                      <span className="feedback-group-label">{getSelectedNegativeFeedbackSBULabel()}</span>
                      <span className="feedback-dropdown-arrow">▼</span>
                    </div>
                    {showNegativeFeedbackSBUDropdown && (
                      <div className="feedback-group-dropdown-menu">
                        <div onClick={handleSelectAllNegativeFeedbackSBU} className="feedback-group-select-all">
                          <input type="checkbox" checked={isAllNegativeFeedbackSBUSelected()} onChange={handleSelectAllNegativeFeedbackSBU} className="feedback-group-checkbox negative" onClick={(e) => e.stopPropagation()} />
                          <span className="feedback-group-option-label">Select All SBU</span>
                        </div>
                        {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => (
                          <div key={sbu.value} onClick={() => handleToggleNegativeFeedbackSBU(sbu)} className="feedback-group-option">
                            <input type="checkbox" checked={Array.isArray(negativeFeedbackSBU) && negativeFeedbackSBU.some(s => s.value === sbu.value)} onChange={() => handleToggleNegativeFeedbackSBU(sbu)} className="feedback-group-checkbox negative" onClick={(e) => e.stopPropagation()} />
                            <span className="feedback-group-option-label">{sbu.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Track Filter */}
                <div className="feedback-filter-item feedback-filter-group">
                  <label className="feedback-filter-label">Track:</label>
                  <div ref={negativeFeedbackTrackDropdownRef} className="feedback-dropdown-wrapper">
                    <div 
                      onClick={() => setShowNegativeFeedbackTrackDropdown(!showNegativeFeedbackTrackDropdown)}
                      className="feedback-group-dropdown-trigger"
                    >
                      <span className="feedback-group-label" style={{ flex: 1 }}>{getSelectedNegativeFeedbackTrackLabel()}</span>
                      {negativeFeedbackTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setNegativeFeedbackTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                      <span className="feedback-dropdown-arrow">▼</span>
                    </div>
                    {showNegativeFeedbackTrackDropdown && (
                      <div className="feedback-group-dropdown-menu">
                        {negativeFeedbackAvailableTracks.filter(t => t.value !== 'all').map((track) => (
                          <div key={track.value} onClick={() => handleToggleNegativeFeedbackTrack(track)} className="feedback-group-option">
                            <input type="radio" name="negativeFeedbackTrackRadio" checked={Array.isArray(negativeFeedbackTrack) && negativeFeedbackTrack.some(t => t.value === track.value)} onChange={() => handleToggleNegativeFeedbackTrack(track)} className="feedback-group-checkbox negative" onClick={(e) => e.stopPropagation()} />
                            <span className="feedback-group-option-label">{track.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
            </div>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={negativeFeedbackData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }} barSize={24}>
                  <defs>
                    <linearGradient id="negativeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" />
                      <stop offset="100%" stopColor="#dc2626" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#4a5568"
                    style={{ fontSize: '11px', fontWeight: '500' }}
                    angle={0}
                    textAnchor="middle"
                    height={30}
                    interval={0}
                  />
                  <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [value, 'Count']}
                  />
                  <Bar 
                    dataKey="count" 
                    barSize={12}
                    fill="url(#negativeGradient)" 
                    radius={[8, 8, 0, 0]}
                  >
                    <LabelList dataKey="count" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '12px' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            <div className="download-button-container">
              <button onClick={handleDownloadNegativeFeedback} className="download-btn">
                📥 Download
              </button>
            </div>
          </div>
        </div>

        {/* 4. Screen Time as per All Teams - Shows teams with their assignment groups combined */}
        <div className="chart-card" style={{ position: 'relative', paddingBottom: '40px' }}>
          <div className="chart-header">
            <UnifiedDashboardHeader
              icon="⏱️"
              title="Screen Time by Track"
              subtitle={selectedTrack?.length > 0 ? 'Top 20 users by screen time for selected track(s)' : selectedSBU?.length > 0 ? 'Top 20 tracks by screen time for selected SBU — Select a Track to see users' : 'Select SBU → Track to see screen time distribution'}
              totalValue={`${Math.round(allTeamsData.reduce((sum, d) => sum + (d.screenTime || 0), 0)).toLocaleString()}h`}
              totalLabel="Total Hours"
              showTotalBadge={false}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              {/* SBU Selector with Checkbox - FIRST */}
              <div className="sbu-selector" ref={sbuDropdownRef} style={{ 
                position: 'relative', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                marginRight: '8px',
                minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  SBU:
                </label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowSBUDropdown(!showSBUDropdown)}
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ 
                      fontSize: '12px', 
                      color: selectedSBU?.length > 0 ? '#1f2937' : '#9ca3af',
                      fontWeight: '500'
                    }}>
                      {getSelectedSBULabel()}
                    </span>
                    <span style={{ 
                      fontSize: '10px', 
                      color: '#6b7280',
                      transform: showSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}>▼</span>
                  </div>
                  
                  {showSBUDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'slideDown 0.2s ease'
                    }}>
                      {/* Select All Option */}
                      <div 
                        onClick={handleSelectAllSBU}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          borderBottom: '2px solid #e5e7eb',
                          backgroundColor: '#f9fafb',
                          fontWeight: '600',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input
                          type="checkbox"
                          checked={isAllSBUSelected()}
                          onChange={handleSelectAllSBU}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#10b981'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>

                      {/* Individual SBU Options */}
                      {availableSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(selectedSBU) && selectedSBU.some(s => s.value === sbu.value);
                        return (
                          <div
                            key={sbu.value}
                            onClick={() => handleToggleSBU(sbu)}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              backgroundColor: isSelected ? '#f0fdf4' : 'white',
                              transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isSelected ? '#f0fdf4' : 'white';
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSBU(sbu)}
                              style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                                accentColor: '#10b981'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span style={{ 
                              fontSize: '12px', 
                              color: '#374151',
                              fontWeight: isSelected ? '500' : '400'
                            }}>
                              {sbu.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector with Checkbox - SECOND */}
              <div className="track-selector" ref={trackDropdownRef} style={{ 
                position: 'relative', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                marginRight: '8px',
                minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Track:
                </label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowTrackDropdown(!showTrackDropdown)}
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ 
                      fontSize: '12px', 
                      color: selectedTrack?.length > 0 ? '#1f2937' : '#9ca3af',
                      fontWeight: '500',
                      flex: 1
                    }}>
                      {getSelectedTrackLabel()}
                    </span>
                    {selectedTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setSelectedTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ 
                      fontSize: '10px', 
                      color: '#6b7280',
                      transform: showTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}>▼</span>
                  </div>
                  
                  {showTrackDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'slideDown 0.2s ease'
                    }}>
                      {/* Individual Track Options */}
                      {availableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(selectedTrack) && selectedTrack.some(t => t.value === track.value);
                        return (
                          <div
                            key={track.value}
                            onClick={() => handleToggleTrack(track)}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              backgroundColor: isSelected ? '#f0fdf4' : 'white',
                              transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isSelected ? '#f0fdf4' : 'white';
                            }}
                          >
                            <input
                              type="radio"
                              name="selectedTrackRadio"
                              checked={isSelected}
                              onChange={() => handleToggleTrack(track)}
                              style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                                accentColor: '#10b981'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span style={{ 
                              fontSize: '12px', 
                              color: '#374151',
                              fontWeight: isSelected ? '500' : '400'
                            }}>
                              {track.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Filter Type Selector */}
              <FilterTypeButtons 
                filterType={allTeamsFilterType} 
                onFilterTypeChange={setAllTeamsFilterType} 
              />
              
              {/* Conditional Filter Selectors */}
              {allTeamsFilterType === 'month' && (
                <MonthSelector
                  year={selectedAllTeamsYear}
                  month={selectedAllTeamsMonth}
                  onYearChange={setSelectedAllTeamsYear}
                  onMonthChange={setSelectedAllTeamsMonth}
                />
              )}
              
              {allTeamsFilterType === 'date' && (
                <DateRangeSelector
                  fromDate={selectedAllTeamsFromDate}
                  toDate={selectedAllTeamsToDate}
                  onFromDateChange={setSelectedAllTeamsFromDate}
                  onToDateChange={setSelectedAllTeamsToDate}
                />
              )}
          </div>
          </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={allTeamsData} margin={{ top: 25, right: 16, bottom: 5, left: 8 }}>
                <defs>
                  <linearGradient id="trackGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="name" 
                  stroke="#4a5568"
                  interval={0}
                  height={36}
                  tick={({ x, y, payload, index }) => {
                    if (index % 2 !== 0) return <g />;
                    const raw = payload.value || '';
                    const label = raw.length > 12 ? raw.substring(0, 11) + '…' : raw;
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text x={0} y={0} dy={12} textAnchor="middle" fill="#4a5568" fontSize={8}>{label}</text>
                      </g>
                    );
                  }}
                />
                <YAxis stroke="#4a5568" style={{ fontSize: '11px' }} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [formatScreenTime(value), 'Screen Time']}
                  labelFormatter={(label) => {
                    const item = allTeamsData.find(d => d.name === label);
                    if (item?.isTrackLevel) return `Track: ${label}`;
                    return item ? `${label} (${item.track})` : label;
                  }}
                />
                <Bar 
                  dataKey="screenTime" 
                  barSize={12}
                  fill="url(#trackGradient)" 
                  radius={[8, 8, 0, 0]}
                >
                  <LabelList dataKey="screenTime" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '11px' }} formatter={(value) => value > 0 ? Math.round(value) : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          <div className="download-button-container" style={{ position: 'absolute', right: '8px', bottom: '8px', marginTop: 0 }}>
            <button onClick={handleDownloadAllTeamsData} className="download-btn">
              📥 Download
            </button>
          </div>
        </div>

        {/* Trends of Login Count Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <UnifiedDashboardHeader
              icon="📈"
              title="Trends of Login Count"
              subtitle={loginTrendsTrack?.length > 0 ? 'Top 5 users by login trend for selected track(s)' : loginTrendsSBU?.length > 0 ? 'Top 5 tracks by login trend for selected SBU — Select a Track to see users' : 'Select SBU → Track to see login trends over time'}
              totalValue={loginTrendsData.reduce((sum, row) => sum + loginTrendsPeriods.reduce((inner, period) => inner + (Number(row[period]) || 0), 0), 0)}
              totalLabel="Total Logins"
              showTotalBadge={false}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              {/* SBU Selector */}
              <div className="sbu-selector" ref={loginTrendsSBUDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowLoginTrendsSBUDropdown(!showLoginTrendsSBUDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: loginTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>
                      {getSelectedLoginTrendsSBULabel()}
                    </span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showLoginTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showLoginTrendsSBUDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px', overflowY: 'auto', zIndex: 1000
                    }}>
                      <div onClick={handleSelectAllLoginTrendsSBU} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e7ff'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input type="checkbox" checked={isAllLoginTrendsSBUSelected()} onChange={handleSelectAllLoginTrendsSBU}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(loginTrendsSBU) && loginTrendsSBU.some(s => s.value === sbu.value);
                        return (
                          <div key={sbu.value} onClick={() => handleToggleLoginTrendsSBU(sbu)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}
                          >
                            <input type="checkbox" checked={isSelected} onChange={() => handleToggleLoginTrendsSBU(sbu)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector */}
              <div className="track-selector" ref={loginTrendsTrackDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowLoginTrendsTrackDropdown(!showLoginTrendsTrackDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: loginTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>
                      {getSelectedLoginTrendsTrackLabel()}
                    </span>
                    {loginTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setLoginTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showLoginTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showLoginTrendsTrackDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px', overflowY: 'auto', zIndex: 1000
                    }}>
                      {loginTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(loginTrendsTrack) && loginTrendsTrack.some(t => t.value === track.value);
                        return (
                          <div key={track.value} onClick={() => handleToggleLoginTrendsTrack(track)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}
                          >
                            <input type="radio" name="loginTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleLoginTrendsTrack(track)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Month/Week Toggle */}
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button
                  onClick={() => setLoginTrendsFilterType('month')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    backgroundColor: loginTrendsFilterType === 'month' ? '#6366f1' : 'white',
                    color: loginTrendsFilterType === 'month' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Month</button>
                <button
                  onClick={() => setLoginTrendsFilterType('week')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    borderLeft: '1px solid #d1d5db',
                    backgroundColor: loginTrendsFilterType === 'week' ? '#6366f1' : 'white',
                    color: loginTrendsFilterType === 'week' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Week</button>
              </div>

              {/* Month Picker */}
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(loginTrendsMonth);
                return (
                  <div style={{ width: '120px' }}>
                    <DatePicker
                      selected={new Date(parseInt(loginTrendsYear), monthIndex)}
                      onChange={(date) => {
                        if (date) {
                          setLoginTrendsYear(date.getFullYear().toString());
                          setLoginTrendsMonth(monthNamesLocal[date.getMonth()]);
                        }
                      }}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      className="date-input"
                      placeholderText="Select Month"
                      popperPlacement="bottom-start"
                      style={{ width: '100%' }}
                    />
                  </div>
                );
              })()}

              {/* Week Selector (only when week mode) */}
              {loginTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select
                    value={loginTrendsWeek}
                    onChange={(e) => setLoginTrendsWeek(e.target.value)}
                    style={{
                      padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px',
                      backgroundColor: 'white', color: '#374151', cursor: 'pointer'
                    }}
                  >
                    <option value="1">Week 1</option>
                    <option value="2">Week 2</option>
                    <option value="3">Week 3</option>
                    <option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={loginTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="trendColor0" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#94a3b8" />
                  <stop offset="100%" stopColor="#64748b" />
                </linearGradient>
                <linearGradient id="trendColor1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
                <linearGradient id="trendColor2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
                <linearGradient id="trendColor3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" stroke="#4a5568" interval={0} height={70}
                tick={({ x, y, payload }) => {
                  const label = payload.value || '';
                  const maxChars = 10;
                  const words = label.split(/[\s&]+/);
                  const lines = [];
                  let current = '';
                  words.forEach(w => {
                    if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; }
                    else { current = current ? current + ' ' + w : w; }
                  });
                  if (current) lines.push(current);
                  return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>);
                }}
              />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                itemSorter={(item) => loginTrendsPeriods.indexOf(item.dataKey)}
                formatter={(value, name) => [value, formatTrendPeriodLabel(name)]}
              />
              <Legend wrapperStyle={{ paddingTop: '4px' }} formatter={(value) => formatTrendPeriodLabel(value)} />
              {loginTrendsPeriods.map((period, idx) => (
                <Bar key={period} dataKey={period} fill={`url(#trendColor${idx})`} barSize={12} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey={period} position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} formatter={(value) => value > 0 ? value : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadLoginTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Trends of Ticket Process Dashboard */}
        <div className="chart-card" style={{ position: 'relative', paddingBottom: '40px' }}>
          <div className="chart-header">
            <UnifiedDashboardHeader
              icon="🎫"
              title="Trends of Ticket Process"
              subtitle={ticketTrendsTrack?.length > 0 ? 'Top 5 users by process trend for selected track(s)' : ticketTrendsSBU?.length > 0 ? 'Top 5 tracks by process trend for selected SBU — Select a Track to see users' : 'Select SBU → Track to see ticket process trends over time'}
              totalValue={ticketTrendsData.reduce((sum, row) => sum + ticketTrendsPeriods.reduce((inner, period) => inner + (Number(row[period]) || 0), 0), 0)}
              totalLabel="Total Processes"
              showTotalBadge={false}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              {/* SBU Selector */}
              <div className="sbu-selector" ref={ticketTrendsSBUDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowTicketTrendsSBUDropdown(!showTicketTrendsSBUDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: ticketTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>
                      {getSelectedTicketTrendsSBULabel()}
                    </span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showTicketTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showTicketTrendsSBUDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px', overflowY: 'auto', zIndex: 1000
                    }}>
                      <div onClick={handleSelectAllTicketTrendsSBU} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e7ff'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input type="checkbox" checked={isAllTicketTrendsSBUSelected()} onChange={handleSelectAllTicketTrendsSBU}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(ticketTrendsSBU) && ticketTrendsSBU.some(s => s.value === sbu.value);
                        return (
                          <div key={sbu.value} onClick={() => handleToggleTicketTrendsSBU(sbu)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}
                          >
                            <input type="checkbox" checked={isSelected} onChange={() => handleToggleTicketTrendsSBU(sbu)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector */}
              <div className="track-selector" ref={ticketTrendsTrackDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowTicketTrendsTrackDropdown(!showTicketTrendsTrackDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: ticketTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>
                      {getSelectedTicketTrendsTrackLabel()}
                    </span>
                    {ticketTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setTicketTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showTicketTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showTicketTrendsTrackDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px', overflowY: 'auto', zIndex: 1000
                    }}>
                      {ticketTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(ticketTrendsTrack) && ticketTrendsTrack.some(t => t.value === track.value);
                        return (
                          <div key={track.value} onClick={() => handleToggleTicketTrendsTrack(track)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}
                          >
                            <input type="radio" name="ticketTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleTicketTrendsTrack(track)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Month/Week Toggle */}
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button
                  onClick={() => setTicketTrendsFilterType('month')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    backgroundColor: ticketTrendsFilterType === 'month' ? '#6366f1' : 'white',
                    color: ticketTrendsFilterType === 'month' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Month</button>
                <button
                  onClick={() => setTicketTrendsFilterType('week')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    borderLeft: '1px solid #d1d5db',
                    backgroundColor: ticketTrendsFilterType === 'week' ? '#6366f1' : 'white',
                    color: ticketTrendsFilterType === 'week' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Week</button>
              </div>

              {/* Month Picker */}
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(ticketTrendsMonth);
                return (
                  <div style={{ width: '120px' }}>
                    <DatePicker
                      selected={new Date(parseInt(ticketTrendsYear), monthIndex)}
                      onChange={(date) => {
                        if (date) {
                          setTicketTrendsYear(date.getFullYear().toString());
                          setTicketTrendsMonth(monthNamesLocal[date.getMonth()]);
                        }
                      }}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      className="date-input"
                      placeholderText="Select Month"
                      popperPlacement="bottom-start"
                      style={{ width: '100%' }}
                    />
                  </div>
                );
              })()}

              {/* Week Selector (only when week mode) */}
              {ticketTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select
                    value={ticketTrendsWeek}
                    onChange={(e) => setTicketTrendsWeek(e.target.value)}
                    style={{
                      padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px',
                      backgroundColor: 'white', color: '#374151', cursor: 'pointer'
                    }}
                  >
                    <option value="1">Week 1</option>
                    <option value="2">Week 2</option>
                    <option value="3">Week 3</option>
                    <option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={ticketTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="ticketTrendColor0" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#94a3b8" />
                  <stop offset="100%" stopColor="#64748b" />
                </linearGradient>
                <linearGradient id="ticketTrendColor1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
                <linearGradient id="ticketTrendColor2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
                <linearGradient id="ticketTrendColor3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" stroke="#4a5568" interval={0} height={70}
                tick={({ x, y, payload }) => {
                  const label = payload.value || '';
                  const maxChars = 10;
                  const words = label.split(/[\s&]+/);
                  const lines = [];
                  let current = '';
                  words.forEach(w => {
                    if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; }
                    else { current = current ? current + ' ' + w : w; }
                  });
                  if (current) lines.push(current);
                  return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>);
                }}
              />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                itemSorter={(item) => ticketTrendsPeriods.indexOf(item.dataKey)}
                formatter={(value, name) => [value, formatTrendPeriodLabel(name)]}
              />
              <Legend wrapperStyle={{ paddingTop: '4px' }} formatter={(value) => formatTrendPeriodLabel(value)} />
              {ticketTrendsPeriods.map((period, idx) => (
                <Bar key={period} dataKey={period} fill={`url(#ticketTrendColor${idx})`} barSize={12} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey={period} position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} formatter={(value) => value > 0 ? value : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container" style={{ position: 'absolute', right: '8px', bottom: '8px', marginTop: 0 }}>
            <button onClick={handleDownloadTicketTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Trends of Overall Feedback Dashboard */}
        <div className="chart-card" style={{ position: 'relative' }}>
          <div className="chart-header">
            <UnifiedDashboardHeader
              icon="💬"
              title="Trends of Overall Feedback"
              subtitle={feedbackTrendsTrack?.length > 0 ? 'Top 5 users by feedback trend for selected track(s)' : feedbackTrendsSBU?.length > 0 ? 'Top 5 tracks by feedback trend for selected SBU — Select a Track to see users' : 'Select SBU → Track to see feedback trends over time'}
              totalValue={feedbackTrendsData.reduce((sum, row) => sum + overallFeedbackTrendBasePeriods.reduce((inner, baseKey) => inner + (Number(row[`${baseKey} 👍`]) || 0) + (Number(row[`${baseKey} 👎`]) || 0), 0), 0)}
              totalLabel="Total Feedbacks"
              showTotalBadge={false}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              <div className="sbu-selector" ref={feedbackTrendsSBUDropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div onClick={() => setShowFeedbackTrendsSBUDropdown(!showFeedbackTrendsSBUDropdown)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(99, 102, 241, 0.12)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}>
                    <span style={{ fontSize: '12px', color: feedbackTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>{getSelectedFeedbackTrendsSBULabel()}</span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showFeedbackTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showFeedbackTrendsSBUDropdown && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', maxHeight: '220px', overflowY: 'auto', zIndex: 1000 }}>
                      <div onClick={handleSelectAllFeedbackTrendsSBU} style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1 }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e7ff'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}>
                        <input type="checkbox" checked={isAllFeedbackTrendsSBUSelected()} onChange={handleSelectAllFeedbackTrendsSBU} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(feedbackTrendsSBU) && feedbackTrendsSBU.some(s => s.value === sbu.value);
                        return (<div key={sbu.value} onClick={() => handleToggleFeedbackTrendsSBU(sbu)} style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}>
                          <input type="checkbox" checked={isSelected} onChange={() => handleToggleFeedbackTrendsSBU(sbu)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                          <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="track-selector" ref={feedbackTrendsTrackDropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div onClick={() => setShowFeedbackTrendsTrackDropdown(!showFeedbackTrendsTrackDropdown)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(99, 102, 241, 0.12)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}>
                    <span style={{ fontSize: '12px', color: feedbackTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>{getSelectedFeedbackTrendsTrackLabel()}</span>
                    {feedbackTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setFeedbackTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showFeedbackTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showFeedbackTrendsTrackDropdown && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', maxHeight: '320px', overflowY: 'auto', zIndex: 1000 }}>
                      {feedbackTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(feedbackTrendsTrack) && feedbackTrendsTrack.some(t => t.value === track.value);
                        return (<div key={track.value} onClick={() => handleToggleFeedbackTrendsTrack(track)} style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}>
                          <input type="radio" name="feedbackTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleFeedbackTrendsTrack(track)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                          <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button onClick={() => setFeedbackTrendsFilterType('month')} style={{ padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer', backgroundColor: feedbackTrendsFilterType === 'month' ? '#6366f1' : 'white', color: feedbackTrendsFilterType === 'month' ? 'white' : '#4b5563', transition: 'all 0.2s ease' }}>Month</button>
                <button onClick={() => setFeedbackTrendsFilterType('week')} style={{ padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer', borderLeft: '1px solid #d1d5db', backgroundColor: feedbackTrendsFilterType === 'week' ? '#6366f1' : 'white', color: feedbackTrendsFilterType === 'week' ? 'white' : '#4b5563', transition: 'all 0.2s ease' }}>Week</button>
              </div>
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(feedbackTrendsMonth);
                return (<div style={{ width: '120px' }}><DatePicker selected={new Date(parseInt(feedbackTrendsYear), monthIndex)} onChange={(date) => { if (date) { setFeedbackTrendsYear(date.getFullYear().toString()); setFeedbackTrendsMonth(monthNamesLocal[date.getMonth()]); } }} dateFormat="MM/yyyy" showMonthYearPicker className="date-input" placeholderText="Select Month" popperPlacement="bottom-start" /></div>);
              })()}
              {feedbackTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select value={feedbackTrendsWeek} onChange={(e) => setFeedbackTrendsWeek(e.target.value)} style={{ padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: 'white', color: '#374151', cursor: 'pointer' }}>
                    <option value="1">Week 1</option><option value="2">Week 2</option><option value="3">Week 3</option><option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={feedbackTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                {overallFeedbackTrendBasePeriods.map((baseKey, idx) => (
                  <React.Fragment key={baseKey}>
                    <linearGradient id={`fbSatisfiedGradient${idx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#86efac" />
                      <stop offset="100%" stopColor="#22c55e" />
                    </linearGradient>
                    <linearGradient id={`fbNotSatisfiedGradient${idx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fca5a5" />
                      <stop offset="100%" stopColor="#ef4444" />
                    </linearGradient>
                  </React.Fragment>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#4a5568" interval={0} height={70} tick={({ x, y, payload }) => { const label = payload.value || ''; const maxChars = 10; const words = label.split(/[\s&]+/); const lines = []; let current = ''; words.forEach(w => { if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; } else { current = current ? current + ' ' + w : w; } }); if (current) lines.push(current); return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>); }} />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip content={({ active }) => {
                if (!active || !hoveredFeedbackBar) return null;
                const { name, periodLabel, satisfied, notSatisfied } = hoveredFeedbackBar;
                const total = satisfied + notSatisfied;
                return (
                  <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '10px 12px', minWidth: '200px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#1f2937', marginBottom: '6px' }}>{name}</div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb' }}>Period: {periodLabel}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#166534', marginTop: '6px', fontWeight: '500' }}><span>👍 Satisfied</span><span style={{ fontWeight: 700 }}>{satisfied}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#b91c1c', marginTop: '4px', fontWeight: '500' }}><span>👎 Not Satisfied</span><span style={{ fontWeight: 700 }}>{notSatisfied}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 700, color: '#1f2937', marginTop: '8px', paddingTop: '6px', borderTop: '1px solid #e5e7eb' }}><span>Total</span><span>{total}</span></div>
                  </div>
                );
              }} />
              {overallFeedbackTrendBasePeriods.map((baseKey, idx) => (
                <React.Fragment key={baseKey}>
                  <Bar dataKey={`${baseKey} 👍`} stackId={baseKey} fill={`url(#fbSatisfiedGradient${idx})`} barSize={12}
                    onMouseEnter={(data) => { const row = data?.payload || {}; setHoveredFeedbackBar({ name: row.name, periodLabel: formatTrendPeriodLabel(baseKey), satisfied: Number(row[`${baseKey} 👍`] || 0), notSatisfied: Number(row[`${baseKey} 👎`] || 0) }); }}
                    onMouseLeave={() => setHoveredFeedbackBar(null)} />
                  <Bar dataKey={`${baseKey} 👎`} stackId={baseKey} fill={`url(#fbNotSatisfiedGradient${idx})`} barSize={12} radius={[6, 6, 0, 0]}
                    onMouseEnter={(data) => { const row = data?.payload || {}; setHoveredFeedbackBar({ name: row.name, periodLabel: formatTrendPeriodLabel(baseKey), satisfied: Number(row[`${baseKey} 👍`] || 0), notSatisfied: Number(row[`${baseKey} 👎`] || 0) }); }}
                    onMouseLeave={() => setHoveredFeedbackBar(null)} />
                </React.Fragment>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '18px', marginTop: '6px', marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#4b5563' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', background: '#22c55e', display: 'inline-block' }}></span>
              <span>Satisfied</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', background: '#ef4444', display: 'inline-block' }}></span>
              <span>Not Satisfied</span>
            </div>
          </div>
          <div className="download-button-container">
            <button onClick={handleDownloadFeedbackTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Trends of Areas to be Improved Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <UnifiedDashboardHeader
              icon="🛠️"
              title="Trends of Areas to be Improved"
              subtitle="Top 5 improvement reasons trend over time — Select SBU/Track to filter"
              totalValue={areasTrendsData.reduce((sum, row) => sum + areasTrendsPeriods.reduce((inner, period) => inner + (Number(row[period]) || 0), 0), 0)}
              totalLabel="Total Issues"
              showTotalBadge={false}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              <div className="sbu-selector" ref={areasTrendsSBUDropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div onClick={() => setShowAreasTrendsSBUDropdown(!showAreasTrendsSBUDropdown)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}>
                    <span style={{ fontSize: '12px', color: areasTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>{getSelectedAreasTrendsSBULabel()}</span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showAreasTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showAreasTrendsSBUDropdown && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', maxHeight: '220px', overflowY: 'auto', zIndex: 1000 }}>
                      <div onClick={handleSelectAllAreasTrendsSBU} style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1 }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e7ff'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}>
                        <input type="checkbox" checked={isAllAreasTrendsSBUSelected()} onChange={handleSelectAllAreasTrendsSBU} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(areasTrendsSBU) && areasTrendsSBU.some(s => s.value === sbu.value);
                        return (<div key={sbu.value} onClick={() => handleToggleAreasTrendsSBU(sbu)} style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}>
                          <input type="checkbox" checked={isSelected} onChange={() => handleToggleAreasTrendsSBU(sbu)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                          <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="track-selector" ref={areasTrendsTrackDropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div onClick={() => setShowAreasTrendsTrackDropdown(!showAreasTrendsTrackDropdown)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}>
                    <span style={{ fontSize: '12px', color: areasTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>{getSelectedAreasTrendsTrackLabel()}</span>
                    {areasTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setAreasTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showAreasTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showAreasTrendsTrackDropdown && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', maxHeight: '320px', overflowY: 'auto', zIndex: 1000 }}>
                      {areasTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(areasTrendsTrack) && areasTrendsTrack.some(t => t.value === track.value);
                        return (<div key={track.value} onClick={() => handleToggleAreasTrendsTrack(track)} style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}>
                          <input type="radio" name="areasTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleAreasTrendsTrack(track)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                          <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button onClick={() => setAreasTrendsFilterType('month')} style={{ padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer', backgroundColor: areasTrendsFilterType === 'month' ? '#6366f1' : 'white', color: areasTrendsFilterType === 'month' ? 'white' : '#4b5563', transition: 'all 0.2s ease' }}>Month</button>
                <button onClick={() => setAreasTrendsFilterType('week')} style={{ padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer', borderLeft: '1px solid #d1d5db', backgroundColor: areasTrendsFilterType === 'week' ? '#6366f1' : 'white', color: areasTrendsFilterType === 'week' ? 'white' : '#4b5563', transition: 'all 0.2s ease' }}>Week</button>
              </div>
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(areasTrendsMonth);
                return (<div style={{ width: '120px' }}><DatePicker selected={new Date(parseInt(areasTrendsYear), monthIndex)} onChange={(date) => { if (date) { setAreasTrendsYear(date.getFullYear().toString()); setAreasTrendsMonth(monthNamesLocal[date.getMonth()]); } }} dateFormat="MM/yyyy" showMonthYearPicker className="date-input" placeholderText="Select Month" popperPlacement="bottom-start" /></div>);
              })()}
              {areasTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select value={areasTrendsWeek} onChange={(e) => setAreasTrendsWeek(e.target.value)} style={{ padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: 'white', color: '#374151', cursor: 'pointer' }}>
                    <option value="1">Week 1</option><option value="2">Week 2</option><option value="3">Week 3</option><option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={areasTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="areasTrendColor0" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#94a3b8" /><stop offset="100%" stopColor="#64748b" /></linearGradient>
                <linearGradient id="areasTrendColor1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a78bfa" /><stop offset="100%" stopColor="#7c3aed" /></linearGradient>
                <linearGradient id="areasTrendColor2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#60a5fa" /><stop offset="100%" stopColor="#2563eb" /></linearGradient>
                <linearGradient id="areasTrendColor3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399" /><stop offset="100%" stopColor="#059669" /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#4a5568" interval={0} height={70} tick={({ x, y, payload }) => { const label = payload.value || ''; const maxChars = 10; const words = label.split(/[\s&]+/); const lines = []; let current = ''; words.forEach(w => { if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; } else { current = current ? current + ' ' + w : w; } }); if (current) lines.push(current); return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>); }} />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                itemSorter={(item) => areasTrendsPeriods.indexOf(item.dataKey)}
                formatter={(value, name) => [value, formatTrendPeriodLabel(name)]}
              />
              <Legend wrapperStyle={{ paddingTop: '4px' }} formatter={(value) => formatTrendPeriodLabel(value)} />
              {areasTrendsPeriods.map((period, idx) => (
                <Bar key={period} dataKey={period} fill={`url(#areasTrendColor${idx})`} barSize={12} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey={period} position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} formatter={(value) => value > 0 ? value : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadAreasTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Trends of Screen Time by Track Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <UnifiedDashboardHeader
              icon="📊"
              title="Trends of Screen Time by Track"
              subtitle={screenTimeTrendsTrack?.length > 0 ? 'Top 5 users by screen time trend for selected track(s)' : screenTimeTrendsSBU?.length > 0 ? 'Top 5 tracks by screen time trend for selected SBU — Select a Track to see users' : 'Select SBU → Track to see screen time trends over time'}
              totalValue={`${Math.round(screenTimeTrendsData.reduce((sum, row) => sum + screenTimeTrendsPeriods.reduce((inner, period) => inner + (Number(row[period]) || 0), 0), 0)).toLocaleString()}h`}
              totalLabel="Total Hours"
              showTotalBadge={false}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              {/* SBU Selector */}
              <div className="sbu-selector" ref={screenTimeTrendsSBUDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowScreenTimeTrendsSBUDropdown(!showScreenTimeTrendsSBUDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: screenTimeTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>
                      {getSelectedScreenTimeTrendsSBULabel()}
                    </span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showScreenTimeTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showScreenTimeTrendsSBUDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px', overflowY: 'auto', zIndex: 1000
                    }}>
                      <div onClick={handleSelectAllScreenTimeTrendsSBU} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e7ff'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input type="checkbox" checked={isAllScreenTimeTrendsSBUSelected()} onChange={handleSelectAllScreenTimeTrendsSBU}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(screenTimeTrendsSBU) && screenTimeTrendsSBU.some(s => s.value === sbu.value);
                        return (
                          <div key={sbu.value} onClick={() => handleToggleScreenTimeTrendsSBU(sbu)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}
                          >
                            <input type="checkbox" checked={isSelected} onChange={() => handleToggleScreenTimeTrendsSBU(sbu)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector */}
              <div className="track-selector" ref={screenTimeTrendsTrackDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowScreenTimeTrendsTrackDropdown(!showScreenTimeTrendsTrackDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: screenTimeTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>
                      {getSelectedScreenTimeTrendsTrackLabel()}
                    </span>
                    {screenTimeTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setScreenTimeTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showScreenTimeTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showScreenTimeTrendsTrackDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px', overflowY: 'auto', zIndex: 1000
                    }}>
                      {screenTimeTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(screenTimeTrendsTrack) && screenTimeTrendsTrack.some(t => t.value === track.value);
                        return (
                          <div key={track.value} onClick={() => handleToggleScreenTimeTrendsTrack(track)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}
                          >
                            <input type="radio" name="screenTimeTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleScreenTimeTrendsTrack(track)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Month/Week Toggle */}
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button
                  onClick={() => setScreenTimeTrendsFilterType('month')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    backgroundColor: screenTimeTrendsFilterType === 'month' ? '#6366f1' : 'white',
                    color: screenTimeTrendsFilterType === 'month' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Month</button>
                <button
                  onClick={() => setScreenTimeTrendsFilterType('week')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    borderLeft: '1px solid #d1d5db',
                    backgroundColor: screenTimeTrendsFilterType === 'week' ? '#6366f1' : 'white',
                    color: screenTimeTrendsFilterType === 'week' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Week</button>
              </div>

              {/* Month Picker */}
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(screenTimeTrendsMonth);
                return (
                  <div style={{ width: '120px' }}>
                    <DatePicker
                      selected={new Date(parseInt(screenTimeTrendsYear), monthIndex)}
                      onChange={(date) => {
                        if (date) {
                          setScreenTimeTrendsYear(date.getFullYear().toString());
                          setScreenTimeTrendsMonth(monthNamesLocal[date.getMonth()]);
                        }
                      }}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      className="date-input"
                      placeholderText="Select Month"
                      popperPlacement="bottom-start"
                      style={{ width: '100%' }}
                    />
                  </div>
                );
              })()}

              {/* Week Selector (only when week mode) */}
              {screenTimeTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select
                    value={screenTimeTrendsWeek}
                    onChange={(e) => setScreenTimeTrendsWeek(e.target.value)}
                    style={{
                      padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px',
                      backgroundColor: 'white', color: '#374151', cursor: 'pointer'
                    }}
                  >
                    <option value="1">Week 1</option>
                    <option value="2">Week 2</option>
                    <option value="3">Week 3</option>
                    <option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={screenTimeTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="stTrendColor0" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#94a3b8" />
                  <stop offset="100%" stopColor="#64748b" />
                </linearGradient>
                <linearGradient id="stTrendColor1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
                <linearGradient id="stTrendColor2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
                <linearGradient id="stTrendColor3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" stroke="#4a5568" interval={0} height={70}
                tick={({ x, y, payload }) => {
                  const label = payload.value || '';
                  const maxChars = 10;
                  const words = label.split(/[\s&]+/);
                  const lines = [];
                  let current = '';
                  words.forEach(w => {
                    if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; }
                    else { current = current ? current + ' ' + w : w; }
                  });
                  if (current) lines.push(current);
                  return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>);
                }}
              />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                itemSorter={(item) => screenTimeTrendsPeriods.indexOf(item.dataKey)}
                formatter={(value, name) => [formatScreenTime(value), formatTrendPeriodLabel(name)]}
              />
              <Legend wrapperStyle={{ paddingTop: '4px' }} formatter={(value) => formatTrendPeriodLabel(value)} />
              {screenTimeTrendsPeriods.map((period, idx) => (
                <Bar key={period} dataKey={period} fill={`url(#stTrendColor${idx})`} barSize={12} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey={period} position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} formatter={(value) => value > 0 ? Math.round(value) : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadScreenTimeTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

          </>
        )}
      </div>
    </div>
  );
};

export default UserAnalyticsTest;

